// src/pages/customer/CustomerDashboard.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Radar, AlertTriangle } from "lucide-react";
import apiService from "../../services/api";
import NotificationBell from "../../components/NotificationBell";
import OpportunityScore from "../../components/OpportunityScore";
import ProductTour from "../../components/ProductTour";
import SalesWorkspaceTour from "../../components/SalesWorkspaceTour";
import CompanySearchTypeahead, {
  SEARCH_TYPEAHEAD_STYLES,
  statusTone,
} from "../../components/CompanySearchTypeahead";
import CompaniesHouseDrawer, {
  CH_DRAWER_STYLES,
} from "../../components/CompaniesHouseDrawer";
import UpgradeLock, {
  UpgradePanel,
  UPGRADE_LOCK_STYLES,
} from "../../components/UpgradeLock";
import usePlanAccess from "../../hooks/usePlanAccess";
import CreditsUsage, {
  CreditBalancePill,
  CREDITS_CHANGED_EVENT,
} from "../../components/CreditsUsage";
import { CAP, PLAN_LABELS, normalisePlanId } from "../../config/planAccess";
import { CAPABILITIES, hasCapability } from "../../config/roles";
import FeedbackModal from "../../components/FeedbackModal";
import { Select } from "../../arc/components/select/select";
import { Dialog, DialogContent } from "../../arc/components/dialog/dialog";
import { Drawer, DrawerContent } from "../../arc/components/drawer/drawer";
import { useToastStack } from "../../arc/components/toast-stack/toast-stack";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../arc/components/tabs/tabs";
import { Skeleton } from "../../arc/components/skeleton/skeleton";
import { ThemeSwitch } from "../../arc/components/theme-switch/theme-switch";
import { applyThemeWithTransition } from "../../utils/themeTransition";
import { Input } from "../../arc/components/input/input";
import { Textarea } from "../../arc/components/textarea/textarea";
import { SortableDataTable } from "../../arc/components/sortable-data-table/sortable-data-table";
import { DateRangePicker } from "../../arc/components/date-range-picker/date-range-picker";
import SalesWorkspace from "./Revenue/SalesWorkspace";
import PipelineBoard from "./Revenue/PipelineBoard";
import AiPerformanceDashboard from "./Revenue/AiPerformanceDashboard";
import RevenueForecastDashboard from "./Revenue/RevenueForecastDashboard";
import InfoTooltip from "../../components/InfoTooltip";

// Creditsafe/enrichment websites are often stored without a protocol
// (e.g. "www.acme.co.uk"). A bare value in href is treated as a relative
// path and redirects back to AXO, so always make it absolute.
const toExternalUrl = (raw) => {
  if (!raw || typeof raw !== "string") return raw;
  const v = raw.trim();
  if (!v) return v;
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith("//")) return `https:${v}`;
  return `https://${v.replace(/^\/+/, "")}`;
};

// Same precedence as the company drawer, so the list "Contact" column also
// shows Creditsafe phone/website when enrichment has none.
const getRowPhone = (company, isSoleTrader) =>
  isSoleTrader
    ? company.phone || company.credit?.telephone || company.enrichment?.phone
    : company.credit?.telephone || company.enrichment?.phone;
const getRowWebsite = (company, isSoleTrader) =>
  isSoleTrader
    ? company.website || company.credit?.website || company.enrichment?.website
    : company.credit?.website || company.enrichment?.website;

const COUNTRY_SWITCHER_CUSTOMER_ID = "00000000-0000-0000-0000-000000000001";

// AiGovernanceSettings intentionally NOT imported here, it's AXO's
// internal AI provider/cost control panel, explicitly "never
// partner-facing" per its own header comment, and lives only at
// /super-admin/ai-governance. It must not ship in the customer bundle.

const REVENUE_INTEL_TABS = [
  { id: "agent", label: "Sales Agent" },
  { id: "pipeline", label: "Pipeline" },
  { id: "performance", label: "AI Performance" },
  { id: "forecast", label: "Forecast" },
  { id: "credits", label: "Credits & usage" },
];

// The date-range filter stores plain "YYYY-MM-DD" strings in the URL; the
// Arc DateRangePicker works with local-midnight Date objects. These bridge
// the two without pulling in a date library.
const parseDateKey = (key) => {
  if (!key) return null;
  const [year, month, day] = key.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};
const toDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

// Money figures from the filed accounts (turnover, net assets) are stored in
// whole GBP. Compact them for badges and values (e.g. 1250000 -> "£1.3M",
// 480000 -> "£480k") so they stay readable, and return null when there's no
// reported figure so callers can omit the badge entirely.
const formatMoneyCompact = (value) => {
  const n = Number(value);
  if (!value || !Number.isFinite(n) || n <= 0) return null;
  if (n >= 1000000) {
    const millions = n / 1000000;
    return `£${(Number.isInteger(millions) ? millions : millions.toFixed(1)).toString()}M`;
  }
  if (n >= 1000) return `£${Math.round(n / 1000)}k`;
  return `£${Math.round(n)}`;
};

// "Good morning / afternoon / evening, {name}", recomputed on every
// render, so it's always current for whenever the user opens the page,
// not just a one-time first-visit banner.
const getGreeting = (fullName) => {
  const hour = new Date().getHours();
  const timeOfDay =
    hour < 5
      ? "evening"
      : hour < 12
        ? "morning"
        : hour < 18
          ? "afternoon"
          : "evening";
  const firstName = fullName?.trim()?.split(/\s+/)[0] || "there";
  return `Good ${timeOfDay}, ${firstName}`;
};

// Account-settings modal styles, exported so TeamManagement.jsx can render
// the same AccountSettingsModal without duplicating the CSS.
export const ACCOUNT_SETTINGS_STYLES = `
  .acct-backdrop { position: fixed; inset: 0; background: rgba(16,16,16,0.45); z-index: 390; display: flex; align-items: center; justify-content: center; padding: 16px; font-family: var(--sans); }
  .acct-modal { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; width: 100%; max-width: 680px; max-height: 88vh; display: flex; flex-direction: column; box-shadow: 0 4px 12px rgba(0,0,0,0.08), 0 12px 36px rgba(0,0,0,0.10); color: var(--text); }
  .acct-head { display: flex; align-items: center; justify-content: space-between; padding: 24px 32px 0; flex-shrink: 0; }
  .acct-title { font-size: 24px; font-weight: 600; line-height: 1.2; letter-spacing: -0.04em; color: var(--text); }
  .acct-close { width: 36px; height: 36px; border-radius: 9999px; background: var(--surface); border: 1px solid var(--border); color: var(--text); font-size: 18px; line-height: 1; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }

  /* nav-pill-group */
  .acct-tabs-wrap { padding: 16px 24px 0; overflow-x: auto; flex-shrink: 0; }

  .acct-body { padding: 20px 24px; overflow-y: auto; flex: 1 1 auto; min-height: 0; }
  .acct-foot { display: flex; align-items: center; justify-content: flex-end; gap: 12px; padding: 14px 24px; border-top: 1px solid var(--border); flex-shrink: 0; }
  .acct-status { margin-right: auto; font-size: 13px; font-weight: 500; color: var(--text-muted); }
  .acct-status.err { color: var(--red); }
  .acct-status.ok { color: var(--green); }

  /* buttons, 40px · 8px radius · 14px/600 */
  .acct-btn { height: 40px; padding: 0 20px; border-radius: 8px; font-family: var(--sans); font-size: 14px; font-weight: 600; line-height: 1; cursor: pointer; white-space: nowrap; display: inline-flex; align-items: center; justify-content: center; }
  .acct-btn-primary { background: var(--text); color: var(--bg); border: 1px solid var(--text); }
  .acct-btn-primary:active:not(:disabled) { opacity: 0.88; }
  .acct-btn-primary:disabled { background: var(--border); border-color: var(--border); color: var(--text-muted); cursor: not-allowed; }
  .acct-btn-secondary { background: var(--surface); color: var(--text); border: 1px solid var(--border); }

  /* sections */
  .acct-section + .acct-section { margin-top: 32px; }
  .acct-section-title { font-size: 16px; font-weight: 600; line-height: 1.4; color: var(--text); }
  .acct-section-sub { font-size: 14px; line-height: 1.5; color: var(--text-muted); margin: 4px 0 16px; }

  /* avatar-circle (36px, pastel fill + initials) */
  .acct-identity { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; }
  .acct-avatar { width: 36px; height: 36px; border-radius: 9999px; display: inline-flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 600; color: #111111; flex-shrink: 0; }
  .acct-identity-name { font-size: 16px; font-weight: 600; line-height: 1.3; color: var(--text); }
  .acct-identity-sub { font-size: 13px; line-height: 1.4; color: var(--text-muted); }

  /* text-input */
  .acct-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 16px; }
  .acct-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
  .acct-field.full { grid-column: 1 / -1; }
  .acct-field label { font-size: 13px; font-weight: 500; line-height: 1.4; color: var(--text); }
  .acct-field input, .acct-field textarea { width: 100%; background: var(--surface); color: var(--text); border: 1px solid var(--border); border-radius: 8px; padding: 10px 14px; font-family: var(--sans); font-size: 14px; line-height: 1.5; outline: none; }
  .acct-field input { height: 40px; }
  .acct-field textarea { resize: vertical; min-height: 88px; }
  .acct-field input::placeholder, .acct-field textarea::placeholder { color: var(--text-faint); }
  .acct-field input:focus, .acct-field textarea:focus { border-color: var(--text); }
  .acct-field input:disabled, .acct-field textarea:disabled { background: var(--surface-sunken); color: var(--text-muted); cursor: not-allowed; }
  .acct-field.invalid input { border-color: var(--red); }
  .acct-err { font-size: 13px; color: var(--red); }

  /* feature-card (surface-card) callout */
  .acct-note { background: var(--surface-sunken); border-radius: 12px; padding: 12px 16px; font-size: 14px; line-height: 1.5; color: var(--text-muted); margin-bottom: 24px; }

  /* preference rows + segmented pill group */
  .acct-pref { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 0; border-bottom: 1px solid var(--border); }
  .acct-pref:last-child { border-bottom: none; }
  .acct-pref-label { font-size: 14px; font-weight: 500; color: var(--text); }
  .acct-pref-sub { font-size: 13px; color: var(--text-muted); margin-top: 2px; }
  .acct-pills { display: inline-flex; gap: 2px; padding: 4px; background: var(--surface-raised); border-radius: 9999px; }
  .acct-pill { background: transparent; border: none; padding: 6px 14px; border-radius: 9999px; font-family: var(--sans); font-size: 13px; font-weight: 500; color: var(--text-muted); cursor: pointer; white-space: nowrap; }
  .acct-pill.active { background: var(--surface); color: var(--text); box-shadow: 0 1px 2px rgba(0,0,0,0.05); }

  /* billing: feature-card + badge-pill + quiet text-link */
  .acct-plan-card { background: var(--surface-sunken); border-radius: 12px; padding: 24px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .acct-plan-name { font-size: 18px; font-weight: 600; line-height: 1.4; color: var(--text); }
  .acct-plan-sub { font-size: 14px; color: var(--text-muted); }
  .acct-badge { display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 9999px; background: var(--surface); border: 1px solid var(--border); font-size: 13px; font-weight: 500; line-height: 1.4; color: var(--text); }
  .acct-quiet-link { background: none; border: none; padding: 0; font-family: var(--sans); font-size: 13px; font-weight: 500; color: var(--text-faint); text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
  .acct-billing-foot { margin-top: 48px; padding-top: 16px; border-top: 1px solid var(--border); }
  .acct-billing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 16px; }
  .acct-billing-item { background: var(--surface-sunken); border-radius: 12px; padding: 14px 16px; }
  .acct-billing-label { font-size: 12px; font-weight: 500; color: var(--text-muted); }
  .acct-billing-value { font-size: 16px; font-weight: 600; color: var(--text); margin-top: 4px; }
  .acct-billing-note { font-size: 12px; color: var(--text-faint); margin-top: 2px; }

  /* connections: one hairline row per service, badge-pill status */
  .acct-conn-item { border-bottom: 1px solid var(--border); }
  .acct-conn-item:last-child { border-bottom: none; }
  .acct-conn { display: flex; align-items: center; gap: 16px; padding: 16px 0; }
  .acct-conn-panel { margin: 0 0 16px 56px; padding: 16px; background: var(--surface-sunken); border-radius: 12px; display: flex; flex-direction: column; gap: 12px; }
  .acct-conn-inline { display: flex; gap: 8px; align-items: center; }
  .acct-conn-inline input { flex: 1 1 auto; min-width: 0; }
  .acct-conn-keyrow { display: flex; align-items: center; justify-content: space-between; gap: 12px; font-size: 13px; color: var(--text-muted); }
  .acct-conn-keyrow b { font-weight: 500; color: var(--text); }
  .acct-keybox { background: var(--surface); border: 1px solid var(--border); border-radius: 8px; padding: 10px 12px; font-family: var(--sans); font-size: 12px; word-break: break-all; color: var(--text); }
  .acct-conn-msg { font-size: 13px; color: var(--text-muted); margin-bottom: 8px; }
  .acct-conn-msg.err { color: var(--red); }
  .acct-conn-confirm { font-size: 13px; color: var(--text-muted); white-space: nowrap; }
  .acct-conn-icon { flex: 0 0 auto; width: 40px; height: 40px; border-radius: 10px; background: var(--surface-raised); display: inline-flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 600; color: var(--text-muted); }
  .acct-conn-main { flex: 1 1 auto; min-width: 0; }
  .acct-conn-name { font-size: 14px; font-weight: 500; color: var(--text); }
  .acct-conn-desc { font-size: 13px; color: var(--text-muted); margin-top: 2px; }
  .acct-conn-detail { font-size: 13px; color: var(--text-faint); margin-top: 2px; }
  .acct-conn-side { flex: 0 0 auto; display: flex; align-items: center; gap: 12px; }
  .acct-conn-status { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 9999px; background: var(--surface); border: 1px solid var(--border); font-size: 13px; font-weight: 500; color: var(--text-muted); white-space: nowrap; }
  .acct-conn-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--text-faint); }
  .acct-conn-status.connected { color: var(--text); }
  .acct-conn-status.connected .acct-conn-dot { background: var(--green); }
  .acct-conn-group { font-size: 12px; font-weight: 600; letter-spacing: 0; color: var(--text-muted); padding: 24px 0 4px; }

  /* Demo-parity card layout */
  .acct-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 16px 18px; margin-bottom: 12px; }
  .acct-card:last-child { margin-bottom: 0; }
  .acct-card-title { font-size: 13px; font-weight: 600; line-height: 1.4; color: var(--text); }
  .acct-card-sub { font-size: 11.5px; line-height: 1.55; color: var(--text-muted); margin: 4px 0 14px; }
  .acct-avatar-lg { width: 54px; height: 54px; font-size: 17px; font-weight: 700; }
  .acct-usage-row { margin-bottom: 12px; }
  .acct-usage-row:last-child { margin-bottom: 0; }
  .acct-usage-top { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; font-size: 11.5px; color: var(--text-muted); margin-bottom: 5px; }
  .acct-usage-top b { font-size: 12px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; }
  .acct-usage-track { height: 6px; border-radius: 100px; background: var(--surface-sunken); overflow: hidden; }
  .acct-usage-fill { height: 100%; border-radius: 100px; background: var(--accent-mid); }
  .acct-conn-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .acct-conn-grid .acct-conn-item { margin-bottom: 0; height: 100%; }
  .acct-conn-grid .acct-conn-item:has(.acct-conn-panel) { grid-column: 1 / -1; }
  .acct-card .acct-conn-item { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 12px 14px; margin-bottom: 10px; }
  .acct-card .acct-conn-item:last-child { margin-bottom: 0; border-bottom: 1px solid var(--border); }
  .acct-card .acct-conn { padding: 0; }
  .acct-conn-grid .acct-conn { display: grid; grid-template-columns: 40px minmax(0, 1fr); gap: 10px 12px; align-items: start; }
  .acct-conn-grid .acct-conn-main { min-width: 0; }
  .acct-conn-grid .acct-conn-side { grid-column: 1 / -1; width: 100%; flex-wrap: wrap; justify-content: flex-start; gap: 8px; padding-top: 10px; margin-top: 2px; border-top: 1px solid var(--border); }
  .acct-conn-grid .acct-conn-desc,
  .acct-conn-grid .acct-conn-detail { overflow-wrap: anywhere; }
  .acct-card .acct-conn-panel { margin-left: 0; margin-top: 12px; }
  .acct-card .acct-conn-group { padding: 8px 0 6px; }
  .acct-card .acct-conn-group:first-child { padding-top: 0; }
  .acct-link-btn { background: none; border: none; padding: 0; font-family: var(--sans); font-size: 12.5px; font-weight: 600; color: var(--accent-mid); cursor: pointer; text-decoration: none; }
  .acct-link-btn:hover { text-decoration: underline; }

  @media (max-width: 640px) {
    .acct-grid { grid-template-columns: 1fr; }
    .acct-head, .acct-body, .acct-foot { padding-left: 20px; padding-right: 20px; }
    .acct-tabs-wrap { padding-left: 20px; padding-right: 20px; }
    .acct-body { padding-top: 24px; padding-bottom: 24px; }
    .acct-pref { flex-wrap: wrap; }
    .acct-conn-grid { grid-template-columns: 1fr; }
    .acct-conn { flex-wrap: wrap; }
    .acct-conn-panel { margin-left: 0; }
    .acct-conn-inline { flex-wrap: wrap; }
    .acct-conn-side { width: 100%; justify-content: space-between; padding-left: 56px; }
  }
`;

const styles = `
  /* Cal.com-style design system: white canvas, monochrome-flat cards,
   * hairline borders, no gradients, accent locked to AXO navy.
   */

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }



  /* Dark-mode component overrides, token-only */
  .dash-root.dark .badge-new { background: rgba(52,211,153,0.1); border-color: rgba(52,211,153,0.25); }
  .dash-root.dark .director-tag { background: var(--surface-raised); border-color: var(--border); }
  .dash-root.dark .field-input, .dash-root.dark .row-select, .dash-root.dark .row-textarea { background: var(--bg); }
  .dash-root.dark .toggle-track { background: var(--border-strong); }
  .dash-root.dark .user-pill { background: var(--header-pill-bg); border-color: var(--header-btn-border); }
  .dash-root.dark .state-missing { background: var(--surface-raised); border-color: var(--border); }
  .dash-root.dark .pagination { background: var(--surface-sunken); }
  .dash-root.dark tbody tr:hover { background: var(--row-hover); }
  .dash-root.dark tbody tr:hover td { background: var(--row-hover) !important; }
  .dash-root.dark thead tr { background: var(--surface-sunken); }
  .dash-root.dark .skeleton { background: linear-gradient(90deg, var(--surface-raised) 25%, var(--border) 50%, var(--surface-raised) 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; }
  .dash-root.dark .table-toolbar { background: var(--surface-raised); }
  .dash-root.dark .row-action-btn { background: var(--surface-raised); }
  .dash-root.dark .quota-bar { background: var(--surface); border-color: var(--border); }
  .dash-root.dark .quota-bar-track { background: var(--border); }
  .dash-root.dark .stat-card { background: var(--surface-raised); border-color: var(--border); }
  .dash-root.dark .stat-card:hover { box-shadow: var(--shadow-md); }
  .dash-root.dark .drawer { background: var(--bg); }
  .dash-root.dark .drawer-card { background: var(--surface); }
  .dash-root.dark .stat-trend-badge.up { border-color: rgba(52,211,153,0.22); }
  .dash-root.dark .stat-trend-badge.down { border-color: rgba(248,113,113,0.22); }

  html, body { overflow-x: hidden; }
  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: var(--text-base); line-height: 1.5; -webkit-font-smoothing: antialiased; overflow-x: clip; overflow-wrap: anywhere; }

  /* ── HEADER, AXO navy brand bar, flat surface, no gradient ──────────────── */
  .dash-header { background: var(--header-bg); border-bottom: 1px solid var(--header-border); position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1580px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
  .dash-brand { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  .dash-brand-logo { width: 54px; height: 54px; border-radius: var(--radius); overflow: hidden; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.13); padding: 4px; }
  .dash-brand-logo img { width: 100%; height: 100%; object-fit: contain; }
  .dash-brand-text { display: flex; flex-direction: column; gap: 2px; }
  .dash-brand-name { font-size: 14px; font-weight: 600; letter-spacing: -0.01em; color: #ffffff; }
  .dash-brand-sub { font-family: var(--sans); font-size: 13px; font-weight: 500; letter-spacing: 0; color: var(--header-text-muted); }
  .dash-nav { display: flex; align-items: center; gap: 6px; margin-left: auto; }
  .dash-nav .btn.active { background: rgba(255,255,255,0.16); border-color: rgba(255,255,255,0.3); color: #fff; }

  .dashboard-greeting { padding: 2px 2px 4px; }
  .dashboard-greeting-text { font-size: var(--text-lg); font-weight: 600; color: var(--text); letter-spacing: -0.01em; }

  .ri-panel { display: flex; flex-direction: column; gap: 0; background: var(--bg); color: var(--text); min-height: 100vh; }
  .ri-subnav { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; border-bottom: 1px solid var(--border); padding-bottom: 12px; }
  .ri-subnav-actions { display: flex; align-items: center; gap: 8px; margin-left: auto; }
  .ri-panel-content { min-height: 200px; }
  .dash-header .btn-ghost { background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); color: var(--header-text); }
  .dash-header .btn-ghost:hover { background: var(--header-btn-hover-bg); border-color: rgba(255,255,255,0.22); color: #ffffff; }
  .dash-header .user-pill { background: var(--header-pill-bg); border: 1px solid var(--header-btn-border); color: var(--header-text); }
  .dash-header .online-dot { background: var(--green); box-shadow: 0 0 5px rgba(16,185,129,0.5); }
  .dash-root.dark .dash-header .online-dot { background: var(--green); box-shadow: 0 0 5px rgba(52,211,153,0.5); }
  /* Settings dropdown is a Cal.com surface card (white canvas + hairline
     border), even though it opens from the navy header. */


  /* BUTTONS */
  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 36px; padding: 0 16px; border: 1px solid transparent; border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; line-height: 1.45; cursor: pointer; white-space: nowrap; text-decoration: none; outline: none; -webkit-tap-highlight-color: transparent; transition: background var(--transition), color var(--transition), border-color var(--transition), box-shadow var(--transition), opacity var(--transition); }
  .btn:focus-visible { box-shadow: 0 0 0 3px var(--accent-dim); }
  .btn:disabled { opacity: 0.52; cursor: not-allowed; }
  .btn-sm { min-height: 32px; padding: 0 12px; }
  .btn-md { min-height: 36px; padding: 0 16px; }
  .btn-lg { min-height: 40px; padding: 0 20px; }
  .btn-ghost { background: transparent; border: 1px solid var(--border); color: var(--text-muted); }
  .btn-ghost:hover { background: var(--surface-raised); border-color: var(--border-strong); color: var(--text); }
  .btn-primary { background: var(--accent); color: #fff; border: 1px solid transparent; letter-spacing: 0.01em; }
  .btn-primary:hover { background: var(--accent-hover); }
  .btn-primary:disabled { background: var(--text-faint); color: var(--surface); border-color: transparent; cursor: not-allowed; opacity: 0.55; }
  .btn-primary:disabled:hover { background: var(--text-faint); }
  .btn-secondary { background: transparent; border: 1px solid var(--border); color: var(--text-muted); }
  .btn-secondary:hover { background: var(--surface-raised); color: var(--text); }
  .btn-accent-ghost { background: var(--accent-dim); border: 1px solid rgba(26,79,168,0.22); color: var(--accent-mid); }
  .btn-accent-ghost:hover { background: var(--accent); color: #fff; border-color: var(--accent); }
  .btn-success-ghost { background: var(--green-dim); border: 1px solid rgba(10,124,92,0.22); color: var(--green); }
  .btn-success-ghost:hover { background: var(--green); color: #fff; }
  .btn-admin { border-color: var(--accent-mid) !important; color: var(--accent-mid) !important; }
  .btn-cancel-sub { width: 100%; padding: 9px 12px; background: var(--red-dim); border: 1px solid rgba(191,41,41,0.22); border-radius: var(--radius-sm); color: var(--red); font-size: var(--text-sm); font-weight: 500; font-family: var(--sans); cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px; transition: background var(--transition), color var(--transition); }
  .btn-cancel-sub:hover { background: var(--red); color: #fff; }

  /* USER PILL */
  .user-pill { display: flex; align-items: center; gap: 7px; padding: 5px 11px; background: var(--surface-raised); border: 1px solid var(--border); border-radius: 100px; font-size: var(--text-xs); color: var(--text-muted); font-weight: 500; }
  .online-dot { width: 6px; height: 6px; background: var(--green); border-radius: 50%; flex-shrink: 0; }

  /* SETTINGS DROPDOWN */
  .settings-wrap { position: relative; }
  .settings-menu { display: none; position: absolute; right: 0; top: calc(100% + 8px); min-width: 320px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: 0 4px 12px rgba(0,0,0,0.08), 0 12px 36px rgba(0,0,0,0.10); z-index: 200; padding: 8px; overflow: hidden; }
  .settings-menu.open { display: block; }
  .settings-row { white-space: nowrap; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; font-size: 14px; font-weight: 500; color: var(--text); border-radius: var(--radius-sm); cursor: pointer; transition: background var(--transition); }
  .settings-row:hover { background: var(--surface-raised); }
  .settings-divider { height: 1px; background: var(--border); margin: 6px 4px; }
  .settings-section-label { font-family: var(--sans); font-size: 12px; font-weight: 600; letter-spacing: 0; color: var(--text-faint); padding: 8px 12px 6px; }
  /* Table density, Cal.com nav-pill-group: soft pill wrapper with the active
     segment as a canvas pill + soft shadow (readable on light and dark). */
  .density-btns { display: flex; gap: 4px; padding: 5px; margin: 0 8px 8px; background: var(--surface-sunken); border-radius: 9999px; }
  .density-btn { flex: 1; white-space: nowrap; padding: 7px 6px; font-size: 12px; font-weight: 500; background: transparent; border: none; border-radius: 9999px; color: var(--text-muted); cursor: pointer; font-family: var(--sans); text-align: center; transition: background var(--transition), color var(--transition), box-shadow var(--transition); }
  .density-btn.active { background: var(--surface); color: var(--text); box-shadow: 0 1px 2px rgba(0,0,0,0.08); }
  .density-btn:hover:not(.active) { color: var(--text); }

  /* TOGGLE */
  .toggle { position: relative; width: 30px; height: 16px; flex-shrink: 0; }
  .toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
  .toggle-track { position: absolute; inset: 0; background: var(--border-strong); border-radius: 9999px; cursor: pointer; transition: background var(--transition); }
  .toggle-track::after { content: ''; position: absolute; width: 12px; height: 12px; left: 2px; top: 2px; background: white; border-radius: 50%; transition: transform var(--transition); box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
  .toggle input:checked ~ .toggle-track { background: var(--accent-mid); }
  .toggle input:checked ~ .toggle-track::after { transform: translateX(14px); }

  /* APP COMING SOON BANNER, flat surface, no gradient */
  .app-banner { background: var(--header-bg); border-bottom: none; padding: 7px 28px; display: flex; align-items: center; justify-content: center; gap: 9px; }
  .app-banner-icon { font-size: 13px; flex-shrink: 0; }
  .app-banner-text { font-size: var(--text-xs); font-weight: 400; color: var(--header-text-muted); letter-spacing: 0.01em; text-align: center; }
  .app-banner-text strong { color: rgba(255,255,255,0.75); font-weight: 500; }
  .app-banner-badge { display: inline-flex; align-items: center; padding: 1px 8px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14); border-radius: 100px; font-family: var(--sans); font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.55); letter-spacing: 0; flex-shrink: 0; }

  /* MAIN LAYOUT, rhythm varied instead of uniform 16px everywhere */
  .dash-main { max-width: 1580px; margin: 0 auto; padding: 24px 28px 40px; display: flex; flex-direction: column; gap: 20px; }
  .dash-body { display: flex; gap: 20px; align-items: flex-start; }
  .dash-sidebar { width: 224px; flex-shrink: 0; position: sticky; top: 100px; display: flex; flex-direction: column; gap: 0; }
  .dash-content { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 18px; }

  /* STATS GRID, asymmetric emphasis instead of four identical tiles */
  .stats-grid { display: grid; grid-template-columns: 1.3fr 1fr 1fr 1fr; gap: 12px; }
  .stat-card { background: var(--surface-sunken); border: 1px solid transparent; border-radius: var(--radius-lg); padding: 22px; box-shadow: none; transition: background var(--transition), border-color var(--transition); cursor: pointer; position: relative; overflow: hidden; display: flex; flex-direction: column; gap: 10px; }
  .stat-card:hover { background: var(--surface-raised); border-color: var(--border); }
    .stat-card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
  .stat-label { font-family: var(--sans); font-size: 13px; font-weight: 500; letter-spacing: 0; color: var(--text-muted); display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
  .stat-filter-hint { font-family: var(--sans); font-size: 9px; letter-spacing: 0.02em; color: var(--accent-mid); opacity: 0.45; transition: opacity var(--transition); white-space: nowrap; font-weight: 500; }
  .stat-card:hover .stat-filter-hint { opacity: 1; }
  .stat-card:focus-visible { outline: 2px solid var(--accent-mid); outline-offset: 2px; }
  .stat-trend-badge { display: inline-flex; align-items: center; gap: 2px; padding: 2px 7px; border-radius: 100px; font-family: var(--sans); font-size: 9px; font-weight: 600; letter-spacing: 0.02em; white-space: nowrap; flex-shrink: 0; }
  .stat-trend-badge.up { background: var(--green-dim); color: var(--green); border: 1px solid rgba(10,122,90,0.16); }
  .stat-trend-badge.down { background: var(--red-dim); color: var(--red); border: 1px solid rgba(191,41,41,0.16); }
  .stat-trend-badge.flat { background: var(--surface-raised); color: var(--text-faint); border: 1px solid var(--border); }
  .stat-value-row { display: flex; align-items: baseline; gap: 10px; }
  .stat-value { font-family: "Cal Sans", var(--sans); font-size: var(--text-xl); font-weight: 600; letter-spacing: -0.5px; line-height: 1.1; color: var(--text); flex-shrink: 0; }
  .stat-value.c-accent { color: var(--accent-mid); }
  .stat-value.c-green { color: var(--green); }
  .stat-value.c-amber { color: var(--amber); }
  .stat-comparison { font-family: var(--sans); font-size: var(--text-2xs); color: var(--text-faint); letter-spacing: 0.02em; }
  .stat-comparison b { color: var(--text-muted); font-weight: 500; }
  .stat-scope { font-family: var(--sans); font-size: 10px; font-weight: 600; letter-spacing: 0.02em; color: var(--text-faint); }
  .stat-value.is-loading { color: var(--text-faint); opacity: 0.45; }
  .stat-loading-bar { height: 3px; width: 64px; margin-top: 4px; border-radius: 99px; background: linear-gradient(90deg, var(--surface-raised) 25%, var(--border) 50%, var(--surface-raised) 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; }
  .scope-strip { display: flex; flex-wrap: wrap; gap: 8px; }
  .scope-chip { display: inline-flex; align-items: center; gap: 6px; padding: 7px 11px; background: var(--surface-raised); border: 1px solid var(--border); border-radius: 100px; font-size: 11.5px; color: var(--text-muted); }
  .scope-chip b { color: var(--text); font-weight: 600; }
  .scope-chip small { color: var(--text-faint); font-size: 10px; }
  .scope-chip-global { border-style: dashed; color: var(--text-faint); }
  .stat-sparkline { display: block; width: 100%; height: 36px; margin-top: auto; }
  .stat-sparkline-path { fill: none; stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; }
  .stat-sparkline-fill { stroke: none; }

  /* QUOTA BAR */
  .quota-bar { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 14px 20px; box-shadow: var(--shadow-xs); }
  .quota-bar-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 9px; gap: 12px; flex-wrap: wrap; }
  .quota-bar-left { display: flex; align-items: center; gap: 9px; }
  .quota-bar-label { display: inline-flex; align-items: center; gap: 4px; font-size: 13px; font-weight: 500; letter-spacing: 0; color: var(--text-muted); }
  .quota-plan-badge { display: inline-flex; align-items: center; padding: 1px 8px; background: var(--accent-dim); border: 1px solid rgba(26,79,168,0.2); border-radius: 100px; font-family: var(--sans); font-size: 11px; font-weight: 600; color: var(--accent-mid); letter-spacing: 0; }
  .quota-bar-right { font-family: var(--sans); font-size: var(--text-sm); font-weight: 500; color: var(--text-muted); white-space: nowrap; }
  .quota-bar-track { height: 6px; background: var(--border); border-radius: 99px; overflow: hidden; }
  .quota-bar-fill { height: 100%; border-radius: 99px; transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1); }
  .quota-bar-fill.safe { background: var(--green); }
  .quota-bar-footer { display: flex; align-items: center; justify-content: space-between; margin-top: 7px; flex-wrap: wrap; gap: 6px; }
  .quota-bar-sub { font-family: var(--sans); font-size: var(--text-2xs); color: var(--text-faint); letter-spacing: 0.02em; }
  .quota-bar-sub b { color: var(--text-muted); font-weight: 500; }
  .quota-unlimited { display: flex; align-items: center; justify-content: space-between; }
  .quota-unlimited-label { font-size: 13px; font-weight: 500; letter-spacing: 0; color: var(--text-muted); display: flex; align-items: center; gap: 4px; }
  .quota-unlimited-value { font-family: var(--sans); font-size: var(--text-sm); font-weight: 600; color: var(--green); letter-spacing: 0.02em; }

  /* REFRESH BUTTON */
  .btn-refresh { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; font-size: var(--text-2xs); font-family: var(--sans); font-weight: 600; letter-spacing: 0.04em; border-radius: var(--radius-sm); cursor: pointer; transition: background var(--transition), color var(--transition), border-color var(--transition); white-space: nowrap; }
  .btn-refresh.available { background: var(--accent-dim); border: 1px solid rgba(26,79,168,0.2); color: var(--accent-mid); }
  .btn-refresh.available:hover { background: var(--accent); color: #fff; border-color: var(--accent); }
  .btn-refresh.unavailable { background: var(--surface-raised); border: 1px solid var(--border); color: var(--text-faint); cursor: not-allowed; }
  .btn-refresh.loading { background: var(--accent-dim); border: 1px solid rgba(26,79,168,0.2); color: var(--accent-mid); cursor: not-allowed; opacity: 0.7; }

  /* FILTER PANEL / SIDEBAR */
  .filter-panel { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 18px; box-shadow: var(--shadow-xs); transition: border-color var(--transition), box-shadow var(--transition); }
  .filter-panel:has(.filter-count-badge) { border-color: var(--accent-mid); box-shadow: 0 0 0 1px var(--accent-dim), var(--shadow-xs); }
  .filter-panel-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
  .filter-panel-title { font-size: 13px; font-weight: 600; letter-spacing: 0; color: var(--text); display: flex; align-items: center; gap: 7px; }
  .filter-count-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 16px; height: 16px; padding: 0 4px; background: var(--accent); color: #fff; border-radius: 100px; font-size: 9.5px; font-weight: 600; letter-spacing: 0; font-family: var(--sans); }
  .filter-grid { display: flex; flex-direction: column; gap: 10px; margin-bottom: 12px; }
  input[type="date"].field-input { color-scheme: light; }
  .dash-root.dark input[type="date"].field-input { color-scheme: dark; }
  .field-group { display: flex; flex-direction: column; gap: 5px; }
  .field-label { font-family: var(--sans); font-size: 12px; font-weight: 500; letter-spacing: 0; color: var(--text-muted); }
  .field-input { width: 100%; padding: 8px 12px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--sans); font-size: var(--text-sm); outline: none; transition: border-color var(--transition), box-shadow var(--transition); -webkit-appearance: none; }
  .field-input::placeholder { color: var(--text-faint); }
  .field-input:focus { border-color: var(--accent-mid); box-shadow: 0 0 0 2px var(--accent-dim); }
  select.field-input { cursor: pointer; padding-right: 34px; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239da3b0' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; }
  /* Date-range picker trigger: mirror the .field-input selects/inputs beside it.
     Child rules use element selectors because the picker's own classes are CSS-module hashed. */
  .drp-field { display: block; width: 100% !important; }
  .drp-field > button { width: 100% !important; justify-content: flex-start; gap: 8px; padding: 0 11px; }
  .drp-field > button > span:nth-child(2) { flex: 1 1 auto; min-width: 0; justify-content: flex-start; }
  .drp-field > button > span:nth-child(2)[data-empty] { color: var(--text-faint); }
  .drp-field > div { background: var(--bg); }
  .drp-field[data-open] > div { background: var(--surface-raised); }
  .drp-field:has(> button:focus-visible) > div { box-shadow: 0 0 0 2px var(--accent-dim); }
  .drp-field:has(> button:focus-visible) > div::after { border-color: var(--accent-mid); }
  .search-wrap { position: relative; }
  .search-clear { position: absolute; right: 7px; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--text-faint); cursor: pointer; padding: 2px; display: flex; align-items: center; justify-content: center; border-radius: 4px; transition: color var(--transition); }
  .search-clear:hover { color: var(--text-muted); }
  .sic-hint { font-family: var(--sans); font-size: 9.5px; color: var(--accent-mid); letter-spacing: 0.02em; min-height: 13px; }
  .filter-actions { display: flex; align-items: center; gap: 7px; }

  /* TABLE TOOLBAR */
  .table-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 9px 16px; border-bottom: 1px solid var(--border); background: var(--surface-raised); gap: 10px; flex-wrap: wrap; }
  .toolbar-label { font-family: var(--sans); font-size: var(--text-xs); color: var(--text-faint); letter-spacing: 0.04em; }
  .per-page-select { padding: 3px 8px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text-muted); font-family: var(--sans); font-size: var(--text-xs); outline: none; cursor: pointer; -webkit-appearance: none; }
  .per-page-select:focus { border-color: var(--accent-mid); }
  .toolbar-select { width: auto; min-height: 28px; padding: 4px 10px; }
  .bulk-results-dialog { width: min(calc(100vw - var(--space-8)), 520px); }
  .bulk-send-dialog { width: min(calc(100vw - var(--space-8)), 560px); }
  .lead-drawer { --drawer-size: min(28rem, calc(100vw - var(--space-4))); }
  .lead-drawer .drawer-body { padding: 0; flex: none; overflow: visible; }
  .lead-drawer .drawer-footer { padding: 18px 0 0; background: transparent; }
  .acct-dialog { width: min(calc(100vw - var(--space-8)), 680px); max-height: 88vh; }
  .acct-dialog .acct-tabs-wrap { padding-left: 0; padding-right: 0; }
  .acct-dialog .acct-body { padding-left: 0; padding-right: 0; padding-top: 16px; }

  /* TABLE */
  .table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow-sm); overflow: hidden; }
  .table-scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  thead tr { border-bottom: 1px solid var(--border); background: var(--surface-sunken); }
  th { padding: 9px 16px; text-align: left; font-family: var(--sans); font-size: var(--text-sm); letter-spacing: 0; text-transform: none; color: var(--text-muted); font-weight: 500; white-space: nowrap; }
  th.sortable { padding: 0; }
  .th-btn { display: flex; align-items: center; gap: 4px; width: 100%; padding: 9px 16px; background: none; border: none; font-family: var(--sans); font-size: var(--text-sm); letter-spacing: 0; text-transform: none; color: var(--text-muted); font-weight: 500; white-space: nowrap; cursor: pointer; transition: color var(--transition); text-align: left; }
  .th-btn:hover { color: var(--text-muted); }
  .th-btn.active { color: var(--accent-mid); }
  .sort-icon { display: inline-flex; flex-direction: column; gap: 1px; opacity: 0.35; flex-shrink: 0; }
  .th-btn.active .sort-icon { opacity: 1; }
  .sort-icon svg { display: block; }

  /* ROW DENSITY */
  .density-compact td { padding: 8px 16px; }
  .density-comfortable td { padding: 13px 16px; }
  .density-spacious td { padding: 19px 16px; }

  tbody tr { border-bottom: 1px solid var(--border); cursor: pointer; background-color: var(--surface); transition: background-color 0.12s ease; }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover { background-color: var(--row-hover); }
  tbody tr:hover td { background-color: var(--row-hover) !important; }
  .dash-root.dark tbody tr:hover { background-color: rgba(58,122,244,0.06); }
  .dash-root.dark tbody tr:hover td { background-color: rgba(58,122,244,0.06) !important; }
  td { padding: 13px 16px; vertical-align: top; font-size: var(--text-sm); }

  /* ROW HOVER QUICK ACTIONS */
  .row-quick-actions { display: flex; align-items: center; gap: 4px; opacity: 0; transition: opacity 0.12s ease; flex-shrink: 0; }
  tbody tr:hover .row-quick-actions { opacity: 1; }
  .row-action-btn { display: inline-flex; align-items: center; justify-content: center; width: 27px; height: 27px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text-faint); cursor: pointer; transition: background var(--transition), border-color var(--transition), color var(--transition); flex-shrink: 0; position: relative; }
  .row-action-btn:hover { border-color: var(--accent-mid); color: var(--accent-mid); background: var(--accent-dim); }
  .row-action-btn .row-tooltip { position: absolute; bottom: calc(100% + 5px); left: 50%; transform: translateX(-50%) translateY(0); background: var(--text); color: var(--surface); font-size: 10px; font-family: var(--sans); white-space: nowrap; padding: 3px 6px; border-radius: 4px; opacity: 0; transition: opacity 0.12s ease, transform 0.12s ease; pointer-events: none; z-index: 10; }
  .row-action-btn:hover .row-tooltip { opacity: 1; transform: translateX(-50%) translateY(-3px); }

  /* TABLE CELL CONTENT */
  .cell-company-name { font-size: 13.5px; font-weight: 600; color: var(--text); letter-spacing: -0.02em; margin-bottom: 3px; line-height: 1.3; }
  .cell-company-num { display: inline-flex; align-items: center; gap: 3px; font-family: var(--sans); font-size: var(--text-xs); color: var(--accent-mid); text-decoration: none; opacity: 0.8; transition: opacity var(--transition); }
  .cell-company-num:hover { opacity: 1; }
  .cell-sic { font-family: var(--sans); font-size: 10px; color: var(--text-faint); margin-top: 2px; }
  .badge-new { display: inline-flex; align-items: center; gap: 3px; padding: 1px 7px; background: var(--green-dim); border: 1px solid rgba(10,124,92,0.2); border-radius: 100px; font-size: 11px; font-weight: 600; letter-spacing: 0; color: var(--green); margin-top: 4px; }
  .badge-new::before { content: ''; width: 4px; height: 4px; background: var(--green); border-radius: 50%; }
  .badge-emailed { display: inline-flex; align-items: center; gap: 3px; padding: 1px 6px; background: var(--accent-dim); border: 1px solid rgba(26,79,168,0.15); border-radius: 100px; font-size: 11px; font-weight: 600; letter-spacing: 0; color: var(--accent-mid); margin-top: 3px; margin-right: 3px; }
  .badge-emailed svg { width: 9px; height: 9px; flex-shrink: 0; }
  .dash-root.dark .badge-emailed { background: rgba(58,122,244,0.12); border-color: rgba(58,122,244,0.25); color: #7ab0ff; }
  .badge-turnover { display: inline-flex; align-items: center; gap: 3px; padding: 1px 7px; background: var(--amber-dim); border: 1px solid var(--amber-border); border-radius: 100px; font-size: 11px; font-weight: 600; letter-spacing: 0; color: var(--amber); margin-top: 3px; margin-right: 3px; }
  .badge-turnover::before { content: ''; width: 4px; height: 4px; background: var(--amber); border-radius: 50%; }
  .director-tag { display: inline-flex; align-items: center; padding: 1px 7px; background: var(--surface-raised); border: 1px solid var(--border); border-radius: 100px; font-size: var(--text-xs); color: var(--text-muted); margin: 2px 2px 2px 0; font-weight: 400; }
  .director-pending { font-family: var(--sans); font-size: 10px; color: var(--text-faint); font-style: italic; }
  .date-val { font-family: var(--sans); font-size: 12px; color: var(--text); }
  .contact-item { display: flex; align-items: center; gap: 5px; font-size: 11.5px; color: var(--text-muted); margin-bottom: 5px; }
  .contact-item svg { flex-shrink: 0; opacity: 0.45; }
  .contact-link { color: var(--accent-mid); text-decoration: none; font-size: 11.5px; transition: opacity var(--transition); }
  .contact-link:hover { opacity: 0.7; text-decoration: underline; }
  .maps-link { display: inline-flex; align-items: center; gap: 4px; font-family: var(--sans); font-size: var(--text-xs); color: #c42b2b; text-decoration: none; transition: opacity var(--transition); }
  .maps-link:hover { opacity: 0.7; }
  /* Contact data must never break mid-word: the root's overflow-wrap: anywhere would otherwise split phones, emails and domains. */
  .contact-item, .contact-link, .maps-link { white-space: nowrap; overflow-wrap: normal; word-break: normal; }
  .contact-item { min-width: max-content; }
  td:has(> .contact-item), td:has(> div > .contact-item) { width: 1%; white-space: nowrap; }

  /* ENRICHMENT STATES */
  .state-processing { font-family: var(--sans); font-size: var(--text-xs); color: var(--purple); animation: pulse 2s infinite; letter-spacing: 0.03em; display: inline-flex; align-items: center; gap: 4px; }
  .state-missing { font-family: var(--sans); font-size: 10px; color: var(--text-faint); padding: 3px 8px; background: var(--surface-raised); border: 1px dashed var(--border); border-radius: var(--radius-sm); display: inline-block; }
  .state-failed { font-family: var(--sans); font-size: 10px; color: var(--red); margin-bottom: 3px; }
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }

  .btn-resync { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; font-size: var(--text-xs); font-family: var(--sans); font-weight: 500; background: transparent; border: 1px solid var(--border-strong); border-radius: var(--radius-sm); color: var(--text-muted); cursor: pointer; transition: background var(--transition), border-color var(--transition), color var(--transition); }
  .btn-resync:hover:not(:disabled) { background: var(--surface-raised); border-color: var(--accent-mid); color: var(--accent-mid); }
  .btn-resync:disabled { opacity: 0.4; cursor: not-allowed; }

  /* ADD EMAIL */
  .add-email-toggle { background: none; border: none; padding: 0; font-size: var(--text-xs); font-family: var(--sans); color: var(--accent-mid); cursor: pointer; text-decoration: underline; text-underline-offset: 2px; opacity: 0.75; transition: opacity var(--transition); }
  .add-email-toggle:hover { opacity: 1; }
  .add-email-row { display: flex; align-items: center; gap: 5px; margin-top: 7px; }
  .add-email-input { flex: 1; padding: 4px 8px; font-size: 12px; font-family: var(--sans); border: 1px solid var(--border-strong); border-radius: var(--radius-sm); background: var(--surface); color: var(--text); outline: none; transition: border-color var(--transition); }
  .add-email-input:focus { border-color: var(--accent-mid); }
  .add-email-save { padding: 4px 10px; font-size: 11.5px; font-family: var(--sans); font-weight: 500; background: var(--accent); color: #fff; border: none; border-radius: var(--radius-sm); cursor: pointer; white-space: nowrap; transition: background var(--transition); }
  .add-email-save:hover:not(:disabled) { background: var(--accent-hover); }
  .add-email-save:disabled { opacity: 0.5; cursor: not-allowed; }
  .add-email-cancel { background: none; border: none; font-size: 17px; line-height: 1; color: var(--text-faint); cursor: pointer; padding: 2px 4px; transition: color var(--transition); }
  .add-email-cancel:hover { color: var(--text-muted); }
  .potential-emails-toggle { background: none; border: none; padding: 0; font-size: 10.5px; font-family: var(--sans); color: var(--text-faint); cursor: pointer; text-decoration: underline; text-underline-offset: 2px; opacity: 0.7; transition: opacity var(--transition); margin-left: 2px; }
  .potential-emails-toggle:hover { opacity: 1; color: var(--accent-mid); }
  .potential-emails-list { margin-top: 4px; display: flex; flex-direction: column; gap: 3px; }
  .potential-email-item { font-size: 11px; font-family: var(--sans); color: var(--text-muted); display: flex; align-items: center; gap: 5px; }
  .potential-email-item a { color: var(--accent-mid); text-decoration: none; opacity: 0.85; transition: opacity var(--transition); }
  .potential-email-item a:hover { opacity: 1; text-decoration: underline; text-underline-offset: 2px; }
  .potential-email-label { font-size: 10px; color: var(--text-faint); font-family: var(--sans); margin-top: 3px; margin-bottom: 1px; }

  /* DUDA BUTTONS */
  .site-actions { display: flex; flex-direction: column; gap: 7px; }
  .btn-send-preview { padding: 7px 12px; font-size: 12px; background: var(--accent); color: #fff; border: none; border-radius: var(--radius-sm); font-family: var(--sans); font-weight: 500; cursor: pointer; text-align: center; text-decoration: none; display: block; transition: background var(--transition); }
  .btn-send-preview:hover { background: var(--accent-hover); }
  .btn-followup { padding: 7px 12px; font-size: 12px; background: var(--amber-dim); color: var(--amber); border: 1px solid rgba(154,93,0,0.22); border-radius: var(--radius-sm); font-family: var(--sans); font-weight: 500; cursor: pointer; text-align: center; text-decoration: none; display: block; transition: background var(--transition), color var(--transition); }
  .btn-followup:hover { background: var(--amber); color: #fff; }
  .btn-send-preview.btn-sent, .btn-send-preview.btn-sent:hover { background: var(--green); cursor: default; }
  .btn-followup.btn-sent, .btn-followup.btn-sent:hover { background: var(--green); color: #fff; border-color: var(--green); cursor: default; }
  .followup-pending { font-family: var(--sans); font-size: 10px; color: var(--text-faint); text-align: center; padding: 4px 0 2px; letter-spacing: 0.02em; }
  .btn-preview-site { padding: 7px 12px; font-size: 12px; background: var(--accent-dim); color: var(--accent-mid); border: 1px solid rgba(26,79,168,0.18); border-radius: var(--radius-sm); font-family: var(--sans); font-weight: 500; cursor: pointer; text-align: center; text-decoration: none; display: block; transition: background var(--transition); }
  .btn-preview-site:hover { background: var(--accent); color: #fff; }


  /* ACCOUNT SETTINGS MODAL, Cal.com design spec (DESIGN-cal.md)
   * canvas card · hairline borders · near-black primary · nav-pill-group tabs
   * 40px inputs @ 8px radius · surface-card callouts · pill badges · 4px grid
   * Tokens map onto the dashboard ladder so dark mode inverts cleanly:
   *   canvas=--surface  surface-soft=--surface-raised  surface-card=--surface-sunken
   *   hairline=--border  ink/primary=--text  on-primary=--bg  muted=--text-muted
   */
${ACCOUNT_SETTINGS_STYLES}

   /* CANCEL SUBSCRIPTION MODAL */
  .cancel-modal-backdrop { position: fixed; inset: 0; background: rgba(9,12,20,0.45); z-index: 400; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .cancel-modal { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 30px; max-width: 400px; width: 100%; box-shadow: var(--shadow-lg); }
  .cancel-modal-icon { width: 46px; height: 46px; background: var(--red-dim); border: 1px solid rgba(196,43,43,0.18); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 18px; font-size: 18px; }
  .cancel-modal-title { font-size: var(--text-md); font-weight: 600; color: var(--text); margin-bottom: 7px; letter-spacing: -0.02em; }
  .cancel-modal-sub { font-size: var(--text-sm); color: var(--text-muted); line-height: 1.6; margin-bottom: 24px; }
  .cancel-modal-actions { display: flex; gap: 9px; justify-content: flex-end; }
  .cancel-success-icon { width: 54px; height: 54px; background: var(--green-dim); border: 1px solid rgba(10,124,92,0.18); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; font-size: 22px; }
  .cancel-success-title { font-size: var(--text-md); font-weight: 600; color: var(--text); margin-bottom: 7px; letter-spacing: -0.02em; text-align: center; }
  .cancel-success-sub { font-size: var(--text-sm); color: var(--text-muted); line-height: 1.6; text-align: center; }

  /* FORM ELEMENTS */
  .row-select { width: 100%; padding: 8px 12px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--sans); font-size: 12px; outline: none; cursor: pointer; transition: border-color var(--transition); -webkit-appearance: none; }
  .row-select:focus { border-color: var(--accent-mid); }
  .row-textarea { width: 100%; margin-top: 5px; padding: 7px 10px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--sans); font-size: 12px; outline: none; resize: vertical; min-width: 160px; min-height: 50px; transition: border-color var(--transition); }
  .row-textarea::placeholder { color: var(--text-faint); }
  .row-textarea:focus { border-color: var(--accent-mid); }
  .btn-save { padding: 6px 13px; font-size: 11.5px; background: transparent; border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text-muted); font-family: var(--sans); font-weight: 500; cursor: pointer; transition: background var(--transition), border-color var(--transition), color var(--transition); white-space: nowrap; }
  .btn-save:hover { border-color: var(--accent-mid); color: var(--accent-mid); background: var(--accent-dim); }
  .btn-save.saved { border-color: var(--green); color: var(--green); background: var(--green-dim); }

  /* SKELETON LOADERS */
  .skeleton { background: linear-gradient(90deg, var(--surface-raised) 25%, var(--border) 50%, var(--surface-raised) 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 4px; }
  @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
  .skeleton-row td { padding: 13px 16px; }
  .skeleton-text { height: 12px; border-radius: 4px; }
  .skeleton-text.w-80 { width: 80%; }
  .skeleton-text.w-60 { width: 60%; }
  .skeleton-text.w-40 { width: 40%; }
  .skeleton-text.w-30 { width: 30%; }

  /* EMPTY STATE */
  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 20px; gap: 13px; text-align: center; }
  .empty-icon { font-size: 32px; opacity: 0.3; }
  .empty-title { font-size: var(--text-base); font-weight: 600; color: var(--text-muted); }
  .empty-sub { font-size: var(--text-sm); color: var(--text-faint); max-width: 320px; line-height: 1.6; }

  /* PAGINATION */
  .pagination { display: flex; align-items: center; justify-content: space-between; padding: 11px 16px; border-top: 1px solid var(--border); background: var(--surface-raised); gap: 10px; flex-wrap: wrap; }
  .page-info { font-family: var(--sans); font-size: var(--text-xs); color: var(--text-faint); letter-spacing: 0.04em; }
  .page-info b { color: var(--text-muted); font-weight: 500; }
  .page-controls { display: flex; align-items: center; gap: 2px; flex-wrap: wrap; }
  .btn-page { display: flex; align-items: center; justify-content: center; min-width: 29px; height: 29px; padding: 0 8px; background: transparent; border: 1px solid var(--border); color: var(--text-muted); border-radius: var(--radius-sm); font-family: var(--sans); font-size: 11px; cursor: pointer; transition: background var(--transition), border-color var(--transition), color var(--transition); }
  .btn-page:hover:not(:disabled) { border-color: var(--accent-mid); color: var(--accent-mid); background: var(--accent-dim); }
  .btn-page:disabled { opacity: 0.25; cursor: not-allowed; }
  .btn-page.active { background: var(--accent); border-color: var(--accent); color: #fff; font-weight: 600; }
  .btn-page.active:hover { background: var(--accent-hover); }
  .page-ellipsis { display: inline-flex; align-items: center; justify-content: center; width: 22px; color: var(--text-faint); font-size: 11px; }
  .page-jump { display: flex; align-items: center; gap: 6px; font-family: var(--sans); font-size: var(--text-xs); color: var(--text-faint); }
  .page-jump-input { width: 46px; height: 29px; padding: 0 7px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--sans); font-size: var(--text-xs); text-align: center; outline: none; transition: border-color var(--transition), box-shadow var(--transition); }
  .page-jump-input:focus { border-color: var(--accent-mid); box-shadow: 0 0 0 2px var(--accent-dim); }

  /* EXPAND BUTTON */
  .btn-expand { display: inline-flex; align-items: center; justify-content: center; width: 27px; height: 27px; background: transparent; border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text-faint); cursor: pointer; transition: background var(--transition), border-color var(--transition), color var(--transition); flex-shrink: 0; }
  .btn-expand:hover { border-color: var(--accent-mid); color: var(--accent-mid); background: var(--accent-dim); }
  .btn-expand.open { border-color: var(--accent-mid); color: var(--accent-mid); background: var(--accent-dim); }
  .btn-expand svg { transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1); }
  .btn-expand.open svg { transform: rotate(180deg); }

  /* DRAWER */
  .drawer-overlay { position: fixed; inset: 0; background: rgba(9,12,20,0.3); z-index: 300; opacity: 0; pointer-events: none; transition: opacity 0.18s ease; }
  .drawer-overlay.open { opacity: 1; pointer-events: all; }
  .drawer { position: fixed; top: 0; right: 0; bottom: 0; width: 440px; max-width: 96vw; background: var(--bg); box-shadow: var(--shadow-md); z-index: 301; display: flex; flex-direction: column; transform: translateX(100%); transition: transform var(--transition-slow); overflow: hidden; }
  .drawer.open { transform: translateX(0); }
  .drawer-header { display: flex; align-items: center; justify-content: space-between; padding: 22px 20px; background: var(--surface); border-bottom: 1px solid var(--border); flex-shrink: 0; gap: 10px; }
  .drawer-title { font-size: 15px; font-weight: 600; color: var(--text); letter-spacing: -0.025em; line-height: 1.2; overflow-wrap: anywhere; min-width: 0; }
  .drawer-subtitle { font-family: var(--sans); font-size: 10px; color: var(--text-faint); margin-top: 3px; letter-spacing: 0.06em; font-weight: 500; }
  .drawer-close { display: flex; align-items: center; justify-content: center; width: 29px; height: 29px; flex-shrink: 0; background: var(--surface); border: 1px solid var(--border); border-radius: 6px; color: var(--text-faint); cursor: pointer; transition: background var(--transition), border-color var(--transition), color var(--transition); }
  .drawer-close:hover { border-color: var(--border-strong); color: var(--text); background: var(--surface-raised); }
  .drawer-body { flex: 1; overflow-y: auto; padding: 22px 20px; display: flex; flex-direction: column; gap: 16px; }
  .drawer-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 18px; box-shadow: var(--shadow-sm); }

  /* PROFILE COMPLETENESS RING */
  .completeness-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 16px 18px; box-shadow: var(--shadow-sm); display: flex; align-items: center; gap: 16px; }
  .completeness-ring-wrap { position: relative; flex-shrink: 0; width: 56px; height: 56px; }
  .completeness-ring-wrap svg { transform: rotate(-90deg); }
  .completeness-ring-track { fill: none; stroke: var(--border); stroke-width: 5; }
  .completeness-ring-fill { fill: none; stroke-width: 5; stroke-linecap: round; transition: stroke-dashoffset 0.6s cubic-bezier(0.22, 1, 0.36, 1), stroke 0.4s ease; }
  .completeness-ring-label { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-family: var(--sans); font-size: 12px; font-weight: 600; color: var(--text); letter-spacing: -0.02em; }
  .completeness-info { flex: 1; min-width: 0; }
  .completeness-title { display: flex; align-items: center; gap: 4px; font-size: 11.5px; font-weight: 600; color: var(--text); letter-spacing: -0.01em; margin-bottom: 6px; }
  .completeness-missing { display: flex; flex-wrap: wrap; gap: 5px; }
  .completeness-chip { display: inline-flex; align-items: center; gap: 3px; padding: 2px 8px; border-radius: 100px; font-family: var(--sans); font-size: 9.5px; font-weight: 500; letter-spacing: 0.04em; }
  .completeness-chip.missing { background: var(--amber-dim); border: 1px solid rgba(154,93,0,0.18); color: var(--amber); }
  .completeness-chip.present { background: var(--green-dim); border: 1px solid rgba(10,124,92,0.18); color: var(--green); }
  .completeness-chip.missing::before { content: '✕'; font-size: 7.5px; opacity: 0.7; }
  .completeness-chip.present::before { content: '✓'; font-size: 8.5px; }
  .drawer-section-title { display: flex; align-items: center; gap: 4px; font-size: 13px; font-weight: 600; letter-spacing: 0; color: var(--text); margin-bottom: 16px; }
  .drawer-contact-item { display: flex; align-items: center; gap: 10px; font-size: var(--text-sm); color: var(--text-muted); padding: 9px 0; border-bottom: 1px solid var(--border); }
  .drawer-contact-item:last-of-type { border-bottom: none; padding-bottom: 0; }
  .drawer-contact-item:first-of-type { padding-top: 0; }
  .drawer-contact-item svg { flex-shrink: 0; color: var(--text-faint); }
  .drawer-contact-item .social-icon-link svg { color: inherit; fill: none; }
  .drawer-contact-link { color: var(--accent-mid); text-decoration: none; font-weight: 500; }
  .drawer-contact-link:hover { text-decoration: underline; }
  .drawer-site-actions { display: flex; flex-direction: column; gap: 9px; }
  .drawer-footer { padding: 18px 20px; background: var(--surface); border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 10px; }

  /* TOAST NOTIFICATIONS */
  .toast-container { position: fixed; bottom: 24px; left: 24px !important; z-index: 9999 !important; display: flex; flex-direction: column; gap: 9px; pointer-events: none; }
  .toast { display: flex; align-items: flex-start; gap: 11px; padding: 13px 15px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow-md); font-size: 13px; color: var(--text); max-width: 360px; pointer-events: all; animation: toastIn 0.22s cubic-bezier(0.22, 1, 0.36, 1); }
  .toast-icon { width: 28px; height: 28px; border-radius: 6px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; margin-top: 1px; }
  .toast-icon svg { width: 15px; height: 15px; }
  .toast-success .toast-icon { background: var(--green-dim); color: var(--green); }
  .toast-error .toast-icon { background: var(--red-dim); color: var(--red); }
  .toast-info .toast-icon { background: var(--accent-dim); color: var(--accent); }
  .toast-content { flex: 1; min-width: 0; }
  .toast-headline { font-weight: 500; font-size: 13px; }
  .toast-sub { font-size: 12px; color: var(--text-muted); margin-top: 2px; line-height: 1.45; }
  .toast-dismiss { width: 22px; height: 22px; border-radius: 6px; border: none; background: transparent; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--text-faint); padding: 0; flex-shrink: 0; }
  .toast-dismiss:hover { background: var(--surface-raised); color: var(--text-muted); }
  .toast-dismiss svg { width: 12px; height: 12px; }
  @keyframes toastIn { from { transform: translateX(16px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  .contact-source { font-family: var(--mono); font-size: 10px; letter-spacing: 0.04em; color: var(--text-faint); border: 1px solid var(--border); padding: 1px 7px; border-radius: 100px; white-space: nowrap; cursor: default; }
  .tag-limited { background: var(--accent-dim); color: var(--accent); padding: 2px 9px; border-radius: 100px; font-size: 11px; font-weight: 600; }
  .tag-sole-trader { background: var(--amber-dim); color: var(--amber); padding: 2px 9px; border-radius: 100px; font-size: 11px; font-weight: 600; }

  /* CREDIT PANEL */
  .credit-panel { display: flex; flex-direction: column; gap: 22px; }
  .credit-score-row { display: flex; align-items: center; gap: 18px; padding: 4px 0; }
  .credit-score-dial { position: relative; flex-shrink: 0; }
  .credit-score-label { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
  .credit-score-num { font-family: var(--sans); font-size: var(--text-lg); font-weight: 600; line-height: 1; letter-spacing: -0.02em; }
  .credit-score-max { font-family: var(--sans); font-size: 9px; color: var(--text-faint); letter-spacing: 0.03em; margin-top: 2px; }
  .credit-score-meta { flex: 1; }
  .credit-category-badge { display: inline-flex; align-items: center; padding: 4px 13px; border-radius: 100px; font-size: 11px; font-weight: 600; letter-spacing: 0; margin-bottom: 9px; }
  .credit-category-badge.excellent { background: var(--green-dim); color: var(--green); border: 1px solid rgba(5,150,105,0.2); }
  .credit-category-badge.good { background: var(--accent-dim); color: var(--accent); border: 1px solid rgba(37,99,235,0.2); }
  .credit-category-badge.fair { background: var(--amber-dim); color: var(--amber); border: 1px solid rgba(180,83,9,0.2); }
  .credit-category-badge.poor { background: var(--red-dim); color: var(--red); border: 1px solid rgba(220,38,38,0.2); }
  .credit-risk-row { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--text-muted); margin-bottom: 4px; }
  .credit-risk-dot { width: 8px; height: 8px; border-radius: 50%; }
  .credit-risk-dot.low { background: var(--green); }
  .credit-risk-dot.medium { background: var(--amber); }
  .credit-risk-dot.high { background: var(--red); }
  .credit-date { font-family: var(--sans); font-size: 10px; color: var(--text-faint); letter-spacing: 0.02em; }

  .credit-kpi-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 13px; }
  .credit-kpi { background: var(--surface-raised); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 13px 15px; transition: border-color var(--transition), transform var(--transition); }
  .credit-kpi:hover { border-color: var(--border-strong); transform: translateY(-1px); }
  .credit-kpi-label { font-family: var(--sans); font-size: 12px; font-weight: 500; letter-spacing: 0; color: var(--text-muted); margin-bottom: 7px; }
  .credit-kpi-value { font-family: var(--sans); font-size: var(--text-md); font-weight: 600; color: var(--text); letter-spacing: -0.01em; }
  .credit-kpi-value.small { font-size: 12px; font-weight: 500; }

  .credit-status-badges { display: flex; gap: 13px; }
  .status-pill { flex: 1; text-align: center; padding: 9px 13px; border-radius: 100px; font-size: 12px; font-weight: 500; background: var(--surface-raised); border: 1px solid var(--border); }
  .status-pill.positive { background: var(--accent-dim); border-color: var(--accent); color: var(--accent); }
  .status-pill.negative { background: var(--red-dim); border-color: rgba(220,38,38,0.25); color: var(--red); }

  .credit-powered-by { text-align: right; font-size: 10px; font-family: var(--sans); letter-spacing: 0.05em; color: var(--text-faint); margin-top: 9px; padding-top: 9px; border-top: 1px solid var(--border); }
  .credit-powered-by strong { font-weight: 600; color: var(--accent); margin-left: 4px; }

  .credit-empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 9px; padding: 30px 16px; text-align: center; }
  .credit-empty-icon { color: var(--text-faint); opacity: 0.6; }
  .credit-empty-icon svg { width: 28px; height: 28px; }
  .credit-empty-text { font-size: 12.5px; color: var(--text-muted); max-width: 220px; line-height: 1.4; }
  .credit-empty-state .credit-powered-by { margin-top: 4px; opacity: 0.6; }

  /* INDUSTRY FILTER BANNER */
  .industry-banner { display: flex; align-items: center; gap: 11px; background: var(--accent-dim); border: 1px solid var(--accent); border-radius: var(--radius-sm); padding: 11px 15px; }
  .industry-banner-text { font-size: 12.5px; color: var(--accent); }
  .industry-banner-text strong { font-weight: 600; }

  /* ── RESPONSIVE, mobile floor ─────────────────────────────────────────── */
  @media (max-width: 900px) {
    .dash-body { flex-direction: column; }
    .dash-sidebar { width: 100%; position: static; }
    .stats-grid { grid-template-columns: repeat(2, 1fr); }
    .stat-card:first-child { grid-column: span 2; }
    .dash-header-inner { padding: 0 16px; }
    .dash-main { padding: 16px 16px 32px; }
  }
  @media (max-width: 480px) {
    .stats-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
    .stat-card:first-child { grid-column: span 2; }
    .stat-value { font-size: 22px; }
    .drawer { width: 100vw; max-width: 100vw; }
    .dash-brand-sub { display: none; }
  }
${SEARCH_TYPEAHEAD_STYLES}
${UPGRADE_LOCK_STYLES}
${CH_DRAWER_STYLES}
`;

// Plan display names now live in config/planAccess.js (Core / Grow / AI / Enterprise).

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "!new", label: "Contacted (all)" },
  { value: "claimed", label: "Claimed" },
  { value: "called_no_answer", label: "Called – No Answer" },
  { value: "follow_up", label: "Follow Up" },
  { value: "not_interested", label: "Not Interested" },
  { value: "qualified", label: "Qualified" },
  { value: "booked", label: "Booked" },
  { value: "disqualified", label: "Disqualified" },
];

// The drawer's Internal Categorisation. "!new" is a filter sentinel, not a
// real status, and the pipeline stages map onto these categories.
const CATEGORISATION_STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "enriched", label: "Enriched" },
  { value: "contacted", label: "Contacted" },
  { value: "claimed", label: "Claimed" },
  { value: "called_no_answer", label: "Called – No Answer" },
  { value: "follow_up", label: "Follow Up" },
  { value: "qualified", label: "Qualified" },
  { value: "booked", label: "Booked" },
  { value: "won", label: "Won" },
  { value: "not_interested", label: "Not Interested" },
  { value: "disqualified", label: "Disqualified" },
];

const getFirstName = (directors) => {
  try {
    if (Array.isArray(directors) && directors.length > 0) {
      const fullName = directors[0].name;
      if (!fullName) return "there";
      const parts = fullName.split(",");
      if (parts.length > 1) return parts[1].trim().split(" ")[0] || "there";
      return parts[0].trim().split(" ")[0] || "there";
    }
    return "there";
  } catch {
    return "there";
  }
};

// Returns { firstName, lastInitial } from directors array (Companies House format: "SURNAME, FIRSTNAME")
const getDirectorNameParts = (directors) => {
  try {
    if (Array.isArray(directors) && directors.length > 0) {
      const fullName = directors[0].name;
      if (!fullName) return null;
      const parts = fullName.split(",");
      let firstName = "",
        lastInitial = "";
      if (parts.length > 1) {
        const givenParts = parts[1].trim().split(" ").filter(Boolean);
        firstName = givenParts[0] || "";
        lastInitial = parts[0].trim()[0] || "";
      } else {
        const nameParts = parts[0].trim().split(" ").filter(Boolean);
        firstName = nameParts[0] || "";
        lastInitial = nameParts[1]?.[0] || "";
      }
      return {
        firstName: firstName.toLowerCase(),
        lastInitial: lastInitial.toLowerCase(),
      };
    }
    return null;
  } catch {
    return null;
  }
};

// Derives email domain from company name: "Peach Loves Ltd" → "peachloves.co.uk"
const companyNameToDomain = (companyName) => {
  if (!companyName) return null;
  const stripped = companyName
    .replace(/\b(limited|ltd\.?|llp|plc|co\.?|company|group|uk)\b/gi, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
  if (!stripped) return null;
  return `${stripped}.co.uk`;
};

// Generates potential email addresses from director + company name
const getPotentialEmails = (directors, companyName) => {
  const nameParts = getDirectorNameParts(directors);
  const domain = companyNameToDomain(companyName);
  if (!nameParts || !domain || !nameParts.firstName) return [];
  const { firstName, lastInitial } = nameParts;
  const emails = [`${firstName}@${domain}`];
  if (lastInitial) emails.push(`${firstName[0]}${lastInitial}@${domain}`);
  return emails;
};

// ── ICONS ─────────────────────────────────────────────────────────────────────
const Icon = {
  Globe: () => (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="2" y1="12" x2="22" y2="12"></line>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
    </svg>
  ),
  Link: () => (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  ),
  Phone: () => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.9 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012.81 1h3a2 2 0 012 1.72c.13.96.36 1.9.68 2.81a2 2 0 01-.45 2.11L6.91 8.86a16 16 0 006.23 6.23l1.22-1.13a2 2 0 012.11-.45c.91.32 1.85.55 2.81.68A2 2 0 0122 16.92z" />
    </svg>
  ),
  Mail: () => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M2 7l10 7 10-7" />
    </svg>
  ),
  Map: () => (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M21 10c0 6-9 13-9 13s-9-7-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  Facebook: ({ color } = {}) => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill={color || "currentColor"}
      stroke={color || "currentColor"}
      strokeWidth="2"
    >
      <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
    </svg>
  ),
  Instagram: ({ color } = {}) => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill={color || "currentColor"}
      stroke={color || "currentColor"}
      strokeWidth="2"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  ),
  LinkedIn: ({ color } = {}) => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill={color || "currentColor"}
      stroke={color || "currentColor"}
      strokeWidth="2"
    >
      <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  ),
  Twitter: ({ color } = {}) => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill={color || "currentColor"}
      stroke={color || "currentColor"}
      strokeWidth="2"
    >
      <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
    </svg>
  ),
  TikTok: ({ color } = {}) => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill={color || "currentColor"}
      stroke={color || "currentColor"}
      strokeWidth="2"
    >
      <path d="M9 12a4 4 0 104 4V4a5 5 0 005 5" />
    </svg>
  ),
  ChevronDown: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  Zap: () => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  X: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Sparkle: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  Lock: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  Filter: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  ),
  Refresh: () => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  ),
  CheckCircle: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  XCircle: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M15 9l-6 6M9 9l6 6" />
    </svg>
  ),
  AlertTriangle: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
};

// ── SORT ICON ──────────────────────────────────────────────────────────────────
const SortIcon = ({ field, sortField, sortOrder }) => {
  const isActive = sortField === field;
  const isAsc = isActive && sortOrder === "asc";
  const isDesc = isActive && sortOrder === "desc";
  return (
    <span className="sort-icon">
      {!isActive && (
        <>
          <svg width="7" height="5" viewBox="0 0 7 5" fill="none">
            <path d="M3.5 0L7 5H0L3.5 0Z" fill="currentColor" />
          </svg>
          <svg width="7" height="5" viewBox="0 0 7 5" fill="none">
            <path d="M3.5 5L0 0H7L3.5 5Z" fill="currentColor" />
          </svg>
        </>
      )}
      {isAsc && (
        <svg
          width="7"
          height="5"
          viewBox="0 0 7 5"
          fill="none"
          style={{ margin: "2px 0" }}
        >
          <path d="M3.5 0L7 5H0L3.5 0Z" fill="var(--accent)" />
        </svg>
      )}
      {isDesc && (
        <svg
          width="7"
          height="5"
          viewBox="0 0 7 5"
          fill="none"
          style={{ margin: "2px 0" }}
        >
          <path d="M3.5 5L0 0H7L3.5 5Z" fill="var(--accent)" />
        </svg>
      )}
    </span>
  );
};

const STORAGE_KEY = (id) => `wizzyl_sent_${id}`;
const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
let toastIdCounter = 0;

// ── ENHANCED STAT CARD ─────────────────────────────────────────────────────────
// const EnhancedStatCard = ({
//   label,
//   value,
//   cls,
//   hint,
//   onClick,
//   trend,
//   sparkline,
//   comparison,
//   sparkColor,
// }) => {
//   const trendDir = trend > 0 ? "up" : trend < 0 ? "down" : "flat";
//   const trendLabel =
//     trend != null
//       ? `${trend > 0 ? "↑" : trend < 0 ? "↓" : "-"} ${Math.abs(trend)}%`
//       : null;
//   return (
//     <div className="stat-card" onClick={onClick} title={hint}>
//       <div className="stat-card-top">
//         <div className="stat-label">
//           {label}
//           <span className="stat-filter-hint">Click to filter →</span>
//         </div>
//         {trendLabel && (
//           <span className={`stat-trend-badge ${trendDir}`}>{trendLabel}</span>
//         )}
//       </div>
//       <div className={`stat-value ${cls}`}>{value.toLocaleString()}</div>
//       {comparison != null && (
//         <div className="stat-comparison">
//           vs last month: <b>{comparison.toLocaleString()}</b>
//         </div>
//       )}
//       <Sparkline points={sparkline} color={sparkColor || "var(--accent)"} />
//     </div>
//   );
// };

const EnhancedStatCard = ({
  label,
  value,
  cls,
  hint,
  onClick,
  trend,
  comparison,
  scope,
  loading = false,
}) => {
  const trendDir = trend > 0 ? "up" : trend < 0 ? "down" : "flat";
  const trendLabel =
    trend != null
      ? `${trend > 0 ? "↑" : trend < 0 ? "↓" : "-"} ${Math.abs(trend)}%`
      : null;
  return (
    <div className="stat-card" onClick={onClick}>
      <div className="stat-card-top">
        <div className="stat-label">
          {label}
          {hint && <InfoTooltip label={`${label}: ${hint}`} content={hint} />}
          <span className="stat-filter-hint">Click to filter →</span>
        </div>
        {trendLabel && (
          <span className={`stat-trend-badge ${trendDir}`}>{trendLabel}</span>
        )}
      </div>
      <div className="stat-value-row">
        <div className={`stat-value ${cls}${loading ? " is-loading" : ""}`}>
          {loading ? "-" : value.toLocaleString()}
        </div>
        {!loading && comparison != null && (
          <div className="stat-comparison">
            vs <b>{comparison.toLocaleString()}</b>
          </div>
        )}
      </div>
      {scope && <div className="stat-scope">{scope}</div>}
      {loading && <div className="stat-loading-bar" aria-label="Loading" />}
    </div>
  );
};

// ── LEAD QUOTA BAR ─────────────────────────────────────────────────────────────
const LeadQuotaBar = ({ customer, onRefresh, refreshing, isSuperAdmin }) => {
  const features = customer?.features;
  const entitlement = customer?.entitlements?.leads || null;
  const max = entitlement?.limit ?? features?.max_leads_per_month ?? 1000;
  const planId = customer?.entitlements?.planId ?? customer?.planId ?? null;
  const isUnlimited = entitlement?.isUnlimited === true || max >= 999999;
  const planLabel = customer?.entitlements?.planName
    ? customer.entitlements.planName.replace(/^AXO /, "")
    : planId
      ? PLAN_LABELS[normalisePlanId(planId)]
      : null;

  const lastRefreshed = features?.last_refreshed_at
    ? new Date(features.last_refreshed_at)
    : null;
  const daysSince = lastRefreshed
    ? (Date.now() - lastRefreshed.getTime()) / (1000 * 60 * 60 * 24)
    : 999;
  const canRefresh = daysSince >= 30;
  const daysLeft = canRefresh ? 0 : Math.ceil(30 - daysSince);

  if (isUnlimited) {
    return (
      <div className="quota-bar">
        <div className="quota-unlimited">
          <div className="quota-unlimited-label">
            Monthly Companies
            <InfoTooltip
              label="What is the monthly company allowance?"
              content="How many new companies are added to your feed each month on your current plan. Unlimited plans have no monthly cap."
            />
            {planLabel && <span className="quota-plan-badge">{planLabel}</span>}
          </div>
          <div className="quota-unlimited-value">∞ Unlimited</div>
        </div>
      </div>
    );
  }

  return (
    <div className="quota-bar">
      <div className="quota-bar-header">
        <div className="quota-bar-left">
          <span className="quota-bar-label">
            Monthly Companies
            <InfoTooltip
              label="What is the monthly company allowance?"
              content="How many new companies are added to your feed each month on your current plan."
            />
          </span>
          {planLabel && <span className="quota-plan-badge">{planLabel}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="quota-bar-right">
            {max.toLocaleString()} companies/month
          </span>
          {!isSuperAdmin && (
            <button
              className={`btn-refresh ${refreshing ? "loading" : canRefresh ? "available" : "unavailable"}`}
              onClick={canRefresh && !refreshing ? onRefresh : undefined}
              title={
                refreshing
                  ? "Refreshing…"
                  : canRefresh
                    ? "Get fresh companies added to your feed"
                    : `Next refresh available in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}`
              }
            >
              <Icon.Refresh />
              {refreshing
                ? "Refreshing…"
                : canRefresh
                  ? "Refresh Leads"
                  : `Refreshes in ${daysLeft}d`}
            </button>
          )}
        </div>
      </div>
      <div className="quota-bar-footer">
        <div className="quota-bar-sub">
          {lastRefreshed ? (
            <>
              Last refreshed:{" "}
              <b>
                {lastRefreshed.toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </b>
            </>
          ) : (
            <span>Companies assigned on signup</span>
          )}
        </div>
        <div
          style={{
            fontFamily: "var(--mono)",
            fontSize: "10.5px",
            color: "var(--text-faint)",
            letterSpacing: "0.02em",
          }}
        >
          {planLabel} Plan · {max.toLocaleString()} companies/mo
        </div>
      </div>
    </div>
  );
};

const getWizzylPreviewUrl = (dudaUrl) => {
  if (!dudaUrl) return "";
  const match = dudaUrl.match(/\/site\/([a-zA-Z0-9_-]+)\//);
  if (!match) return dudaUrl;
  return `https://builder.wizzyl.com/preview/${match[1]}?t=${Date.now()}`;
};

// ── COMPANY ROW (with optional checkbox) ───────────────────────────────────────
const CompanyRow = ({
  company,
  isSoleTrader,
  onTriggerEnrich,
  isNew,
  onOpenDrawer,
  density,
  onToast,
  checkbox = false,
  isSelected = false,
  onSelectChange = null,
}) => {
  const displayName = isSoleTrader ? company.businessName : company.companyName;
  const [enrichingInline, setEnrichingInline] = useState(false);
  const densityClass =
    {
      compact: "density-compact",
      comfortable: "density-comfortable",
      spacious: "density-spacious",
    }[density] || "density-comfortable";

  const handleQuickEnrich = async (e) => {
    e.stopPropagation();
    setEnrichingInline(true);
    try {
      await onTriggerEnrich(
        company.id,
        () => {},
        (freshData) => {},
      );
      onToast("Enrichment triggered", "success");
    } catch {
      onToast("Enrichment failed", "error");
    } finally {
      setEnrichingInline(false);
    }
  };

  return (
    <tr className={densityClass} onClick={() => onOpenDrawer(company)}>
      {checkbox && (
        <td
          style={{ width: 32, verticalAlign: "middle" }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelectChange?.(company.id, e.target.checked)}
          />
        </td>
      )}
      <td style={{ minWidth: 180 }}>
        <div className="cell-company-name">{displayName}</div>
        {isSoleTrader ? (
          company.website && (
            <a
              href={toExternalUrl(company.website)}
              target="_blank"
              rel="noopener noreferrer"
              className="cell-company-num"
              onClick={(e) => e.stopPropagation()}
            >
              <Icon.Globe /> Visit Website
            </a>
          )
        ) : (
          <a
            href={`https://find-and-update.company-information.service.gov.uk/company/${company.companyNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="cell-company-num"
            onClick={(e) => e.stopPropagation()}
          >
            <Icon.Link /> {company.companyNumber}
          </a>
        )}
        {isSoleTrader ? (
          <div className="cell-sic">
            {company.category} · {company.postcode}
          </div>
        ) : (
          (company.sicCodes || company.sicDescription) && (
            <div className="cell-sic">
              {company.sicCodes && <>SIC {company.sicCodes}</>}
              {company.sicDescription && (
                <span> · {company.sicDescription}</span>
              )}
            </div>
          )
        )}
        {isNew && <div className="badge-new">New</div>}
        {/* {company.lastEmailType &&
          (() => {
            const EMAIL_TYPE_LABEL = {
              preview: "Wizzyl Outreach Sent",
              followup: "Wizzyl Follow-up Sent",
              intro: "AXO Signal Outreach Sent",
            };
            const label = EMAIL_TYPE_LABEL[company.lastEmailType];
            if (!label) return null;
            return (
              <div className="badge-emailed">
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="1" y="3" width="14" height="10" rx="1.5" />
                  <path d="M1 5l7 5 7-5" />
                </svg>
                {label}
              </div>
            );
          })()} */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            marginTop: "5px",
          }}
        >
          {/* Turnover from the latest filed accounts, or a provider estimate */}
          {formatMoneyCompact(company.turnover) && (
            <div
              className="badge-turnover"
              title={
                company.turnoverEstimated
                  ? `Estimated turnover from ${company.turnoverSource || "provider"}`
                  : company.turnoverPeriodEnd
                    ? `Turnover to ${new Date(company.turnoverPeriodEnd).toLocaleDateString("en-GB")}`
                    : "Turnover from latest filed accounts"
              }
            >
              {formatMoneyCompact(company.turnover)} turnover
              {company.turnoverEstimated ? " (est.)" : ""}
            </div>
          )}
          {/* AXO intro badge – always show if sent */}
          {company.axoOutreachEmailSentAt && (
            <div className="badge-emailed">AXO Signal Outreach Sent</div>
          )}
          {/* Wizzyl preview badge – only if site ready AND email was sent */}
          {company.wizzylOutreachEmailSentAt &&
            company.enrichment?.dudaStatus === "ready" &&
            company.enrichment?.previewUrl && (
              <div className="badge-emailed">Wizzyl Outreach Sent</div>
            )}
          {/* Wizzyl follow-up badge – only if site ready AND email was sent */}
          {company.wizzylFollowUpEmailSentAt &&
            company.enrichment?.dudaStatus === "ready" &&
            company.enrichment?.previewUrl && (
              <div className="badge-emailed">Wizzyl Follow-up Sent</div>
            )}
          {/* Brief form badge – no dedicated timestamp column like the
              others (brief has nothing to be "ready" for yet), so this
              keys off lastEmailType directly instead. Once the site has
              actually been generated, swap it for a link to the preview
              rather than leaving a stale "Brief Form Sent" badge. */}
          {company.lastEmailType === "brief" &&
            (company.enrichment?.dudaStatus === "ready" &&
            company.enrichment?.previewUrl ? (
              <a
                href={getWizzylPreviewUrl(company.enrichment.previewUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="badge-emailed"
              >
                Preview Generated Website
              </a>
            ) : (
              <div className="badge-emailed">Brief Form Sent</div>
            ))}
        </div>
      </td>

      {!isSoleTrader && (
        <td style={{ minWidth: 160 }}>
          {Array.isArray(company.directors) && company.directors.length > 0 ? (
            company.directors.map((dir, i) => (
              <span key={i} className="director-tag">
                {typeof dir === "string" ? dir : dir.name || "Unknown"}
              </span>
            ))
          ) : typeof company.directors === "string" ? (
            company.directors
              .split("|")
              .filter((d) => d.trim())
              .map((d, i) => (
                <span key={i} className="director-tag">
                  {d.trim()}
                </span>
              ))
          ) : (
            <span className="director-pending">
              {company.directorsFetchedAt ? "None listed" : "Pending…"}
            </span>
          )}
        </td>
      )}

      <td style={{ minWidth: 110 }}>
        <div className="date-val">
          {isSoleTrader
            ? company.discoveredAt
              ? new Date(company.discoveredAt).toLocaleDateString("en-GB")
              : "-"
            : company.incorporatedAt
              ? new Date(company.incorporatedAt).toLocaleDateString("en-GB")
              : "-"}
        </div>
      </td>

      <td style={{ minWidth: "max-content", whiteSpace: "nowrap" }}>
        {getRowPhone(company, isSoleTrader) && (
          <div className="contact-item">
            <Icon.Phone />
            <a
              href={`tel:${getRowPhone(company, isSoleTrader)}`}
              className="contact-link"
            >
              {getRowPhone(company, isSoleTrader)}
            </a>
          </div>
        )}
        {!isSoleTrader && (company.enrichment?.email || company.email) && (
          <div className="contact-item">
            <Icon.Mail />
            <a
              href={`mailto:${company.enrichment?.email || company.email}`}
              className="contact-link"
              onClick={(e) => e.stopPropagation()}
            >
              {company.enrichment?.email || company.email}
            </a>
          </div>
        )}
        {getRowWebsite(company, isSoleTrader) && (
          <div className="contact-item">
            <Icon.Link />
            <a
              href={toExternalUrl(getRowWebsite(company, isSoleTrader))}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-link"
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                try {
                  return new URL(
                    toExternalUrl(getRowWebsite(company, isSoleTrader)),
                  ).hostname.replace(/^www\./, "");
                } catch (_) {
                  return getRowWebsite(company, isSoleTrader);
                }
              })()}
            </a>
          </div>
        )}
        {!isSoleTrader && (
          <div className="contact-item" style={{ marginTop: 2 }}>
            <Icon.Map />
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(displayName + " " + (company.postcode || ""))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="maps-link"
              onClick={(e) => e.stopPropagation()}
            >
              {company.locality || company.postcode || "View on Maps"}
            </a>
          </div>
        )}
      </td>

      <td
        style={{
          width: "1px",
          whiteSpace: "nowrap",
          verticalAlign: "middle",
          textAlign: "right",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            justifyContent: "flex-end",
          }}
        >
          <div className="row-quick-actions">
            {!isSoleTrader &&
              !company.enrichment?.phone &&
              !company.enrichment?.email && (
                <button
                  className="row-action-btn"
                  onClick={handleQuickEnrich}
                  disabled={enrichingInline}
                >
                  <span className="row-tooltip">Quick Enrich</span>
                  <Icon.Sparkle />
                </button>
              )}
          </div>
          <button
            className="btn-expand"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDrawer(company);
            }}
            title="Open Details"
          >
            <Icon.ChevronDown />
          </button>
        </div>
      </td>
    </tr>
  );
};

// Cell renderers extracted from CompanyRow so the leads table can use Arc's
// SortableDataTable columns while keeping the same markup.
const LeadsCompanyCell = ({ company, isSoleTrader, isNew, market = "GB" }) => {
  const displayName = isSoleTrader ? company.businessName : company.companyName;
  const isUk = !market || market === "GB";
  return (
    <>
      <div className="cell-company-name">{displayName}</div>
      {isSoleTrader ? (
        company.website && (
          <a
            href={toExternalUrl(company.website)}
            target="_blank"
            rel="noopener noreferrer"
            className="cell-company-num"
          >
            <Icon.Globe /> Visit Website
          </a>
        )
      ) : isUk ? (
        <a
          href={`https://find-and-update.company-information.service.gov.uk/company/${company.companyNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          className="cell-company-num"
        >
          <Icon.Link /> {company.companyNumber}
        </a>
      ) : (
        <span className="cell-company-num">
          <Icon.Link /> {company.companyNumber}
        </span>
      )}
      {isSoleTrader ? (
        <div className="cell-sic">
          {company.category} · {company.postcode}
        </div>
      ) : (
        (company.sicCodes || company.sicDescription) && (
          <div className="cell-sic">
            {company.sicCodes && <>SIC {company.sicCodes}</>}
            {company.sicDescription && <span> · {company.sicDescription}</span>}
          </div>
        )
      )}
      {isNew && <div className="badge-new">New</div>}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "4px",
          marginTop: "5px",
        }}
      >
        {formatMoneyCompact(company.turnover) && (
          <div
            className="badge-turnover"
            title={
              company.turnoverEstimated
                ? `Estimated turnover from ${company.turnoverSource || "provider"}`
                : company.turnoverPeriodEnd
                  ? `Turnover to ${new Date(company.turnoverPeriodEnd).toLocaleDateString("en-GB")}`
                  : "Turnover from latest filed accounts"
            }
          >
            {formatMoneyCompact(company.turnover)} turnover
            {company.turnoverEstimated ? " (est.)" : ""}
          </div>
        )}
        {company.axoOutreachEmailSentAt && (
          <div className="badge-emailed">AXO Signal Outreach Sent</div>
        )}
        {company.wizzylOutreachEmailSentAt &&
          company.enrichment?.dudaStatus === "ready" &&
          company.enrichment?.previewUrl && (
            <div className="badge-emailed">Wizzyl Outreach Sent</div>
          )}
        {company.wizzylFollowUpEmailSentAt &&
          company.enrichment?.dudaStatus === "ready" &&
          company.enrichment?.previewUrl && (
            <div className="badge-emailed">Wizzyl Follow-up Sent</div>
          )}
        {company.lastEmailType === "brief" &&
          (company.enrichment?.dudaStatus === "ready" &&
          company.enrichment?.previewUrl ? (
            <a
              href={getWizzylPreviewUrl(company.enrichment.previewUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="badge-emailed"
            >
              Preview Generated Website
            </a>
          ) : (
            <div className="badge-emailed">Brief Form Sent</div>
          ))}
      </div>
    </>
  );
};

const LeadsDirectorsCell = ({ company }) => {
  if (Array.isArray(company.directors) && company.directors.length > 0) {
    return company.directors.map((dir, i) => (
      <span key={i} className="director-tag">
        {typeof dir === "string" ? dir : dir.name || "Unknown"}
      </span>
    ));
  }
  if (typeof company.directors === "string") {
    return company.directors
      .split("|")
      .filter((d) => d.trim())
      .map((d, i) => (
        <span key={i} className="director-tag">
          {d.trim()}
        </span>
      ));
  }
  return (
    <span className="director-pending">
      {company.directorsFetchedAt ? "None listed" : "Pending…"}
    </span>
  );
};

const LeadsContactCell = ({ company, isSoleTrader, displayName }) => (
  <>
    {getRowPhone(company, isSoleTrader) && (
      <div className="contact-item">
        <Icon.Phone />
        <a
          href={`tel:${getRowPhone(company, isSoleTrader)}`}
          className="contact-link"
        >
          {getRowPhone(company, isSoleTrader)}
        </a>
      </div>
    )}
    {!isSoleTrader && (company.enrichment?.email || company.email) && (
      <div className="contact-item">
        <Icon.Mail />
        <a
          href={`mailto:${company.enrichment?.email || company.email}`}
          className="contact-link"
        >
          {company.enrichment?.email || company.email}
        </a>
      </div>
    )}
    {getRowWebsite(company, isSoleTrader) && (
      <div className="contact-item">
        <Icon.Link />
        <a
          href={toExternalUrl(getRowWebsite(company, isSoleTrader))}
          target="_blank"
          rel="noopener noreferrer"
          className="contact-link"
        >
          {(() => {
            try {
              return new URL(
                toExternalUrl(getRowWebsite(company, isSoleTrader)),
              ).hostname.replace(/^www\./, "");
            } catch (_) {
              return getRowWebsite(company, isSoleTrader);
            }
          })()}
        </a>
      </div>
    )}
    {!isSoleTrader && (
      <div className="contact-item" style={{ marginTop: 2 }}>
        <Icon.Map />
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(displayName + " " + (company.postcode || ""))}`}
          target="_blank"
          rel="noopener noreferrer"
          className="maps-link"
        >
          {company.locality || company.postcode || "View on Maps"}
        </a>
      </div>
    )}
  </>
);

const LeadsRowActions = ({
  company,
  isSoleTrader,
  onTriggerEnrich,
  onToast,
  onOpenDrawer,
}) => {
  const [enrichingInline, setEnrichingInline] = useState(false);
  const handleQuickEnrich = async (e) => {
    e.stopPropagation();
    setEnrichingInline(true);
    try {
      await onTriggerEnrich(
        company.id,
        () => {},
        () => {},
      );
      onToast("Enrichment triggered", "success");
    } catch {
      onToast("Enrichment failed", "error");
    } finally {
      setEnrichingInline(false);
    }
  };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        justifyContent: "flex-end",
      }}
    >
      <div className="row-quick-actions">
        {!isSoleTrader &&
          !company.enrichment?.phone &&
          !company.enrichment?.email && (
            <button
              className="row-action-btn"
              onClick={handleQuickEnrich}
              disabled={enrichingInline}
            >
              <span className="row-tooltip">Quick Enrich</span>
              <Icon.Sparkle />
            </button>
          )}
      </div>
      <button
        className="btn-expand"
        onClick={(e) => {
          e.stopPropagation();
          onOpenDrawer(company);
        }}
        title="Open Details"
      >
        <Icon.ChevronDown />
      </button>
    </div>
  );
};

// ── SKELETON ROWS ──────────────────────────────────────────────────────────────
const SkeletonRows = ({ count = 8 }) => (
  <tr className="skeleton-row">
    <td colSpan={6} style={{ padding: "14px 12px" }}>
      <Skeleton lines={count} label="Loading companies" />
    </td>
  </tr>
);

const TOAST_ICONS = {
  success: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M5.5 8l1.8 1.8 3.2-3.2" />
    </svg>
  ),
  error: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" />
    </svg>
  ),
  info: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 7.5v4M8 5.5v.01" />
    </svg>
  ),
};

// Splits "3 sent, 0 failed, 1 skipped (unsubscribed)" style messages into headline + sub
const parseToastMessage = (message, type) => {
  // Bulk send pattern
  const bulkMatch = message.match(/^Bulk send finished:\s*(.+)$/);
  if (bulkMatch) {
    return { headline: "Bulk send complete", sub: bulkMatch[1] };
  }
  return { headline: message, sub: null };
};

// ── TOAST CONTAINER ────────────────────────────────────────────────────────────
const ToastContainer = ({ toasts, onDismiss }) => (
  <div className="toast-container">
    {toasts.map((t) => {
      const { headline, sub } = parseToastMessage(t.message, t.type);
      return (
        <div key={t.id} className={`toast toast-${t.type}`}>
          <div className="toast-icon">
            {TOAST_ICONS[t.type] || TOAST_ICONS.info}
          </div>
          <div className="toast-content">
            <div className="toast-headline">{headline}</div>
            {sub && <div className="toast-sub">{sub}</div>}
          </div>
          {onDismiss && (
            <button
              className="toast-dismiss"
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss"
            >
              <svg
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M1 1l10 10M11 1L1 11" />
              </svg>
            </button>
          )}
        </div>
      );
    })}
  </div>
);

// ── PROFILE COMPLETENESS RING ──────────────────────────────────────────────────
const CONTACT_SOURCE_LABELS = {
  google_places: "Google Places",
  website: "Company website",
  linkedin: "LinkedIn",
  yell: "Yell",
  thomson_local: "Thomson Local",
  facebook: "Facebook",
  manual: "Manual entry",
  credit: "Credit report",
};

// Resolve where the value currently shown in the drawer came from.
// Returns { label, title } or null when provenance is unknown (e.g. records
// enriched before sources were recorded), so we never show a guessed source.
const resolveContactSource = (field, shownValue, enrichData, creditValue) => {
  if (!shownValue) return null;
  if (creditValue && shownValue === creditValue) {
    return { label: CONTACT_SOURCE_LABELS.credit, title: null };
  }
  if (!enrichData || enrichData[field] !== shownValue) return null;
  const src = enrichData.contactSources?.[field];
  if (!src?.source) return null;
  const label = CONTACT_SOURCE_LABELS[src.source];
  if (!label) return null;
  let title = null;
  if (src.url) {
    try {
      title = `Found on ${new URL(src.url).hostname.replace(/^www\./, "")}`;
    } catch {
      /* ignore bad url */
    }
  }
  return { label, title };
};

const ProfileCompletenessRing = ({ enrichData, company }) => {
  const fields = [
    { key: "phone", label: "Phone", value: enrichData?.phone },
    {
      key: "email",
      label: "Email",
      value: enrichData?.email || company?.email,
    },
    { key: "website", label: "Website", value: enrichData?.website },
  ];
  const presentCount = fields.filter((f) => !!f.value).length;
  const pct = Math.round((presentCount / fields.length) * 100);
  const radius = 27,
    circ = 2 * Math.PI * radius;
  const offset = circ - (pct / 100) * circ;
  const ringColor =
    pct === 100 ? "var(--green)" : pct >= 50 ? "var(--accent)" : "var(--amber)";
  return (
    <div className="completeness-card">
      <div className="completeness-ring-wrap">
        <svg width="64" height="64" viewBox="0 0 64 64">
          <circle
            className="completeness-ring-track"
            cx="32"
            cy="32"
            r={radius}
          />
          <circle
            className="completeness-ring-fill"
            cx="32"
            cy="32"
            r={radius}
            style={{
              stroke: ringColor,
              strokeDasharray: circ,
              strokeDashoffset: offset,
            }}
          />
        </svg>
        <div className="completeness-ring-label">{pct}%</div>
      </div>
      <div className="completeness-info">
        <div className="completeness-title">
          Profile Completeness
          <InfoTooltip
            label="What is profile completeness?"
            content="How much of this company's profile AXO has. The more fields filled in, the more confident its recommendations and outreach will be."
          />
        </div>
        <div className="completeness-missing">
          {fields.map((f) => (
            <span
              key={f.key}
              className={`completeness-chip ${f.value ? "present" : "missing"}`}
            >
              {f.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── CREDIT PANEL ───────────────────────────────────────────────────────────────

// const CreditPanel = ({ credit }) => {
//   if (!credit) return null;

//   const score = credit.creditScore ?? credit.latestCreditRating ?? 0;
//   const category = (credit.creditRatingCategory || "").toLowerCase();
//   const riskLevel = (credit.riskLevel || "").toLowerCase();
//   const creditWorthy = credit.creditWorthy === "Y";
//   const hasNegative = credit.negativeIndicator === "Y";

//   const scoreColor =
//     category === "excellent"
//       ? "var(--green)"
//       : category === "good"
//         ? "var(--accent)"
//         : category === "fair"
//           ? "var(--amber)"
//           : "var(--red)";

//   const dialRadius = 26;
//   const dialCirc = 2 * Math.PI * dialRadius;
//   const dialOffset = dialCirc - (score / 100) * dialCirc;

//   const fmt = (val) => {
//     if (!val) return "-";
//     const n = parseFloat(val);
//     if (isNaN(n)) return val;
//     return "£" + n.toLocaleString("en-GB", { maximumFractionDigits: 0 });
//   };

//   const fmtDate = (d) => {
//     if (!d) return null;
//     try {
//       return new Date(d).toLocaleDateString("en-GB", {
//         day: "numeric",
//         month: "short",
//         year: "numeric",
//       });
//     } catch {
//       return d;
//     }
//   };

//   return (
//     <div className="credit-panel">
//       {/* Score dial + category */}
//       <div className="credit-score-row">
//         <div className="credit-score-dial">
//           <svg width="68" height="68" viewBox="0 0 68 68">
//             <circle
//               cx="34"
//               cy="34"
//               r={dialRadius}
//               fill="none"
//               stroke="var(--border)"
//               strokeWidth="5"
//             />
//             <circle
//               cx="34"
//               cy="34"
//               r={dialRadius}
//               fill="none"
//               stroke={scoreColor}
//               strokeWidth="5"
//               strokeLinecap="round"
//               strokeDasharray={dialCirc}
//               strokeDashoffset={dialOffset}
//               style={{
//                 transform: "rotate(-90deg)",
//                 transformOrigin: "34px 34px",
//                 transition: "stroke-dashoffset 0.6s cubic-bezier(0.16,1,0.3,1)",
//               }}
//             />
//           </svg>
//           <div className="credit-score-label">
//             <span className="credit-score-num" style={{ color: scoreColor }}>
//               {score}
//             </span>
//             <span className="credit-score-max">/100</span>
//           </div>
//         </div>
//         <div className="credit-score-meta">
//           <div className={`credit-category-badge ${category}`}>
//             {credit.creditRatingCategory || "Unknown"}
//           </div>
//           <div className="credit-risk-row">
//             <span className={`credit-risk-dot ${riskLevel}`} />
//             <span>{credit.riskLevel || "-"} Risk</span>
//           </div>
//           {fmtDate(credit.creditRatingDate) && (
//             <div
//               className="credit-date"
//               style={{ marginTop: 6, textAlign: "left" }}
//             >
//               Rated {fmtDate(credit.creditRatingDate)}
//             </div>
//           )}
//         </div>
//       </div>

//       {/* KPI grid – accent border, default text colour */}
//       <div className="credit-kpi-grid">
//         <div className="credit-kpi">
//           <div className="credit-kpi-label">Credit Limit</div>
//           <div className="credit-kpi-value">
//             {fmt(credit.latestCreditLimit)}
//           </div>
//         </div>
//         <div className="credit-kpi">
//           <div className="credit-kpi-label">Contract Limit</div>
//           <div className="credit-kpi-value">{fmt(credit.contractLimit)}</div>
//         </div>
//         <div className="credit-kpi">
//           <div className="credit-kpi-label">Recommended Limit</div>
//           <div className="credit-kpi-value">
//             {fmt(credit.recommendedCreditLimit)}
//           </div>
//         </div>
//         <div className="credit-kpi">
//           <div className="credit-kpi-label">Last Updated</div>
//           <div className="credit-kpi-value small">
//             {fmtDate(credit.lastImportedAt) || "-"}
//           </div>
//         </div>
//       </div>

//       {/* Status badges – accent for positive */}
//       <div className="credit-status-badges">
//         <div
//           className={`status-pill ${creditWorthy ? "positive" : "negative"}`}
//         >
//           {creditWorthy ? "Credit Worthy" : "Not Credit Worthy"}
//         </div>
//         <div className={`status-pill ${hasNegative ? "negative" : "positive"}`}>
//           {hasNegative ? "Negative Indicator" : "No Negatives"}
//         </div>
//       </div>

//       {/* Powered by CreditSafe */}
//       <div className="credit-powered-by">
//         <span>Powered by</span>
//         <strong>CreditSafe</strong>
//       </div>
//     </div>
//   );
// };

// ── CREDIT PANEL ───────────────────────────────────────────────────────────────
const CreditPanel = ({ credit }) => {
  if (!credit) return null;

  // A credit row can exist (matched via companyReg) but carry no actual
  // CreditSafe rating data yet, treat that the same as "no data available"
  // rather than rendering a panel full of "Unknown" / "-" placeholders.
  const hasData =
    credit.creditScore != null ||
    credit.latestCreditRating != null ||
    !!credit.creditRatingCategory ||
    credit.latestCreditLimit != null;

  if (!hasData) {
    return (
      <div className="credit-empty-state">
        <div className="credit-empty-text">
          No CreditSafe data available for this company
        </div>
        <div className="credit-powered-by">
          <span>Powered by</span>
          <strong>CreditSafe</strong>
        </div>
      </div>
    );
  }

  const score = credit.creditScore ?? credit.latestCreditRating ?? 0;
  const category = (credit.creditRatingCategory || "").toLowerCase();
  const riskLevel = (credit.riskLevel || "").toLowerCase();
  const creditWorthy = credit.creditWorthy === "Y";
  const hasNegative = credit.negativeIndicator === "Y";

  const scoreColor =
    category === "excellent"
      ? "var(--green)"
      : category === "good"
        ? "var(--accent)"
        : category === "fair"
          ? "var(--amber)"
          : "var(--red)";

  const dialRadius = 26;
  const dialCirc = 2 * Math.PI * dialRadius;
  const dialOffset = dialCirc - (score / 100) * dialCirc;

  const fmt = (val) => {
    if (!val) return "-";
    const n = parseFloat(val);
    if (isNaN(n)) return val;
    return "£" + n.toLocaleString("en-GB", { maximumFractionDigits: 0 });
  };

  const fmtDate = (d) => {
    if (!d) return null;
    try {
      return new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return d;
    }
  };

  return (
    <div className="credit-panel">
      {/* Score dial + category */}
      <div className="credit-score-row">
        <div className="credit-score-dial">
          <svg width="68" height="68" viewBox="0 0 68 68">
            <circle
              cx="34"
              cy="34"
              r={dialRadius}
              fill="none"
              stroke="var(--border)"
              strokeWidth="5"
            />
            <circle
              cx="34"
              cy="34"
              r={dialRadius}
              fill="none"
              stroke={scoreColor}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={dialCirc}
              strokeDashoffset={dialOffset}
              style={{
                transform: "rotate(-90deg)",
                transformOrigin: "34px 34px",
                transition: "stroke-dashoffset 0.6s cubic-bezier(0.16,1,0.3,1)",
              }}
            />
          </svg>
          <div className="credit-score-label">
            <span className="credit-score-num" style={{ color: scoreColor }}>
              {score}
            </span>
            <span className="credit-score-max">/100</span>
          </div>
        </div>
        <div className="credit-score-meta">
          <div className={`credit-category-badge ${category}`}>
            {credit.creditRatingCategory || "Unknown"}
          </div>
          <div className="credit-risk-row">
            <span className={`credit-risk-dot ${riskLevel}`} />
            <span>{credit.riskLevel || "-"} Risk</span>
          </div>
          {fmtDate(credit.creditRatingDate) && (
            <div
              className="credit-date"
              style={{ marginTop: 6, textAlign: "left" }}
            >
              Rated {fmtDate(credit.creditRatingDate)}
            </div>
          )}
        </div>
      </div>

      {/* KPI grid – accent border, default text colour */}
      <div className="credit-kpi-grid">
        <div className="credit-kpi">
          <div className="credit-kpi-label">Credit Limit</div>
          <div className="credit-kpi-value">
            {fmt(credit.latestCreditLimit)}
          </div>
        </div>
        <div className="credit-kpi">
          <div className="credit-kpi-label">Contract Limit</div>
          <div className="credit-kpi-value">{fmt(credit.contractLimit)}</div>
        </div>
        <div className="credit-kpi">
          <div className="credit-kpi-label">Recommended Limit</div>
          <div className="credit-kpi-value">
            {fmt(credit.recommendedCreditLimit)}
          </div>
        </div>
        <div className="credit-kpi">
          <div className="credit-kpi-label">Last Updated</div>
          <div className="credit-kpi-value small">
            {fmtDate(credit.lastImportedAt) || "-"}
          </div>
        </div>
      </div>

      {/* Status badges – accent for positive */}
      <div className="credit-status-badges">
        <div
          className={`status-pill ${creditWorthy ? "positive" : "negative"}`}
        >
          {creditWorthy ? "Credit Worthy" : "Not Credit Worthy"}
        </div>
        <div className={`status-pill ${hasNegative ? "negative" : "positive"}`}>
          {hasNegative ? "Negative Indicator" : "No Negatives"}
        </div>
      </div>

      {/* Powered by CreditSafe */}
      <div className="credit-powered-by">
        <span>Powered by</span>
        <strong>CreditSafe</strong>
      </div>
    </div>
  );
};

// ── COMPANY DRAWER ─────────────────────────────────────────────────────────────
const CompanyDrawer = forwardRef(
  (
    {
      company,
      scoreCache,
      isSoleTrader,
      onClose,
      onUpdate,
      onTriggerDuda,
      onResumeDudaPoll,
      onTriggerEnrich,
      onResync,
      dudaEnabled,
      onToast,
      onEmailSent,
      onFinancialsRefreshed,
    },
    ref,
  ) => {
    const { user } = useAuth();
    const { can, upgradeLabel } = usePlanAccess();
    const canEnrich = can(CAP.ENRICHMENT); // Core is view-only
    const [status, setStatus] = useState(company.workflow?.status || "new");
    const [notes, setNotes] = useState(company.workflow?.notes || "");
    const [saved, setSaved] = useState(false);
    const [refreshingFin, setRefreshingFin] = useState(false);
    const [financialsError, setFinancialsError] = useState(null);
    const [milestones, setMilestones] = useState([]);
    const [milestoneMeta, setMilestoneMeta] = useState({
      periods: 0,
      latestPeriod: null,
    });
    const [milestonesLoading, setMilestonesLoading] = useState(false);

    useEffect(() => {
      let cancelled = false;
      if (!company?.id) return undefined;
      setMilestonesLoading(true);
      apiService
        .getCompanyMilestones(company.id)
        .then((res) => {
          if (!cancelled) {
            setMilestones(res?.data || []);
            setMilestoneMeta(res?.meta || { periods: 0, latestPeriod: null });
          }
        })
        .catch(() => {
          if (!cancelled) {
            setMilestones([]);
            setMilestoneMeta({ periods: 0, latestPeriod: null });
          }
        })
        .finally(() => {
          if (!cancelled) setMilestonesLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }, [company?.id]);

    // Pull this company's latest filed accounts and surface the figures
    // immediately (no backfill needed).
    const handleRefreshFinancials = async () => {
      if (!company.companyNumber || refreshingFin) return;
      setRefreshingFin(true);
      setFinancialsError(null);
      try {
        const res = await apiService.refreshCompanyFinancials(
          company.companyNumber,
        );
        const bs = res?.data?.balanceSheet || null;
        if (bs) {
          const netAssets = bs.netAssets ?? null;
          const totalAssets = bs.totalAssets ?? null;
          onFinancialsRefreshed?.({
            turnover: bs.turnover ?? null,
            turnoverPeriodEnd: bs.periodEnd ?? null,
            turnoverEstimated: false,
            turnoverSource: bs.source || "ch_ixbrl",
            netAssets,
            totalAssets,
            currentAssets: bs.currentAssets ?? null,
            debtors: bs.debtors ?? null,
            cash: bs.cash ?? null,
            creditorsDueWithinOneYear: bs.creditorsDueWithinOneYear ?? null,
            netCurrentAssets: bs.netCurrentAssets ?? null,
            totalAssetsLessCurrentLiabilities:
              bs.totalAssetsLessCurrentLiabilities ?? null,
            size: netAssets ?? totalAssets ?? null,
          });
          onToast?.(
            bs.turnover != null
              ? "Turnover updated from the latest filed accounts"
              : "Accounts refreshed: this filing has no turnover",
            bs.turnover != null ? "success" : "info",
          );
        } else {
          onToast?.(
            "No machine-readable figures found for this company",
            "info",
          );
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message || "Could not refresh financials";
        setFinancialsError(msg);
        onToast?.(msg, "error");
      } finally {
        setRefreshingFin(false);
      }
    };
    const [enrichData, setEnrichData] = useState(company.enrichment || null);
    const [dudaState, setDudaState] = useState(() => {
      const s = company.enrichment?.dudaStatus || "none";
      const qs = company.enrichment?.qaStatus || null;
      // Same QA gate as the enrichData sync effect below, without this,
      // mounting with a "ready"-but-unapproved site sets dudaState="ready"
      // one tick before the sync effect corrects it, and the resume-poll
      // effect (which reads dudaState on mount) sees "ready" and never
      // starts polling, so an approval that lands later never gets picked
      // up without a full page refresh.
      if (
        s === "ready" &&
        (qs === "pending_qa" || qs === "regeneration_requested")
      ) {
        return "processing";
      }
      return s;
    });
    const [qaState, setQaState] = useState(
      company.enrichment?.qaStatus || null,
    );
    const [resyncState, setResyncState] = useState("idle");
    const [firstSentAt, setFirstSentAt] = useState(() => {
      const s = localStorage.getItem(STORAGE_KEY(company.id));
      return s ? parseInt(s, 10) : null;
    });
    const alreadyEnriched = !!(
      enrichData?.phone ||
      enrichData?.email ||
      enrichData?.website
    );
    const [enrichState, setEnrichState] = useState(
      company.enrichment?.phone ||
        company.enrichment?.email ||
        company.enrichment?.website
        ? "done"
        : "idle",
    );
    const [showEmailInput, setShowEmailInput] = useState(false);
    const [emailInput, setEmailInput] = useState("");
    const [emailSaving, setEmailSaving] = useState(false);
    const [emailSaveError, setEmailSaveError] = useState(null);
    const [showPotentialEmails, setShowPotentialEmails] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    // NOTE: do not seed this from company.wizzylOutreachEmailSentAt, that
    // flag can be stale from a previously generated Duda site. The CTA
    // should default to "Send Pitch Email" for a freshly-built site and
    // only flip to "sent" once the user actually sends it in this session.
    const [sentSuccess, setSentSuccess] = useState(
      () => !!company.wizzylOutreachEmailSentAt,
    );
    const [followUpSent, setFollowUpSent] = useState(
      () => !!company.wizzylFollowUpEmailSentAt,
    );
    const [sendingIntroEmail, setSendingIntroEmail] = useState(null);
    const [sendingBrief, setSendingBrief] = useState(false);
    // Seeded from lastEmailType, same reasoning as followUpSent below, brief
    // has no dedicated timestamp column, lastEmailType is the only signal.
    const [briefSent, setBriefSent] = useState(
      () => company.lastEmailType === "brief",
    );

    const handleSendIntroEmail = async (
      email,
      firstName,
      companyName,
      companyId,
    ) => {
      if (!email || sendingIntroEmail === email) return;
      setSendingIntroEmail(email);
      try {
        await apiService.sendIntroductionEmail({
          toEmail: email,
          firstName: firstName || "there",
          companyName: companyName,
          companyId: companyId,
          customerId: user.customerId,
        });
        onEmailSent?.(company.id, "intro");
        onToast(`Introduction email sent to ${email}`, "success");
      } catch (err) {
        console.error("Failed to send intro email:", err);
        onToast(`Failed to send to ${email}`, "error");
      } finally {
        setSendingIntroEmail(null);
      }
    };

    const handleSendBrief = async (e) => {
      e.preventDefault();
      if (!contactEmail || sendingBrief) return;
      setSendingBrief(true);
      try {
        await apiService.sendBriefEmail({
          toEmail: contactEmail,
          firstName: getFirstName(company.directors) || "there",
          companyName: company.companyName,
          companyId: company.id,
          customerId: user.customerId,
        });
        setBriefSent(true);
        onEmailSent?.(company.id, "brief");
        onToast("Brief form sent!", "success");
      } catch (err) {
        console.error("Failed to send brief email:", err);
        onToast("Failed to send brief form", "error");
      } finally {
        setSendingBrief(false);
      }
    };

    useEffect(() => {
      if (enrichData?.dudaStatus && enrichData.dudaStatus !== "none") {
        const qs = enrichData?.qaStatus;
        // Backend's /companies list returns the raw dudaStatus ("ready" as
        // soon as the build finishes), unlike /duda-status which holds it at
        // "processing" while QA is pending. Mirror that gating here so a
        // list refresh (e.g. after a brief-triggered build completes) can't
        // skip the "awaiting QA review" state straight to the ready CTAs.
        if (
          enrichData.dudaStatus === "ready" &&
          (qs === "pending_qa" || qs === "regeneration_requested")
        ) {
          setDudaState("processing");
        } else {
          setDudaState(enrichData.dudaStatus);
        }
      }
      if (enrichData?.qaStatus !== undefined) setQaState(enrichData.qaStatus);
    }, [enrichData]);

    // Resume watching this company if it's already mid-build or sitting in
    // QA when the drawer opens, covers reopening a drawer that was closed
    // before the build/approval finished, which previously left it stuck
    // showing whatever state was true at last company-list fetch.
    useEffect(() => {
      if (dudaState !== "processing" || !onResumeDudaPoll) return;
      const stop = onResumeDudaPoll(
        company.id,
        setDudaState,
        (freshPreviewUrl) => {
          if (freshPreviewUrl) {
            setEnrichData((prev) => ({
              ...(prev || {}),
              previewUrl: freshPreviewUrl,
              dudaStatus: "ready",
            }));
          }
        },
        setQaState,
      );
      return stop;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company.id, dudaState]);

    const contactPhone = isSoleTrader
      ? company.phone || company.credit?.telephone || enrichData?.phone
      : company.credit?.telephone || enrichData?.phone;
    const contactEmail = isSoleTrader
      ? company.email || enrichData?.email
      : enrichData?.email || company.email;
    const contactWebsite = isSoleTrader
      ? company.website || company.credit?.website || enrichData?.website
      : company.credit?.website || enrichData?.website;
    const phoneSource = resolveContactSource(
      "phone",
      contactPhone,
      enrichData,
      company.credit?.telephone,
    );
    const emailSource = resolveContactSource(
      "email",
      contactEmail,
      enrichData,
      null,
    );
    // Any filed-accounts figures at all - drives the drawer's financial panel
    // (shown without needing to run the opportunity score).
    const hasFinancialFigures =
      company.turnover != null ||
      company.currentAssets != null ||
      company.debtors != null ||
      company.cash != null ||
      company.netCurrentAssets != null ||
      (company.size != null && formatMoneyCompact(company.size));
    const contactSocials = {
      ...(enrichData?.socials || {}),
      linkedin: enrichData?.socials?.linkedin || enrichData?.linkedinUrl,
    };
    const hasSocials = Object.values(contactSocials).some(Boolean);
    const hasContact = !!(contactPhone || contactEmail);
    const isSiteReady =
      ["ready", "COMPLETED"].includes(dudaState) && enrichData?.previewUrl;
    const now = Date.now();
    const followUpReady = firstSentAt && now - firstSentAt >= THREE_DAYS_MS;
    const daysUntilFollowUp = firstSentAt
      ? Math.max(0, Math.ceil((THREE_DAYS_MS - (now - firstSentAt)) / 86400000))
      : null;

    const handleSave = () => {
      onUpdate(company.id, {
        status,
        notes,
        assigned_to: company.workflow?.assignedTo || null,
      });
      setSaved(true);
      onToast("Changes saved", "success");
      setTimeout(() => setSaved(false), 2000);
      // Reset dirty flag
      initialStatusRef.current = status;
      initialNotesRef.current = notes;
      setHasUnsaved(false);
    };

    const handleEnrich = () => {
      setEnrichState("running");
      onTriggerEnrich(company.id, setEnrichState, (data) => {
        setEnrichData(data);
        onToast(
          data ? "Enrichment complete" : "No data found",
          data ? "success" : "info",
        );
      });
    };

    const handleResync = () => {
      setResyncState("running");
      onResync(company.id, (nextState, freshData) => {
        setResyncState(nextState === "empty" ? "done" : nextState);
        if (freshData) {
          setEnrichData(freshData);
          onToast("Re-sync complete", "success");
        } else if (nextState === "empty")
          onToast("Re-sync complete, no new data found", "info");
        else if (nextState === "failed") onToast("Re-sync failed", "error");
      });
    };

    const handleSaveManualEmail = async () => {
      const trimmed = emailInput.trim();
      if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        setEmailSaveError("Please enter a valid email address.");
        return;
      }
      setEmailSaving(true);
      setEmailSaveError(null);
      try {
        await apiService.upsertEnrichmentOverride(company.id, {
          email: trimmed,
        });
        setEnrichData((prev) => ({
          ...(prev || {}),
          email: trimmed,
          contactSources: {
            ...(prev?.contactSources || {}),
            email: { source: "manual" },
          },
          isOverridden: true,
        }));
        setShowEmailInput(false);
        setEmailInput("");
        onToast("Email saved", "success");
      } catch (err) {
        setEmailSaveError(
          err.response?.data?.errors?.email ||
            err.response?.data?.message ||
            "Failed to save. Please try again.",
        );
      } finally {
        setEmailSaving(false);
      }
    };

    const buildEmailPayload = () => ({
      toEmail: contactEmail,
      firstName: getFirstName(company.directors),
      companyName: company.companyName,
      previewUrl: getWizzylPreviewUrl(enrichData?.previewUrl),
      companyId: company.id,
      customerId: user.customerId,
      unsubscribeEmail: contactEmail,
    });

    const handleSendPreview = async (e) => {
      e.preventDefault();
      if (!contactEmail) return;
      setSending(true);
      setSendError(null);
      try {
        await apiService.sendPreviewEmail(buildEmailPayload());
        if (!firstSentAt) {
          const ts = Date.now();
          localStorage.setItem(STORAGE_KEY(company.id), ts.toString());
          setFirstSentAt(ts);
        }
        setStatus("claimed");

        // Autosave: persist the status change immediately rather than
        // leaving it as a locally-dirty field waiting on manual "Save
        // Changes". Mirrors handleSave's onUpdate call. Reset both refs
        // (+ notes, unchanged here) so the dirty-check effect sees this
        // as already-saved and doesn't pop the "unsaved changes" modal
        // when the drawer is closed right after sending.
        onUpdate(company.id, {
          status: "claimed",
          notes,
          assigned_to: company.workflow?.assignedTo || null,
        });
        initialStatusRef.current = "claimed";
        initialNotesRef.current = notes;
        setHasUnsaved(false);

        setSentSuccess(true);
        onEmailSent?.(company.id, "preview");
        onToast("Pitch email sent!", "success");
      } catch {
        setSendError("Failed to send. Please try again.");
        onToast("Failed to send email", "error");
      } finally {
        setSending(false);
      }
    };

    const handleSendFollowUp = async (e) => {
      e.preventDefault();
      if (!contactEmail) return;
      setSending(true);
      setSendError(null);
      try {
        await apiService.sendFollowUpEmail(buildEmailPayload());
        setFollowUpSent(true);
        onEmailSent?.(company.id, "followup");
        onToast("Follow-up sent!", "success");
      } catch {
        setSendError("Failed to send. Please try again.");
        onToast("Failed to send follow-up", "error");
      } finally {
        setSending(false);
      }
    };

    // To edit phone or website, user must contact support to override via enrichment overrides API, which will flag the value as overridden and show a badge in the UI. Email can be edited manually by user in the drawer, since it's often the most critical for outreach.
    const [showPhoneInput, setShowPhoneInput] = useState(false);
    const [phoneInput, setPhoneInput] = useState("");
    const [phoneSaving, setPhoneSaving] = useState(false);
    const [phoneSaveError, setPhoneSaveError] = useState(null);

    const [showWebsiteInput, setShowWebsiteInput] = useState(false);
    const [websiteInput, setWebsiteInput] = useState("");
    const [websiteSaving, setWebsiteSaving] = useState(false);
    const [websiteSaveError, setWebsiteSaveError] = useState(null);

    // Get initial status and notes when drawer opens

    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [pendingCloseAction, setPendingCloseAction] = useState(null);
    const initialStatusRef = useRef(company.workflow?.status || "new");
    const initialNotesRef = useRef(company.workflow?.notes || "");
    const [hasUnsaved, setHasUnsaved] = useState(false);

    // Update dirty flag whenever status, notes, or any manual input is open/unsaved
    useEffect(() => {
      const isDirty =
        status !== initialStatusRef.current ||
        notes !== initialNotesRef.current ||
        showPhoneInput ||
        showWebsiteInput ||
        showEmailInput;
      setHasUnsaved(isDirty);
    }, [status, notes, showPhoneInput, showWebsiteInput, showEmailInput]);

    // Function to check before closing
    const checkAndClose = (closeFn) => {
      if (hasUnsaved) {
        setPendingCloseAction(() => closeFn);
        setShowUnsavedModal(true);
      } else {
        closeFn();
      }
    };

    // Expose canClose to parent
    useImperativeHandle(ref, () => ({
      canClose: () => {
        if (hasUnsaved) {
          setShowUnsavedModal(true);
          return false;
        }
        return true;
      },
      // Optional: reset after discard
      discardUnsaved: () => {
        setShowUnsavedModal(false);
        setPendingCloseAction(null);
        setHasUnsaved(false);
      },
    }));

    const confirmDiscard = () => {
      setShowUnsavedModal(false);
      if (pendingCloseAction) {
        pendingCloseAction();
        setPendingCloseAction(null);
      }
      setHasUnsaved(false);
    };

    // Save phone manually
    const handleSaveManualPhone = async () => {
      const trimmed = phoneInput.trim();
      if (!trimmed || trimmed.length < 5) {
        setPhoneSaveError("Please enter a valid phone number (min 5 chars).");
        return;
      }
      setPhoneSaving(true);
      setPhoneSaveError(null);
      try {
        await apiService.upsertEnrichmentOverride(company.id, {
          phone: trimmed,
        });
        setEnrichData((prev) => ({
          ...(prev || {}),
          phone: trimmed,
          contactSources: {
            ...(prev?.contactSources || {}),
            phone: { source: "manual" },
          },
          isOverridden: true,
        }));
        setShowPhoneInput(false);
        setPhoneInput("");
        onToast("Phone number saved", "success");
      } catch {
        setPhoneSaveError("Failed to save phone. Please try again.");
      } finally {
        setPhoneSaving(false);
      }
    };

    // Save website manually
    const handleSaveManualWebsite = async () => {
      let trimmed = websiteInput.trim();
      if (!trimmed) {
        setWebsiteSaveError("Please enter a website URL.");
        return;
      }
      if (!/^https?:\/\//i.test(trimmed)) {
        trimmed = "https://" + trimmed;
      }
      setWebsiteSaving(true);
      setWebsiteSaveError(null);
      try {
        await apiService.upsertEnrichmentOverride(company.id, {
          website: trimmed,
        });
        setEnrichData((prev) => ({
          ...(prev || {}),
          website: trimmed,
          isOverridden: true,
        }));
        setShowWebsiteInput(false);
        setWebsiteInput("");
        onToast("Website saved", "success");
      } catch {
        setWebsiteSaveError("Failed to save website. Please try again.");
      } finally {
        setWebsiteSaving(false);
      }
    };

    return (
      <>
        <div className="drawer-body">
          <ProfileCompletenessRing
            enrichData={
              isSoleTrader
                ? {
                    phone: contactPhone,
                    email: contactEmail,
                    website: contactWebsite,
                  }
                : enrichData
            }
            company={company}
          />

          <div className="drawer-card">
            <div className="drawer-section-title">
              Opportunity Score
              <InfoTooltip
                label="What is the opportunity score?"
                content="AXO's 0–100 score for how promising this company is, blending fit, buying signals, financial health and timing. A higher score means it's more worth pursuing."
              />
            </div>
            {!can(CAP.OPPORTUNITY_SCORE) ? (
              <UpgradeLock
                feature="Opportunity Score"
                planLabel={upgradeLabel(CAP.OPPORTUNITY_SCORE)}
                compact
              />
            ) : (
              <OpportunityScore
                companyId={company.id}
                companyNumber={company.companyNumber}
                canRefreshFinancials={can(CAP.FINANCIALS_REFRESH)}
                compact={false}
                serviceProfiles={
                  user?.customer?.features?.service_profiles?.length
                    ? user.customer.features.service_profiles
                    : user?.customer?.features?.service_profile
                      ? [user.customer.features.service_profile]
                      : []
                }
                cachedScore={scoreCache.current[company.id] ?? null}
                onScoreLoaded={(data) => {
                  scoreCache.current[company.id] = data;
                  try {
                    localStorage.setItem(
                      "axo_score_cache",
                      JSON.stringify(scoreCache.current),
                    );
                  } catch {
                    /* quota */
                  }
                }}
                websiteAvailable={!!contactWebsite}
              />
            )}
          </div>

          <div className="drawer-card">
            <div className="drawer-section-title">
              Publicly Available Contact Details
              <InfoTooltip
                label="About these contact details"
                content="AXO gathers contact details that are publicly available for this company, along with where each was found. We can't guarantee an email or phone belongs to a specific director, so verify before outreach."
              />
            </div>
            {/* <div className="drawer-contact-item">
            <Icon.Phone />
            <span>
              {contactPhone ? (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <a href={`tel:${contactPhone}`} className="contact-link">
                    {contactPhone}
                  </a>
                  {!isSoleTrader &&
                    company.credit?.telephone &&
                    !enrichData?.phone && (
                      <span
                        style={{
                          fontFamily: "var(--mono)",
                          fontSize: "9.5px",
                          letterSpacing: "0.05em",
                          padding: "1px 6px",
                          background: "var(--accent-dim)",
                          color: "var(--accent)",
                          border: "1px solid var(--accent)",
                          borderRadius: "100px",
                          textTransform: "uppercase",
                          fontWeight: 600,
                        }}
                      >
                        credit
                      </span>
                    )}
                </span>
              ) : (
                <span style={{ color: "var(--text-faint)" }}>Not found</span>
              )}
            </span>
          </div> */}
            {/* Phone row – add manual entry */}
            <div className="drawer-contact-item">
              <Icon.Phone />
              {contactPhone ? (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <a href={`tel:${contactPhone}`} className="contact-link">
                    {contactPhone}
                  </a>
                  {!isSoleTrader &&
                    company.credit?.telephone &&
                    !enrichData?.phone && (
                      <span className="tag-limited">credit</span>
                    )}
                  {phoneSource && (
                    <span
                      className="contact-source"
                      title={phoneSource.title || undefined}
                    >
                      {phoneSource.label}
                    </span>
                  )}
                </span>
              ) : showPhoneInput ? (
                <div style={{ flex: 1 }}>
                  <div className="add-email-row">
                    <input
                      className="add-email-input"
                      type="tel"
                      placeholder="+44 20 7946 0123"
                      value={phoneInput}
                      onChange={(e) => {
                        setPhoneInput(e.target.value);
                        setPhoneSaveError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveManualPhone();
                        if (e.key === "Escape") {
                          setShowPhoneInput(false);
                          setPhoneInput("");
                          setPhoneSaveError(null);
                        }
                      }}
                      autoFocus
                    />
                    <button
                      className="add-email-save"
                      onClick={handleSaveManualPhone}
                      disabled={phoneSaving}
                    >
                      {phoneSaving ? "Saving…" : "Save"}
                    </button>
                    <button
                      className="add-email-cancel"
                      onClick={() => {
                        setShowPhoneInput(false);
                        setPhoneInput("");
                        setPhoneSaveError(null);
                      }}
                    >
                      ×
                    </button>
                  </div>
                  {phoneSaveError && (
                    <div className="state-failed">{phoneSaveError}</div>
                  )}
                </div>
              ) : (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: "var(--text-faint)" }}>Not found</span>
                  {canEnrich && (
                    <button
                      className="add-email-toggle"
                      onClick={() => setShowPhoneInput(true)}
                    >
                      + Add phone
                    </button>
                  )}
                </span>
              )}
            </div>

            {/* Email row – add manual entry */}
            <div className="drawer-contact-item">
              <Icon.Mail />
              {contactEmail ? (
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <a
                      href={`mailto:${contactEmail}`}
                      className="drawer-contact-link"
                    >
                      {contactEmail}
                    </a>
                    {emailSource && (
                      <span
                        className="contact-source"
                        title={emailSource.title || undefined}
                      >
                        {emailSource.label}
                      </span>
                    )}
                    {contactEmail.startsWith("info@") &&
                      (() => {
                        const potEmails = getPotentialEmails(
                          company.directors,
                          company.companyName,
                        );
                        if (!potEmails.length) return null;
                        return (
                          <button
                            className="potential-emails-toggle"
                            onClick={() => setShowPotentialEmails((v) => !v)}
                          >
                            {showPotentialEmails
                              ? "▾ potential emails"
                              : "▸ potential emails"}
                          </button>
                        );
                      })()}
                  </div>

                  {showPotentialEmails &&
                    contactEmail.startsWith("info@") &&
                    (() => {
                      const potEmails = getPotentialEmails(
                        company.directors,
                        company.companyName,
                      );
                      if (!potEmails.length) return null;
                      const directorFirstName = getFirstName(company.directors);
                      return (
                        <div className="potential-emails-list">
                          {potEmails.map((em, i) => (
                            <div
                              key={i}
                              className="potential-email-item"
                              style={{
                                justifyContent: "space-between",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <a href={`mailto:${em}`} style={{ flex: 1 }}>
                                {em}
                              </a>
                              <button
                                className="btn-resync"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendIntroEmail(
                                    em,
                                    directorFirstName,
                                    company.companyName,
                                    company.id,
                                  );
                                }}
                                disabled={sendingIntroEmail === em}
                                style={{
                                  marginLeft: 8,
                                  padding: "4px 10px",
                                  fontSize: "11px",
                                }}
                              >
                                {sendingIntroEmail === em ? (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    <svg
                                      width="11"
                                      height="11"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2.5"
                                    >
                                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                    </svg>
                                    Sending...
                                  </span>
                                ) : (
                                  <>
                                    <svg
                                      width="11"
                                      height="11"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    >
                                      <path d="M22 2L11 13" />
                                      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                                    </svg>
                                    Send Intro
                                  </>
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                </div>
              ) : showEmailInput ? (
                <div style={{ flex: 1 }}>
                  <div className="add-email-row">
                    <input
                      className="add-email-input"
                      type="email"
                      placeholder="name@example.com"
                      value={emailInput}
                      onChange={(e) => {
                        setEmailInput(e.target.value);
                        setEmailSaveError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveManualEmail();
                        if (e.key === "Escape") {
                          setShowEmailInput(false);
                          setEmailInput("");
                          setEmailSaveError(null);
                        }
                      }}
                      autoFocus
                    />
                    <button
                      className="add-email-save"
                      onClick={handleSaveManualEmail}
                      disabled={emailSaving}
                    >
                      {emailSaving ? "Saving…" : "Save"}
                    </button>
                    <button
                      className="add-email-cancel"
                      onClick={() => {
                        setShowEmailInput(false);
                        setEmailInput("");
                        setEmailSaveError(null);
                      }}
                    >
                      ×
                    </button>
                  </div>
                  {emailSaveError && (
                    <div className="state-failed" style={{ marginTop: 4 }}>
                      {emailSaveError}
                    </div>
                  )}
                </div>
              ) : (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: "var(--text-faint)" }}>Not found</span>
                  {canEnrich && (
                    <button
                      className="add-email-toggle"
                      onClick={() => setShowEmailInput(true)}
                    >
                      + Add email
                    </button>
                  )}
                </span>
              )}
            </div>

            {/* Website row */}
            {/* <div className="drawer-contact-item">
            <Icon.Link />
            {contactWebsite ? (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <a
                  href={toExternalUrl(contactWebsite)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="drawer-contact-link"
                >
                  {(() => {
                    try {
                      return new URL(toExternalUrl(contactWebsite)).hostname.replace(
                        /^www\./,
                        "",
                      );
                    } catch (_) {
                      return contactWebsite;
                    }
                  })()}
                </a>
                {!isSoleTrader &&
                  company.credit?.website &&
                  !enrichData?.website && (
                    <span
                      style={{
                        fontFamily: "var(--mono)",
                        fontSize: "9.5px",
                        letterSpacing: "0.05em",
                        padding: "1px 6px",
                        background: "var(--accent-dim)",
                        color: "var(--accent)",
                        border: "1px solid var(--accent)",
                        borderRadius: "100px",
                        textTransform: "uppercase",
                        fontWeight: 600,
                      }}
                    >
                      credit
                    </span>
                  )}
              </span>
            ) : (
              <span style={{ color: "var(--text-faint)" }}>Not found</span>
            )}
          </div> */}
            {/* Website row – add manual entry */}
            <div className="drawer-contact-item">
              <Icon.Link />
              {contactWebsite ? (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <a
                    href={toExternalUrl(contactWebsite)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="drawer-contact-link"
                  >
                    {(() => {
                      try {
                        return new URL(
                          toExternalUrl(contactWebsite),
                        ).hostname.replace(/^www\./, "");
                      } catch {
                        return contactWebsite;
                      }
                    })()}
                  </a>
                  {!isSoleTrader &&
                    company.credit?.website &&
                    !enrichData?.website && (
                      <span className="tag-limited">credit</span>
                    )}
                </span>
              ) : showWebsiteInput ? (
                <div style={{ flex: 1 }}>
                  <div className="add-email-row">
                    <input
                      className="add-email-input"
                      type="text"
                      placeholder="example.com"
                      value={websiteInput}
                      onChange={(e) => {
                        setWebsiteInput(e.target.value);
                        setWebsiteSaveError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveManualWebsite();
                        if (e.key === "Escape") {
                          setShowWebsiteInput(false);
                          setWebsiteInput("");
                          setWebsiteSaveError(null);
                        }
                      }}
                      autoFocus
                    />
                    <button
                      className="add-email-save"
                      onClick={handleSaveManualWebsite}
                      disabled={websiteSaving}
                    >
                      {websiteSaving ? "Saving…" : "Save"}
                    </button>
                    <button
                      className="add-email-cancel"
                      onClick={() => {
                        setShowWebsiteInput(false);
                        setWebsiteInput("");
                        setWebsiteSaveError(null);
                      }}
                    >
                      ×
                    </button>
                  </div>
                  {websiteSaveError && (
                    <div className="state-failed">{websiteSaveError}</div>
                  )}
                </div>
              ) : (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: "var(--text-faint)" }}>Not found</span>
                  {canEnrich && (
                    <button
                      className="add-email-toggle"
                      onClick={() => setShowWebsiteInput(true)}
                    >
                      + Add website
                    </button>
                  )}
                </span>
              )}
            </div>

            {/* Social links row */}
            {hasSocials && (
              <div className="drawer-contact-item">
                <Icon.Link />
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  {contactSocials.facebook && (
                    <a
                      href={contactSocials.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="drawer-contact-link social-icon-link"
                      title="Facebook"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: "#1877F2",
                      }}
                    >
                      <Icon.Facebook color="#1877F2" />
                    </a>
                  )}
                  {contactSocials.instagram && (
                    <a
                      href={contactSocials.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="drawer-contact-link social-icon-link"
                      title="Instagram"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: "#E1306C",
                      }}
                    >
                      <Icon.Instagram color="#E1306C" />
                    </a>
                  )}
                  {contactSocials.linkedin && (
                    <a
                      href={contactSocials.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="drawer-contact-link social-icon-link"
                      title="LinkedIn"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: "#0A66C2",
                      }}
                    >
                      <Icon.LinkedIn color="#0A66C2" />
                    </a>
                  )}
                  {(contactSocials.twitter || contactSocials.x) && (
                    <a
                      href={contactSocials.twitter || contactSocials.x}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="drawer-contact-link social-icon-link"
                      title="X / Twitter"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: "#1DA1F2",
                      }}
                    >
                      <Icon.Twitter color="#1DA1F2" />
                    </a>
                  )}
                  {contactSocials.tiktok && (
                    <a
                      href={contactSocials.tiktok}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="drawer-contact-link social-icon-link"
                      title="TikTok"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: "#000000",
                      }}
                    >
                      <Icon.TikTok color="#000000" />
                    </a>
                  )}
                </span>
              </div>
            )}

            {/* Address row */}
            {!isSoleTrader && (
              <div className="drawer-contact-item">
                <Icon.Map />
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent((company.businessName || company.companyName) + " " + (company.postcode || ""))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="drawer-contact-link"
                >
                  {company.locality || "View address on Maps"}
                </a>
              </div>
            )}
            {!hasContact && !contactWebsite && !isSoleTrader && !canEnrich && (
              <div style={{ marginTop: 16 }}>
                <UpgradeLock
                  feature="Data enrichment"
                  planLabel={upgradeLabel(CAP.ENRICHMENT)}
                  compact
                />
              </div>
            )}
            {!hasContact && !contactWebsite && !isSoleTrader && canEnrich && (
              <div style={{ marginTop: 16 }}>
                {enrichState === "running" ? (
                  <span className="state-processing">
                    <Icon.Zap /> Enriching lead…
                  </span>
                ) : (
                  <button
                    className="btn btn-accent-ghost btn-sm"
                    onClick={handleEnrich}
                    style={{ width: "100%" }}
                  >
                    {enrichState === "failed"
                      ? "Retry Enrichment"
                      : "Trigger Data Enrichment"}
                  </button>
                )}
              </div>
            )}
            {alreadyEnriched && canEnrich && (
              <div
                style={{
                  marginTop: 16,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                {resyncState === "running" ? (
                  <span className="state-processing">
                    <Icon.Zap /> Syncing…
                  </span>
                ) : (
                  <button className="btn-resync" onClick={handleResync}>
                    ↻ Overwrite & Re-sync
                  </button>
                )}
              </div>
            )}
          </div>

          {!isSoleTrader && (
            <div className="drawer-card">
              <div className="drawer-section-title">
                Filed Accounts
                <InfoTooltip
                  label="Where do these figures come from?"
                  content="Taken from the company's most recent accounts filed at Companies House. Turnover is only present when the filing includes a profit-and-loss account (many small companies file a balance sheet only), so net assets is shown as a size indicator, since the balance sheet is filed far more often."
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {!hasFinancialFigures && (
                  <div style={{ fontSize: 12, color: "var(--text-faint)" }}>
                    No filed-accounts figures on file yet.
                  </div>
                )}
                {company.turnover != null && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "var(--text-faint)",
                      }}
                    >
                      {company.turnoverEstimated
                        ? `Turnover (est. · ${company.turnoverSource || "provider"})`
                        : "Turnover"}
                    </span>
                    <span
                      title={`£${Number(company.turnover).toLocaleString("en-GB")}`}
                      style={{
                        fontFamily: "var(--mono)",
                        fontSize: 20,
                        fontWeight: 600,
                        color: "var(--amber)",
                      }}
                    >
                      {formatMoneyCompact(company.turnover)}
                    </span>
                  </div>
                )}
                {company.size != null && formatMoneyCompact(company.size) && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "var(--text-faint)",
                      }}
                    >
                      {company.netAssets != null
                        ? "Net assets"
                        : "Total assets"}
                    </span>
                    <span
                      title={`£${Number(company.size).toLocaleString("en-GB")}`}
                      style={{
                        fontFamily: "var(--mono)",
                        fontSize: 20,
                        fontWeight: 600,
                        color: "var(--text)",
                      }}
                    >
                      {formatMoneyCompact(company.size)}
                    </span>
                  </div>
                )}
                {(() => {
                  const fmtSigned = (v) => {
                    if (v == null) return null;
                    const n = Number(v);
                    if (!Number.isFinite(n)) return null;
                    if (n === 0) return "£0";
                    const abs = formatMoneyCompact(Math.abs(n));
                    return abs ? `${n < 0 ? "-" : ""}${abs}` : null;
                  };
                  const ca = company.currentAssets;
                  const cl = company.creditorsDueWithinOneYear;
                  const ratio =
                    ca != null && cl != null && Math.abs(cl) > 0
                      ? ca / Math.abs(cl)
                      : null;
                  const extra = [
                    ratio != null && {
                      label: "Current ratio",
                      value: ratio.toFixed(2),
                    },
                    fmtSigned(company.netCurrentAssets) && {
                      label: "Net current assets",
                      value: fmtSigned(company.netCurrentAssets),
                    },
                    fmtSigned(company.debtors) && {
                      label: "Debtors",
                      value: fmtSigned(company.debtors),
                    },
                    fmtSigned(company.cash) && {
                      label: "Cash",
                      value: fmtSigned(company.cash),
                    },
                  ].filter(Boolean);
                  if (extra.length === 0) return null;
                  return (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px 12px",
                        borderTop: "1px solid var(--border)",
                        paddingTop: 8,
                      }}
                    >
                      {extra.map((item) => (
                        <div
                          key={item.label}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              color: "var(--text-faint)",
                            }}
                          >
                            {item.label}
                          </span>
                          <span
                            style={{
                              fontFamily: "var(--mono)",
                              fontSize: 13,
                              fontWeight: 600,
                              color: "var(--text)",
                            }}
                          >
                            {item.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {company.turnoverPeriodEnd && (
                  <span
                    style={{
                      color: "var(--text-faint)",
                      fontSize: 11,
                      textAlign: "right",
                    }}
                  >
                    to{" "}
                    {new Date(company.turnoverPeriodEnd).toLocaleDateString(
                      "en-GB",
                    )}
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleRefreshFinancials}
                  disabled={refreshingFin || !company.companyNumber}
                  title="Download the latest filed accounts from Companies House"
                  style={{
                    alignSelf: "flex-start",
                    background: "none",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    padding: "4px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--accent)",
                    cursor: refreshingFin ? "wait" : "pointer",
                    opacity: refreshingFin || !company.companyNumber ? 0.6 : 1,
                  }}
                >
                  {refreshingFin
                    ? "Fetching from Companies House…"
                    : "Refresh from Companies House"}
                </button>
                {financialsError && (
                  <div style={{ fontSize: 11, color: "var(--red)" }}>
                    {financialsError}
                  </div>
                )}
              </div>
            </div>
          )}

          {!isSoleTrader && (
            <div className="drawer-card">
              <div className="drawer-section-title">
                Growth Milestones
                <InfoTooltip
                  label="What are growth milestones?"
                  content="Changes detected across the company's filed accounts: verified turnover crossings (from a filed P&L), classification changes such as micro-entity to small company, and balance-sheet/headcount growth. Estimates are labelled and never presented as filed figures."
                />
              </div>
              {milestonesLoading ? (
                <span className="state-processing">
                  Checking filed accounts…
                </span>
              ) : milestones.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--text-faint)" }}>
                  {milestoneMeta.periods < 2
                    ? "Not enough filed-account periods on file yet. Milestones need at least two years to compare. Ingest older monthly files or use Refresh from Companies House."
                    : "No milestones detected yet. They appear when a new filing crosses a threshold or changes classification."}
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {milestones.slice(0, 6).map((m, i) => {
                    const tone =
                      m.category === "verified"
                        ? { color: "var(--green)", bg: "var(--green-dim)" }
                        : m.category === "estimated"
                          ? { color: "var(--amber)", bg: "var(--amber-dim)" }
                          : {
                              color: "var(--accent-mid)",
                              bg: "var(--accent-dim)",
                            };
                    return (
                      <div
                        key={`${m.type}-${m.periodEnd}-${i}`}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 3,
                          padding: "8px 10px",
                          border: "1px solid var(--border)",
                          borderRadius: 8,
                          background: "var(--surface-raised)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: "0.06em",
                              textTransform: "uppercase",
                              padding: "1px 6px",
                              borderRadius: 100,
                              color: tone.color,
                              background: tone.bg,
                            }}
                          >
                            {m.category === "verified"
                              ? "Verified"
                              : m.category === "estimated"
                                ? "Estimated"
                                : "Indicator"}
                          </span>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "var(--text)",
                            }}
                          >
                            {m.title}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-faint)",
                            lineHeight: 1.45,
                          }}
                        >
                          {m.detail}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!isSoleTrader && (
            <div className="drawer-card">
              <div className="drawer-section-title">
                Credit Intelligence
                <InfoTooltip
                  label="What is credit intelligence?"
                  content="Financial health signals from credit data, risk rating, credit limits and any adverse events, where available for this company."
                />
              </div>
              <CreditPanel credit={company.credit || {}} />
            </div>
          )}

          {dudaEnabled && (
            <div className="drawer-card">
              <div className="drawer-section-title">
                Wizzyl AI Automations
                <InfoTooltip
                  label="What are Wizzyl AI automations?"
                  content="Generate a custom website concept for this company and send the pitch or follow-up email, all from here."
                />
              </div>
              {!hasContact ? (
                <span
                  className="state-missing"
                  style={{ width: "100%", textAlign: "center" }}
                >
                  Need contact info to generate
                </span>
              ) : isSiteReady ? (
                <div className="drawer-site-actions">
                  <a
                    href={getWizzylPreviewUrl(enrichData.previewUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-preview-site"
                  >
                    Preview Generated Site
                  </a>
                  {contactEmail && (
                    <button
                      className={`btn-send-preview ${sentSuccess ? "btn-sent" : ""}`}
                      onClick={handleSendPreview}
                      disabled={sending || sentSuccess}
                    >
                      {sending
                        ? "Sending…"
                        : sentSuccess
                          ? "✓ Pitch Email Sent"
                          : "Send Pitch Email"}
                    </button>
                  )}
                  {firstSentAt &&
                    contactEmail &&
                    (followUpReady ? (
                      <button
                        className={`btn-followup ${followUpSent ? "btn-sent" : ""}`}
                        onClick={handleSendFollowUp}
                        disabled={sending || followUpSent}
                      >
                        {sending
                          ? "Sending…"
                          : followUpSent
                            ? "✓ Follow-up Sent"
                            : "Send Follow-up Email"}
                      </button>
                    ) : (
                      <div className="followup-pending">
                        Follow-up auto-releases in {daysUntilFollowUp}d
                      </div>
                    ))}
                  {sendError && <div className="state-failed">{sendError}</div>}
                </div>
              ) : dudaState === "processing" &&
                (qaState === "pending_qa" ||
                  qaState === "regeneration_requested") ? (
                <span className="state-processing">
                  {qaState === "regeneration_requested"
                    ? "Rebuilding after QA feedback…"
                    : "Site generated, awaiting QA review"}
                </span>
              ) : dudaState === "processing" ? (
                <span className="state-processing">
                  Orchestrating AI deployment…
                </span>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {contactEmail && (
                    <button
                      className={`btn-send-preview ${briefSent ? "btn-sent" : ""}`}
                      onClick={handleSendBrief}
                      disabled={sendingBrief || briefSent}
                      style={{ width: "100%" }}
                    >
                      {sendingBrief
                        ? "Sending…"
                        : briefSent
                          ? "✓ Brief Form Sent"
                          : "Send Brief Form"}
                    </button>
                  )}
                  <button
                    className="btn btn-primary btn-md"
                    disabled={briefSent && dudaState !== "failed"}
                    onClick={() =>
                      onTriggerDuda(
                        company.id,
                        setDudaState,
                        (freshPreviewUrl) => {
                          if (freshPreviewUrl) {
                            setEnrichData((prev) => ({
                              ...(prev || {}),
                              previewUrl: freshPreviewUrl,
                              dudaStatus: "ready",
                            }));
                          }
                        },
                        setQaState,
                        true, // skipQA
                      )
                    }
                    style={{ width: "100%" }}
                  >
                    {dudaState === "failed"
                      ? "Retry AI deployment"
                      : briefSent
                        ? "Brief already sent"
                        : contactEmail
                          ? "Or Generate Site Now (skip brief)"
                          : "Generate Custom Site"}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="drawer-card">
            <div className="drawer-section-title">
              Internal Categorisation
              <InfoTooltip
                label="What is internal categorisation?"
                content="Internal pipeline stage and notes used to organise this lead. Only your team sees this, it isn't shared with the company."
              />
            </div>
            <div className="field-group">
              <span className="field-label">Current Pipeline Stage</span>
              <Select
                label="Current Pipeline Stage"
                hideLabel
                className="row-select"
                value={status}
                onValueChange={setStatus}
                options={CATEGORISATION_STATUS_OPTIONS.map((s) => ({
                  value: s.value,
                  label: s.label,
                }))}
              />
            </div>
            <div className="field-group" style={{ marginTop: 14 }}>
              <span className="field-label">Workspace Scribble / Notes</span>
              <Textarea
                label="Workspace Scribble / Notes"
                hideLabel
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="row-textarea"
                placeholder="Spoke to director, call back in 10 minutes..."
              />
            </div>
          </div>
        </div>
        <div className="drawer-footer">
          <button
            className="btn btn-secondary btn-md"
            onClick={() => checkAndClose(onClose)}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={`btn btn-primary btn-md ${saved ? "saved" : ""}`}
            style={{ minWidth: 100 }}
          >
            {saved ? "Saved ✓" : "Save Changes"}
          </button>
        </div>
        {showUnsavedModal && (
          <Dialog
            open
            onOpenChange={(next) => {
              if (!next) setShowUnsavedModal(false);
            }}
          >
            <DialogContent title="Discard unsaved changes?">
              <p
                style={{
                  fontSize: "13px",
                  color: "var(--text-muted)",
                  lineHeight: "1.55",
                }}
              >
                You have unsaved edits to this record. Discarding will
                permanently remove them.
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                  marginTop: "16px",
                }}
              >
                <button
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowUnsavedModal(false)}
                >
                  Keep editing
                </button>
                <button
                  className="btn btn-md"
                  onClick={confirmDiscard}
                  style={{
                    background: "var(--red)",
                    color: "#fff",
                    border: "none",
                  }}
                >
                  Discard
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  },
);

// ── DASHBOARD ──────────────────────────────────────────────────────────────────
// ── ACCOUNT SETTINGS MODAL ─────────────────────────────────────────────────────
// Tabs: Profile · Company & channels · Preferences · Billing.
// Cancel Subscription lives at the bottom of Billing as a quiet text link
// (it still opens the existing confirm modal, nothing is hidden or removed).
const CHANNEL_FIELDS = [
  { key: "website", label: "Website URL", placeholder: "yourcompany.com" },
  {
    key: "linkedinCompany",
    label: "LinkedIn company page",
    placeholder: "linkedin.com/company/…",
  },
  { key: "twitter", label: "X / Twitter", placeholder: "x.com/…" },
  { key: "facebook", label: "Facebook", placeholder: "facebook.com/…" },
  { key: "instagram", label: "Instagram", placeholder: "instagram.com/…" },
  { key: "youtube", label: "YouTube", placeholder: "youtube.com/@…" },
];

const SERVICE_PRODUCT_OPTIONS = [
  { value: "", label: "No specific AXO product" },
  { value: "wizzyl", label: "Wizzyl (Website)" },
  { value: "axo_signal", label: "AXO Signal (Growth)" },
  { value: "6ela", label: "6ELA (Finance)" },
  { value: "telco", label: "Telco (Comms)" },
];

const AVATAR_PASTELS = ["#fb923c", "#ec4899", "#8b5cf6", "#34d399"];
const avatarFor = (name = "") => {
  const initials =
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";
  const hue =
    [...name].reduce((n, c) => n + c.charCodeAt(0), 0) % AVATAR_PASTELS.length;
  return { initials, bg: AVATAR_PASTELS[hue] };
};

export const AccountSettingsModal = ({
  user,
  initialTab,
  darkMode,
  setDarkMode,
  density,
  setDensity,
  onClose,
  onRequestCancel,
}) => {
  const [tab, setTab] = useState(initialTab || "profile");
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState(null); // null = not loaded
  const [connectionsError, setConnectionsError] = useState(false);
  const [connPanel, setConnPanel] = useState(null); // open service key
  const [connConfirm, setConnConfirm] = useState(null); // service awaiting disconnect confirm
  const [connBusy, setConnBusy] = useState(false);
  const [connMsg, setConnMsg] = useState(null); // { type: 'ok'|'err', text }
  const [liDraft, setLiDraft] = useState({}); // unsaved LinkedIn inputs
  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState(null); // plaintext, shown once
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'ok'|'err', text }
  const [pwStatus, setPwStatus] = useState("idle"); // idle | sending | sent | error
  const [errors, setErrors] = useState({});
  const [canEditCompany, setCanEditCompany] = useState(false);
  const [form, setForm] = useState({ user: {}, company: {} });
  const [saved, setSaved] = useState({ user: {}, company: {} });
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState(null);
  const { can: canSalesPlan } = usePlanAccess();

  useEffect(() => {
    if (tab !== "billing") return undefined;
    let cancelled = false;
    setBillingLoading(true);
    setBillingError(null);
    apiService
      .getSubscriptionStatus()
      .then((res) => {
        if (!cancelled) setBilling(res.data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setBillingError(
            err.response?.data?.message || "Couldn't load billing details",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setBillingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiService.getProfile();
        if (!alive) return;
        // apiService's response interceptor already unwraps to the body.
        const next = { user: res.user || {}, company: res.company || {} };
        setForm(next);
        setSaved(next);
        setCanEditCompany(!!res.canEditCompany);
      } catch (e) {
        if (alive)
          setStatus({ type: "err", text: "Couldn't load your profile." });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Connections load lazily the first time the tab is opened.
  useEffect(() => {
    if (tab !== "connections" || connections !== null) return;
    let alive = true;
    (async () => {
      try {
        const res = await apiService.getConnections();
        if (alive) {
          setConnections(res.data || []);
          setConnectionsError(false);
        }
      } catch (e) {
        if (alive) setConnectionsError(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [tab, connections]);

  // ── Connections: connect / disconnect ────────────────────────────────
  const reloadConnections = () => setConnections(null);

  const connFail = (e, fallback, errKey) =>
    setConnMsg({
      type: "err",
      text:
        (errKey && e?.response?.data?.errors?.[errKey]) ||
        e?.response?.data?.message ||
        fallback,
    });

  // Save (connect) or clear (disconnect) LinkedIn links. `patch` is
  // { personal?: string, company?: string }; "" clears. Only the keys that
  // changed are mirrored into the Profile tab's state, so unsaved edits there
  // aren't overwritten.
  const updateLinkedin = async (patch, okText) => {
    setConnBusy(true);
    setConnMsg(null);
    try {
      const payload = {};
      if ("personal" in patch)
        payload.user = { linkedinPersonal: patch.personal };
      if ("company" in patch)
        payload.company = { linkedinCompany: patch.company };
      const res = await apiService.updateProfile(payload);
      const sync = (state) => {
        const next = {
          ...state,
          user: { ...state.user },
          company: { ...state.company },
        };
        if ("personal" in patch)
          next.user.linkedinPersonal = res.user?.linkedinPersonal || "";
        if ("company" in patch)
          next.company.linkedinCompany = res.company?.linkedinCompany || "";
        return next;
      };
      setForm(sync);
      setSaved(sync);
      setLiDraft({});
      setConnConfirm(null);
      setConnMsg({ type: "ok", text: okText });
      reloadConnections();
    } catch (e) {
      connFail(
        e,
        "Couldn't update LinkedIn.",
        "personal" in patch ? "linkedinPersonal" : "linkedinCompany",
      );
    } finally {
      setConnBusy(false);
    }
  };

  const disconnectLinkedin = (c) => {
    const patch = {};
    if (c.links?.personal) patch.personal = "";
    if (c.links?.company && c.canEditCompany) patch.company = "";
    return updateLinkedin(patch, "LinkedIn disconnected");
  };

  const createKey = async () => {
    setConnBusy(true);
    setConnMsg(null);
    try {
      const res = await apiService.createApiKey({
        name: newKeyName.trim() || "API key",
      });
      setCreatedKey(res.data?.key || null);
      setNewKeyName("");
      reloadConnections();
    } catch (e) {
      connFail(e, "Couldn't create the API key.");
    } finally {
      setConnBusy(false);
    }
  };

  const revokeKeys = async (ids, okText) => {
    setConnBusy(true);
    setConnMsg(null);
    try {
      for (const id of ids) await apiService.revokeApiKey(id);
      setConnConfirm(null);
      setConnMsg({ type: "ok", text: okText });
    } catch (e) {
      connFail(e, "Couldn't revoke the API key.");
    } finally {
      reloadConnections();
      setConnBusy(false);
    }
  };

  const togglePanel = (key) => {
    setConnPanel((p) => (p === key ? null : key));
    setConnConfirm(null);
    setConnMsg(null);
    setLiDraft({});
    setCreatedKey(null);
  };

  const set = (group, key, value) => {
    setForm((f) => ({ ...f, [group]: { ...f[group], [key]: value } }));
    setErrors((e) => ({ ...e, [key]: undefined }));
    setStatus(null);
  };

  const setServiceProfiles = (updater) => {
    setForm((f) => {
      const current = Array.isArray(f.company.serviceProfiles)
        ? f.company.serviceProfiles
        : [];
      return {
        ...f,
        company: {
          ...f.company,
          serviceProfiles:
            typeof updater === "function" ? updater(current) : updater,
        },
      };
    });
    setStatus(null);
  };

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  const save = async () => {
    setSaving(true);
    setStatus(null);
    setErrors({});
    try {
      const payload = {
        user: {
          name: form.user.name,
          jobTitle: form.user.jobTitle || "",
          linkedinPersonal: form.user.linkedinPersonal || "",
        },
      };
      if (canEditCompany) {
        payload.company = {
          industry: form.company.industry || "",
          description: form.company.description || "",
          serviceProfiles: form.company.serviceProfiles || [],
        };
        CHANNEL_FIELDS.forEach((c) => {
          payload.company[c.key] = form.company[c.key] || "";
        });
      }
      const res = await apiService.updateProfile(payload);
      const next = { user: res.user, company: res.company };
      setForm(next);
      setSaved(next);
      setStatus({ type: "ok", text: "Saved" });
      // Refresh the auth user so the drawer's Opportunity Score and the Sales
      // Workspace see the new service memo immediately.
      window.dispatchEvent(new CustomEvent("axo:profile-changed"));
    } catch (e) {
      setErrors(e?.response?.data?.errors || {});
      setStatus({
        type: "err",
        text: e?.response?.data?.message || "Couldn't save changes.",
      });
    } finally {
      setSaving(false);
    }
  };

  const sendPasswordReset = async () => {
    if (!form.user.email || pwStatus === "sending" || pwStatus === "sent")
      return;
    setPwStatus("sending");
    try {
      await apiService.forgotPassword(form.user.email);
      setPwStatus("sent");
    } catch (e) {
      setPwStatus("error");
    }
  };

  const field = (group, key, label, opts = {}) => (
    <div
      className={`acct-field${opts.full ? " full" : ""}${errors[key] ? " invalid" : ""}`}
    >
      <label htmlFor={`acct-${key}`}>{label}</label>
      {opts.textarea ? (
        <Textarea
          label={label}
          hideLabel
          id={`acct-${key}`}
          rows={3}
          maxLength={600}
          value={form[group][key] || ""}
          placeholder={opts.placeholder}
          disabled={opts.disabled}
          onChange={(e) => set(group, key, e.target.value)}
        />
      ) : (
        <Input
          label={label}
          hideLabel
          id={`acct-${key}`}
          type="text"
          value={form[group][key] || ""}
          placeholder={opts.placeholder}
          disabled={opts.disabled}
          onChange={(e) => set(group, key, e.target.value)}
        />
      )}
      {errors[key] && <span className="acct-err">{errors[key]}</span>}
    </div>
  );

  const TABS = [
    ["profile", "Profile"],
    ["channels", "Company & channels"],
    ["connections", "Connections"],
    ["prefs", "Preferences"],
    ["billing", "Billing"],
  ];
  const showSave = tab === "profile" || tab === "channels";
  const av = avatarFor(form.user.name || user?.name || "");
  const planName = user?.customer?.planId
    ? PLAN_LABELS[normalisePlanId(user.customer.planId)] || user.customer.planId
    : null;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="acct-dialog" title="Account settings">
        <Tabs value={tab} onValueChange={setTab} className="acct-tabs-root">
          <div className="acct-tabs-wrap">
            <TabsList aria-label="Account settings">
              {TABS.map(([id, label]) => (
                <TabsTrigger key={id} value={id}>
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <div className="acct-body">
            <TabsContent value={tab} className="acct-tab-panel">
              {loading && (tab === "profile" || tab === "channels") ? (
                <div className="acct-section-sub">Loading…</div>
              ) : (
                <>
                  {tab === "profile" && (
                    <>
                      <div className="acct-card">
                        <div
                          className="acct-identity"
                          style={{ marginBottom: 0 }}
                        >
                          <span
                            className="acct-avatar acct-avatar-lg"
                            style={{ background: av.bg }}
                          >
                            {av.initials}
                          </span>
                          <div>
                            <div className="acct-identity-name">
                              {form.user.name || "Your profile"}
                            </div>
                            <div className="acct-identity-sub">
                              {[form.user.jobTitle, user?.role]
                                .filter(Boolean)
                                .join(" · ") || form.user.email}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="acct-card">
                        <div className="acct-card-title">Personal details</div>
                        <div className="acct-card-sub">
                          These details appear on outreach and shared briefs.
                        </div>
                        <div className="acct-grid">
                          {field("user", "name", "Full name")}
                          {field("user", "jobTitle", "Job title", {
                            placeholder: "e.g. Head of Sales",
                          })}
                          <div className="acct-field full">
                            <label htmlFor="acct-email">Email</label>
                            <Input
                              label="Email"
                              hideLabel
                              id="acct-email"
                              type="text"
                              value={form.user.email || ""}
                              disabled
                            />
                          </div>
                          {field(
                            "user",
                            "linkedinPersonal",
                            "Your LinkedIn profile",
                            {
                              full: true,
                              placeholder: "linkedin.com/in/…",
                            },
                          )}
                        </div>
                      </div>

                      <div className="acct-card">
                        <div className="acct-card-title">Security</div>
                        <div className="acct-card-sub">
                          Passwords are managed securely. Send yourself a reset
                          link to choose a new one.
                        </div>
                        <div className="acct-pref">
                          <div>
                            <div className="acct-pref-label">Password</div>
                            <div className="acct-pref-sub">
                              {pwStatus === "sent"
                                ? `Reset link sent to ${form.user.email}`
                                : `A reset link will be sent to ${form.user.email}`}
                            </div>
                          </div>
                          <button
                            className="acct-btn acct-btn-secondary"
                            onClick={sendPasswordReset}
                            disabled={
                              !form.user.email ||
                              pwStatus === "sending" ||
                              pwStatus === "sent"
                            }
                          >
                            {pwStatus === "sending"
                              ? "Sending…"
                              : pwStatus === "sent"
                                ? "Reset link sent"
                                : "Reset password"}
                          </button>
                        </div>
                        {pwStatus === "error" && (
                          <div className="acct-err">
                            Couldn't send a reset link. Please try again.
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {tab === "channels" && (
                    <>
                      <div className="acct-card">
                        <div className="acct-card-title">Company details</div>
                        <div className="acct-card-sub">
                          Used to keep your own company data consistent across
                          the workspace.
                        </div>
                        {!canEditCompany && (
                          <div className="acct-note">
                            Only admins can edit company details.
                          </div>
                        )}
                        <div className="acct-grid">
                          {field("company", "name", "Company name", {
                            full: true,
                            disabled: true,
                          })}
                          {field("company", "industry", "Industry", {
                            disabled: !canEditCompany,
                            placeholder: "e.g. Commercial cleaning",
                          })}
                          {field(
                            "company",
                            "description",
                            "What does your company do?",
                            {
                              full: true,
                              textarea: true,
                              disabled: !canEditCompany,
                              placeholder: "A sentence or two is plenty.",
                            },
                          )}
                        </div>
                      </div>

                      <div className="acct-card">
                        <div className="acct-card-title">Service memo</div>
                        <div className="acct-card-sub">
                          What you sell and its traits. AXO uses this to tailor
                          briefings, company research and outreach drafts.
                        </div>
                        {!canEditCompany && (
                          <div className="acct-note">
                            Only admins can edit the service memo.
                          </div>
                        )}
                        <div className="flex flex-col gap-3">
                          {(form.company.serviceProfiles || []).map(
                            (svc, index) => (
                              <div
                                key={index}
                                className="rounded-xl p-4 flex flex-col gap-3"
                                style={{ border: "1px solid var(--border)" }}
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <span className="text-xs font-semibold">
                                    Service {index + 1}
                                  </span>
                                  {canEditCompany &&
                                    (form.company.serviceProfiles || [])
                                      .length > 1 && (
                                      <button
                                        type="button"
                                        className="acct-quiet-link"
                                        onClick={() =>
                                          setServiceProfiles((list) =>
                                            list.filter((_, i) => i !== index),
                                          )
                                        }
                                      >
                                        Remove
                                      </button>
                                    )}
                                </div>

                                <div className="acct-field full">
                                  <label htmlFor={`acct-service-name-${index}`}>
                                    Service name
                                  </label>
                                  <Input
                                    label="Service name"
                                    hideLabel
                                    id={`acct-service-name-${index}`}
                                    type="text"
                                    maxLength={120}
                                    value={svc.name || ""}
                                    placeholder="e.g. Commercial cleaning"
                                    disabled={!canEditCompany}
                                    onChange={(e) =>
                                      setServiceProfiles((list) =>
                                        list.map((s, i) =>
                                          i === index
                                            ? { ...s, name: e.target.value }
                                            : s,
                                        ),
                                      )
                                    }
                                  />
                                </div>

                                <div className="acct-field full">
                                  <label
                                    htmlFor={`acct-service-description-${index}`}
                                  >
                                    What does it include?
                                  </label>
                                  <Textarea
                                    label="What does it include?"
                                    hideLabel
                                    id={`acct-service-description-${index}`}
                                    rows={3}
                                    maxLength={2000}
                                    value={svc.description || ""}
                                    placeholder="Who you help, what you deliver, and what makes it different."
                                    disabled={!canEditCompany}
                                    onChange={(e) =>
                                      setServiceProfiles((list) =>
                                        list.map((s, i) =>
                                          i === index
                                            ? {
                                                ...s,
                                                description: e.target.value,
                                              }
                                            : s,
                                        ),
                                      )
                                    }
                                  />
                                </div>

                                <div className="acct-field full">
                                  <label
                                    htmlFor={`acct-service-traits-${index}`}
                                  >
                                    Traits
                                  </label>
                                  <Input
                                    label="Traits"
                                    hideLabel
                                    id={`acct-service-traits-${index}`}
                                    type="text"
                                    value={(svc.traits || []).join(", ")}
                                    placeholder="e.g. fixed pricing, 24-hour turnaround, local team"
                                    disabled={!canEditCompany}
                                    onChange={(e) =>
                                      setServiceProfiles((list) =>
                                        list.map((s, i) =>
                                          i === index
                                            ? {
                                                ...s,
                                                traits: e.target.value
                                                  .split(",")
                                                  .map((t) => t.trim())
                                                  .filter(Boolean),
                                              }
                                            : s,
                                        ),
                                      )
                                    }
                                  />
                                  <span
                                    className="acct-card-sub"
                                    style={{
                                      display: "block",
                                      margin: "4px 0 0",
                                    }}
                                  >
                                    Separate traits with commas.
                                  </span>
                                </div>

                                <div className="acct-grid">
                                  <div className="acct-field">
                                    <label
                                      htmlFor={`acct-service-price-${index}`}
                                    >
                                      Typical deal value (£)
                                    </label>
                                    <Input
                                      label="Typical deal value"
                                      hideLabel
                                      id={`acct-service-price-${index}`}
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={svc.priceGbp ?? ""}
                                      placeholder="e.g. 2500"
                                      disabled={!canEditCompany}
                                      onChange={(e) =>
                                        setServiceProfiles((list) =>
                                          list.map((s, i) =>
                                            i === index
                                              ? {
                                                  ...s,
                                                  priceGbp: e.target.value,
                                                }
                                              : s,
                                          ),
                                        )
                                      }
                                    />
                                    <span
                                      className="acct-card-sub"
                                      style={{
                                        display: "block",
                                        margin: "4px 0 0",
                                      }}
                                    >
                                      Used to estimate pipeline value in Phase
                                      5.
                                    </span>
                                  </div>
                                  <div className="acct-field">
                                    <label>AXO mapping</label>
                                    <div
                                      style={{
                                        padding: "8px 0",
                                        fontSize: 13,
                                        color: "var(--text-muted)",
                                      }}
                                    >
                                      {svc.productKey
                                        ? `Mapped to ${
                                            SERVICE_PRODUCT_OPTIONS.find(
                                              (o) => o.value === svc.productKey,
                                            )?.label || svc.productKey
                                          }`
                                        : "Awaiting AXO mapping"}
                                    </div>
                                    <span
                                      className="acct-card-sub"
                                      style={{
                                        display: "block",
                                        margin: "4px 0 0",
                                      }}
                                    >
                                      AXO maps each service to a product during
                                      onboarding. You can still update the
                                      details and deal value.
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}

                          {canEditCompany && (
                            <button
                              type="button"
                              className="acct-quiet-link"
                              onClick={() =>
                                setServiceProfiles((list) => [
                                  ...list,
                                  {
                                    name: "",
                                    description: "",
                                    traits: [],
                                    productKey: "",
                                    priceGbp: "",
                                  },
                                ])
                              }
                            >
                              + Add another service
                            </button>
                          )}
                          {!(form.company.serviceProfiles || []).length &&
                            !canEditCompany && (
                              <div className="acct-section-sub">
                                No services added yet.
                              </div>
                            )}
                        </div>
                      </div>

                      <div className="acct-card">
                        <div className="acct-card-title">
                          Connected channels
                        </div>
                        <div className="acct-card-sub">
                          Public channels AXO uses to understand your brand and
                          check outreach context.
                        </div>
                        <div className="acct-grid">
                          {CHANNEL_FIELDS.map((c) =>
                            field("company", c.key, c.label, {
                              placeholder: c.placeholder,
                              disabled: !canEditCompany,
                              full: c.key === "website",
                            }),
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {tab === "connections" && (
                    <div className="acct-card">
                      <div className="acct-card-title">Connected apps</div>
                      <div className="acct-card-sub">
                        Sync company, contact and pipeline data with the tools
                        your team already uses. API access is managed here too.
                      </div>
                      {connectionsError ? (
                        <div className="acct-note">
                          Couldn't load your connections.{" "}
                          <button
                            className="acct-quiet-link"
                            onClick={() => {
                              setConnectionsError(false);
                              setConnections(null);
                            }}
                          >
                            Try again
                          </button>
                        </div>
                      ) : connections === null ? (
                        <div className="acct-section-sub">Loading…</div>
                      ) : (
                        <>
                          {connMsg && (
                            <div className={`acct-conn-msg ${connMsg.type}`}>
                              {connMsg.text}
                            </div>
                          )}
                          <div className="acct-conn-grid">
                            {connections.map((c) => {
                              const connected = c.status === "connected";
                              const canAct = c.interactive && c.canManage;
                              return (
                                <React.Fragment key={c.key}>
                                  <div className="acct-conn-item">
                                    <div className="acct-conn">
                                      <span
                                        className="acct-conn-icon"
                                        aria-hidden="true"
                                      >
                                        {c.key === "linkedin" ? (
                                          <Icon.LinkedIn color="#0A66C2" />
                                        ) : (
                                          c.name.charAt(0)
                                        )}
                                      </span>
                                      <div className="acct-conn-main">
                                        <div className="acct-conn-name">
                                          {c.name}
                                        </div>
                                        <div className="acct-conn-desc">
                                          {c.description}
                                        </div>
                                        <div className="acct-conn-detail">
                                          {c.detail}
                                          {c.managedBy === "axo"
                                            ? " · Managed by AXO"
                                            : ""}
                                        </div>
                                      </div>
                                      <div className="acct-conn-side">
                                        <span
                                          className={`acct-conn-status ${c.status}`}
                                        >
                                          <span className="acct-conn-dot" />
                                          {connected
                                            ? "Connected"
                                            : c.status === "not_connected"
                                              ? "Not connected"
                                              : "Not enabled"}
                                        </span>
                                        {c.interactive &&
                                        connConfirm === c.key ? (
                                          <>
                                            <span className="acct-conn-confirm">
                                              {c.key === "api_access"
                                                ? `Revoke ${c.keys.length} key${c.keys.length === 1 ? "" : "s"}?`
                                                : "Remove saved link(s)?"}
                                            </span>
                                            <button
                                              className="btn btn-secondary btn-md"
                                              disabled={connBusy}
                                              onClick={() =>
                                                setConnConfirm(null)
                                              }
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              className="btn btn-md"
                                              disabled={connBusy}
                                              style={{
                                                background: "var(--red)",
                                                color: "#fff",
                                                border: "none",
                                              }}
                                              onClick={() =>
                                                c.key === "api_access"
                                                  ? revokeKeys(
                                                      c.keys.map((k) => k.id),
                                                      "API access disconnected",
                                                    )
                                                  : disconnectLinkedin(c)
                                              }
                                            >
                                              {connBusy
                                                ? "Working…"
                                                : "Disconnect"}
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            {c.key !== "linkedin" &&
                                              c.interactive &&
                                              (connected || canAct) && (
                                                <button
                                                  className="btn btn-secondary btn-md"
                                                  onClick={() =>
                                                    togglePanel(c.key)
                                                  }
                                                >
                                                  {connPanel === c.key
                                                    ? "Close"
                                                    : connected
                                                      ? "Manage"
                                                      : "Connect"}
                                                </button>
                                              )}
                                            {c.interactive &&
                                              connected &&
                                              canAct && (
                                                <button
                                                  className="btn btn-secondary btn-md"
                                                  onClick={() => {
                                                    setConnMsg(null);
                                                    setConnConfirm(c.key);
                                                  }}
                                                >
                                                  Disconnect
                                                </button>
                                              )}
                                            {c.action?.tab && (
                                              <button
                                                className="btn btn-secondary btn-md"
                                                onClick={() =>
                                                  setTab(c.action.tab)
                                                }
                                              >
                                                {c.action.label}
                                              </button>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </div>

                                    {connPanel === c.key &&
                                      c.key === "linkedin" && (
                                        <div className="acct-conn-panel">
                                          {[
                                            [
                                              "personal",
                                              "Your LinkedIn profile",
                                              "linkedin.com/in/…",
                                            ],
                                            [
                                              "company",
                                              "Company LinkedIn page",
                                              "linkedin.com/company/…",
                                            ],
                                          ].map(([f, label, ph]) => {
                                            const savedVal = c.links?.[f] || "";
                                            const draft =
                                              liDraft[f] ?? savedVal;
                                            const locked =
                                              f === "company" &&
                                              !c.canEditCompany;
                                            return (
                                              <div
                                                className="acct-field"
                                                key={f}
                                              >
                                                <label htmlFor={`conn-li-${f}`}>
                                                  {label}
                                                </label>
                                                <div className="acct-conn-inline">
                                                  <input
                                                    id={`conn-li-${f}`}
                                                    type="text"
                                                    value={draft}
                                                    placeholder={ph}
                                                    disabled={
                                                      locked || connBusy
                                                    }
                                                    onChange={(e) =>
                                                      setLiDraft((d) => ({
                                                        ...d,
                                                        [f]: e.target.value,
                                                      }))
                                                    }
                                                  />
                                                  <button
                                                    className="btn btn-primary btn-md"
                                                    disabled={
                                                      locked ||
                                                      connBusy ||
                                                      !draft.trim() ||
                                                      draft.trim() === savedVal
                                                    }
                                                    onClick={() =>
                                                      updateLinkedin(
                                                        { [f]: draft.trim() },
                                                        "LinkedIn link saved",
                                                      )
                                                    }
                                                  >
                                                    Save
                                                  </button>
                                                  {savedVal && !locked && (
                                                    <button
                                                      className="btn btn-secondary btn-md"
                                                      disabled={connBusy}
                                                      onClick={() =>
                                                        updateLinkedin(
                                                          { [f]: "" },
                                                          "LinkedIn link removed",
                                                        )
                                                      }
                                                    >
                                                      Remove
                                                    </button>
                                                  )}
                                                </div>
                                                {locked && (
                                                  <span className="acct-conn-detail">
                                                    Only admins can edit the
                                                    company page.
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })}
                                          <div className="acct-conn-detail">
                                            These links are used by “Copy & open
                                            LinkedIn”. AXO never sees your
                                            LinkedIn login.
                                          </div>
                                        </div>
                                      )}

                                    {connPanel === c.key &&
                                      c.key === "api_access" && (
                                        <div className="acct-conn-panel">
                                          {!c.canManage && (
                                            <div className="acct-conn-detail">
                                              Only admins can create or revoke
                                              API keys.
                                            </div>
                                          )}
                                          {c.keys.length === 0 && (
                                            <div className="acct-conn-detail">
                                              No active API keys.
                                            </div>
                                          )}
                                          {c.keys.map((k) => (
                                            <div
                                              className="acct-conn-keyrow"
                                              key={k.id}
                                            >
                                              <span>
                                                <b>{k.name}</b> · {k.prefix}…
                                                {k.lastUsedAt
                                                  ? ` · last used ${new Date(k.lastUsedAt).toLocaleDateString()}`
                                                  : " · never used"}
                                              </span>
                                              {c.canManage && (
                                                <button
                                                  className="btn btn-secondary btn-md"
                                                  disabled={connBusy}
                                                  onClick={() =>
                                                    revokeKeys(
                                                      [k.id],
                                                      "API key revoked",
                                                    )
                                                  }
                                                >
                                                  Revoke
                                                </button>
                                              )}
                                            </div>
                                          ))}
                                          {createdKey && (
                                            <div>
                                              <div
                                                className="acct-conn-detail"
                                                style={{ marginBottom: 6 }}
                                              >
                                                Copy this key now, you won't be
                                                able to see it again.
                                              </div>
                                              <div className="acct-keybox">
                                                {createdKey}
                                              </div>
                                              <button
                                                className="btn btn-secondary btn-md"
                                                style={{ marginTop: 8 }}
                                                onClick={() => {
                                                  navigator.clipboard
                                                    ?.writeText(createdKey)
                                                    .then(() =>
                                                      setConnMsg({
                                                        type: "ok",
                                                        text: "API key copied",
                                                      }),
                                                    )
                                                    .catch(() =>
                                                      setConnMsg({
                                                        type: "err",
                                                        text: "Couldn't copy, select the key and copy it.",
                                                      }),
                                                    );
                                                }}
                                              >
                                                Copy key
                                              </button>
                                            </div>
                                          )}
                                          {c.canManage && (
                                            <div className="acct-conn-inline">
                                              <input
                                                type="text"
                                                value={newKeyName}
                                                placeholder="Key name, e.g. CRM sync"
                                                maxLength={60}
                                                disabled={connBusy}
                                                onChange={(e) =>
                                                  setNewKeyName(e.target.value)
                                                }
                                              />
                                              <button
                                                className="btn btn-primary btn-md"
                                                disabled={connBusy}
                                                onClick={createKey}
                                              >
                                                Create key
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                  </div>
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {tab === "prefs" && (
                    <>
                      <div className="acct-card">
                        <div className="acct-card-title">Appearance</div>
                        <div className="acct-card-sub">
                          These controls apply to this browser only.
                        </div>
                        <div className="acct-pref">
                          <div>
                            <div className="acct-pref-label">Dark mode</div>
                          </div>
                          <ThemeSwitch
                            iconOnly
                            theme={darkMode ? "dark" : "light"}
                            onThemeChange={(next, variant, trigger) =>
                              applyThemeWithTransition(
                                next,
                                trigger,
                                setDarkMode,
                                variant,
                              )
                            }
                          />
                        </div>
                        <div className="acct-pref">
                          <div>
                            <div className="acct-pref-label">Table density</div>
                            <div className="acct-pref-sub">
                              How much room each row gets.
                            </div>
                          </div>
                          <div
                            className="acct-pills"
                            role="group"
                            aria-label="Table density"
                          >
                            {["compact", "comfortable", "spacious"].map((d) => (
                              <button
                                key={d}
                                className={`acct-pill${density === d ? " active" : ""}`}
                                onClick={() => setDensity(d)}
                              >
                                {d.charAt(0).toUpperCase() + d.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {tab === "billing" && (
                    <>
                      {billingLoading ? (
                        <div className="acct-card">
                          <div className="acct-card-sub">Loading billing…</div>
                        </div>
                      ) : billingError ? (
                        <div className="acct-card">
                          <div className="acct-card-sub">{billingError}</div>
                        </div>
                      ) : (
                        (() => {
                          const ent =
                            billing?.entitlements ||
                            user?.customer?.entitlements ||
                            null;
                          const seats = ent?.seats;
                          const leads = ent?.leads;
                          const hasSubscription =
                            ent?.subscription?.hasSubscription === true;
                          const price =
                            billing?.planPriceGbp ?? ent?.planPriceGbp ?? null;
                          const seatsPct =
                            seats && !seats.isUnlimited && seats.limit > 0
                              ? Math.min(
                                  100,
                                  Math.round(
                                    ((seats.used || 0) / seats.limit) * 100,
                                  ),
                                )
                              : 0;
                          const leadsPct =
                            leads && !leads.isUnlimited && leads.limit > 0
                              ? Math.min(
                                  100,
                                  Math.round(
                                    ((leads.used || 0) / leads.limit) * 100,
                                  ),
                                )
                              : 0;
                          return (
                            <>
                              <div className="acct-card">
                                <div className="acct-card-title">
                                  Current plan
                                </div>
                                <div
                                  className="acct-plan-card"
                                  style={{ marginTop: 12 }}
                                >
                                  <div>
                                    <div className="acct-plan-name">
                                      {billing?.planName ||
                                        ent?.planName ||
                                        "Manual / custom"}
                                    </div>
                                    <div className="acct-plan-sub">
                                      {price != null
                                        ? `£${price}/month · `
                                        : ""}
                                      {ent?.subscription?.statusLabel ||
                                        "Manual account"}
                                    </div>
                                  </div>
                                  <span className="acct-badge">
                                    {hasSubscription
                                      ? "Subscribed"
                                      : "No subscription"}
                                  </span>
                                </div>
                              </div>

                              {!canSalesPlan(CAP.REVENUE_INTELLIGENCE) && (
                                <CreditsUsage
                                  canManageBilling={hasCapability(
                                    user,
                                    CAPABILITIES.BILLING_MANAGE,
                                  )}
                                />
                              )}

                              <div className="acct-card">
                                <div className="acct-card-title">
                                  Usage this month
                                </div>
                                <div className="acct-card-sub">
                                  Limits reset on the first of each month.
                                </div>
                                <div className="acct-usage-row">
                                  <div className="acct-usage-top">
                                    <span>Seats</span>
                                    <b>
                                      {seats
                                        ? seats.isUnlimited
                                          ? `${seats.used ?? 0} used · Unlimited`
                                          : `${seats.used ?? 0} / ${seats.limit}`
                                        : "—"}
                                    </b>
                                  </div>
                                  <div className="acct-usage-track">
                                    <div
                                      className="acct-usage-fill"
                                      style={{ width: `${seatsPct}%` }}
                                    />
                                  </div>
                                </div>
                                <div className="acct-usage-row">
                                  <div className="acct-usage-top">
                                    <span>Monthly companies</span>
                                    <b>
                                      {leads
                                        ? leads.isUnlimited
                                          ? `${Number(leads.used || 0).toLocaleString()} · Unlimited`
                                          : `${Number(leads.used || 0).toLocaleString()} / ${Number(leads.limit || 0).toLocaleString()}`
                                        : "—"}
                                    </b>
                                  </div>
                                  <div className="acct-usage-track">
                                    <div
                                      className="acct-usage-fill"
                                      style={{ width: `${leadsPct}%` }}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="acct-card">
                                <div className="acct-card-title">
                                  Payment method
                                </div>
                                <div className="acct-card-sub">
                                  How your workspace is billed.
                                </div>
                                <div className="acct-pref">
                                  <div>
                                    <div className="acct-pref-label">
                                      {hasSubscription
                                        ? "Stripe subscription"
                                        : "Billed manually"}
                                    </div>
                                    <div className="acct-pref-sub">
                                      {billing?.licenseStatus ||
                                        ent?.subscription?.licenseStatus ||
                                        ent?.subscription?.statusLabel ||
                                        "No active subscription"}
                                    </div>
                                  </div>
                                  <span className="acct-badge">
                                    {hasSubscription ? "Active" : "Manual"}
                                  </span>
                                </div>
                              </div>

                              {hasSubscription && (
                                <div className="acct-card">
                                  <div className="acct-card-title">
                                    Cancel subscription
                                  </div>
                                  <div className="acct-card-sub">
                                    A cancellation request is sent to your
                                    administrator. We'll contact you to confirm
                                    next steps.
                                  </div>
                                  <button
                                    className="acct-link-btn"
                                    onClick={onRequestCancel}
                                  >
                                    Request cancellation
                                  </button>
                                </div>
                              )}
                            </>
                          );
                        })()
                      )}
                    </>
                  )}
                </>
              )}
            </TabsContent>
          </div>
        </Tabs>

        <div className="acct-foot">
          {status && (
            <span className={`acct-status ${status.type}`}>{status.text}</span>
          )}
          <button className="acct-btn acct-btn-secondary" onClick={onClose}>
            {showSave ? "Close" : "Done"}
          </button>
          {showSave && (
            <button
              className="acct-btn acct-btn-primary"
              onClick={save}
              disabled={saving || loading || !dirty}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CustomerDashboard = () => {
  const { user, logout, checkAuth } = useAuth();
  const { can: canPlan } = usePlanAccess();
  const [companies, setCompanies] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sicExact, setSicExact] = useState(false);
  const [sicValidationError, setSicValidationError] = useState("");
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [pendingRecipients, setPendingRecipients] = useState([]);
  const [emailPreview, setEmailPreview] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    new24h: 0,
    contactedToday: 0,
    totalCreditSafe: 0,
    totalTrend: null,
    new24hTrend: null,
    contactedTodayTrend: null,
    totalSparkline: [],
    new24hSparkline: [],
    contactedTodaySparkline: [],
    totalLastMonth: null,
  });
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
  });
  const [teamUsers, setTeamUsers] = useState([]);
  const [sicHint, setSicHint] = useState("");
  const [jumpPage, setJumpPage] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("axo_theme") === "dark",
  );
  const [density, setDensity] = useState(
    () => localStorage.getItem("axo_density") || "comfortable",
  );
  const [perPage, setPerPage] = useState(() =>
    parseInt(localStorage.getItem("axo_per_page") || "20", 10),
  );
  const [drawerCompany, setDrawerCompany] = useState(null);

  useEffect(() => {
    const handler = () => checkAuth?.();
    window.addEventListener("axo:profile-changed", handler);
    return () => window.removeEventListener("axo:profile-changed", handler);
  }, [checkAuth]);

  // Read-only Companies House profile opened from the dynamic search bar.
  const [chSelected, setChSelected] = useState(null);
  const initialScoreCache = (() => {
    try {
      const s = localStorage.getItem("axo_score_cache");
      const parsed = s ? JSON.parse(s) : {};
      // Drop malformed entries (e.g. a failed refresh that was once cached
      // without `scores`) so they can't crash the score panel on reopen.
      return Object.fromEntries(
        Object.entries(parsed).filter(([, v]) => v && v.scores),
      );
    } catch {
      return {};
    }
  })();
  const scoreCache = useRef(initialScoreCache);
  const drawerRef = useRef(null);
  // Ensures the one-time "land on Sales" redirect below only fires once per
  // mount, so pressing Overview during the session isn't bounced back.
  const defaultLandedRef = useRef(false);
  const [toasts, setToasts] = useState([]);
  const searchDebounceRef = useRef(null);
  const [viewMode, setViewMode] = useState("limited");
  const [activeSection, setActiveSectionState] = useState(
    () => searchParams.get("section") || "overview",
  );
  const [riTab, setRiTabState] = useState(
    () => searchParams.get("ri_tab") || "agent",
  );

  // ── Market (GB / AU / ...) ────────────────────────────────────────────
  // availableMarkets comes from the customer's entitlements
  // (GET /companies/markets); the toggle only renders when there's more
  // than one. activeMarket defaults to whichever the API flags isDefault,
  // falling back to GB before that call returns.
  // Country switcher is only exposed to this one customer for now.
  const canSwitchCountry = user?.customerId === COUNTRY_SWITCHER_CUSTOMER_ID;
  const [availableMarkets, setAvailableMarkets] = useState([
    { countryCode: "GB", isDefault: true },
  ]);
  const [activeMarket, setActiveMarketState] = useState(
    () => (canSwitchCountry && searchParams.get("market")) || "GB",
  );
  const setActiveMarket = (countryCode) => {
    setActiveMarketState(countryCode);
    setSearchParams((prev) => {
      // Switching country starts a fresh list: UK-only filters (SIC, credit,
      // turnover, etc.) must not leak into SA and vice versa.
      const next = new URLSearchParams();
      ["section", "ri_tab"].forEach((key) => {
        const value = prev.get(key);
        if (value) next.set(key, value);
      });
      if (countryCode === "GB") {
        next.delete("market");
      } else {
        next.set("market", countryCode);
      }
      next.set("page", "1");
      return next;
    });
  };

  useEffect(() => {
    apiService
      .getMarkets()
      .then((res) => {
        const markets = res?.data || [];
        if (markets.length > 0) setAvailableMarkets(markets);
        // Only override the URL-driven activeMarket if none was specified
        // and the API's default isn't GB (keeps deep links intact).
        if (canSwitchCountry && !searchParams.get("market")) {
          const def = markets.find((m) => m.isDefault);
          if (def && def.countryCode !== "GB") {
            // Go through the URL-backed setter so the overview reloads for
            // the default market, not just the tab highlight.
            setActiveMarket(def.countryCode);
          }
        }
      })
      .catch((err) => console.error("Failed to load markets:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Both are kept in the URL (not just React state) so a page refresh, a
  // shared link, or the browser back/forward buttons land back on the same
  // dashboard section/tab instead of silently resetting to "overview",
  // same rationale as the existing filter/sort searchParams below.
  const setActiveSection = (section) => {
    setActiveSectionState(section);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (section === "overview") {
        next.delete("section");
        next.delete("ri_tab");
      } else {
        next.set("section", section);
      }
      return next;
    });
  };

  const setRiTab = (tab) => {
    setRiTabState(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("ri_tab", tab);
      return next;
    });
  };

  // Non-entitled users can still open ?section=revenue-intel; they get an
  // upgrade notice instead of the workspace (the Phase 5 endpoints are also
  // gated server-side by requireRevenueIntelligenceFeature).

  // Phase 5 success criterion: "a salesperson can begin each day by opening
  // AXO and receiving a prioritised list". For an entitled user landing on
  // /dashboard with no explicit section, default straight to the Sales
  // workspace. Runs once per mount, an explicit deep link (?section=…) or
  // the header's Overview button always wins, and choosing Overview is not
  // undone by this effect.
  useEffect(() => {
    if (defaultLandedRef.current) return;
    if (!user) return;
    defaultLandedRef.current = true;
    if (searchParams.get("section")) return;
    if (canPlan(CAP.REVENUE_INTELLIGENCE)) {
      setActiveSection("revenue-intel");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, canPlan]);
  const isSoleTrader = viewMode === "sole_trader";
  const isSouthAfrica = activeMarket === "ZA";
  const saCellStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  useEffect(() => {
    if (isSouthAfrica && viewMode === "sole_trader") {
      setViewMode("limited");
    }
  }, [isSouthAfrica, viewMode]);
  const [showFeedback, setShowFeedback] = useState(false);
  const demoUser = user?.isDemo ?? false;
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAcct, setShowAcct] = useState(false);
  const [acctTab, setAcctTab] = useState("profile");

  // Back from Stripe after a £10 credit top-up: confirm it right away (the
  // webhook does the same, idempotently), then land on Billing.
  useEffect(() => {
    const result = searchParams.get("topup");
    if (!result) return;
    const sessionId = searchParams.get("session_id");
    const next = new URLSearchParams(searchParams);
    next.delete("topup");
    next.delete("session_id");
    setSearchParams(next, { replace: true });
    if (result === "success" && sessionId) {
      apiService
        .confirmCreditTopUp(sessionId)
        .catch(() => {})
        .finally(() => window.dispatchEvent(new Event(CREDITS_CHANGED_EVENT)));
    }
    if (canPlan(CAP.REVENUE_INTELLIGENCE)) {
      setActiveSection("revenue-intel");
      setRiTab("credits");
    } else {
      setAcctTab("billing");
      setShowAcct(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [cancelSubmitted, setCancelSubmitted] = useState(false);
  // SA outreach import
  const [saImporting, setSaImporting] = useState(false);
  const [facetsRefresh, setFacetsRefresh] = useState(0);
  const saFileRef = useRef(null);
  // Bulk email states
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(new Set());
  const [bulkEmailType, setBulkEmailType] = useState("preview");
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  // Full template preview/edit for the bulk send confirm dialog
  const [templateMode, setTemplateMode] = useState("preview"); // "preview" | "edit"
  const [customSubject, setCustomSubject] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [templateTouched, setTemplateTouched] = useState(false);
  // Partner Outreach (6ela / wizzyl fixed templates), internal-only bulk
  // email type, gated by the logged-in user's email domain. Mirrors the
  // superadmin PartnerOutreach.jsx page, but reuses the selected companies
  // as recipients instead of a manual list.
  const [partnerTemplateKey, setPartnerTemplateKey] = useState("wizzyl");
  const PARTNER_OUTREACH_DOMAINS = [
    "peachloves.co.uk",
    "peachlovesdigital.co.uk",
  ];
  const canSendPartnerOutreach = PARTNER_OUTREACH_DOMAINS.includes(
    (user?.email?.split("@")[1] || "").toLowerCase(),
  );

  const handleCloseDrawer = () => {
    if (drawerRef.current && !drawerRef.current.canClose()) {
      return; // unsaved changes – modal is shown inside drawer
    }
    setDrawerCompany(null);
  };

  // Feature flags
  const soleTraderEnabled =
    user?.customer?.features?.sole_traders_enabled !== false;
  const targetSicCodes = user?.customer?.features?.target_sic_codes || [];
  const isSuperAdmin = user?.isSuperAdmin === true;

  const [discoveryTrade, setDiscoveryTrade] = useState("");
  const [discoveryLocation, setDiscoveryLocation] = useState("");
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryHasRun, setDiscoveryHasRun] = useState(false);
  const [lastDiscovery, setLastDiscovery] = useState({
    trade: "",
    location: "",
  });

  const [filters, setFilters] = useState({
    search: searchParams.get("q") || "",
    period: searchParams.get("period") || "",
    leadStatus: searchParams.get("lead_status") || "",
    sicCode: searchParams.get("sic") || "",
    enriched: searchParams.get("enriched") || "",
    modifiedBy: searchParams.get("modified_by") || "",
    incorporatedAfter: searchParams.get("incorporated_after") || "",
    incorporatedBefore: searchParams.get("incorporated_before") || "",
    hasCreditData: searchParams.get("has_credit_data") || "",
    emailSent: searchParams.get("email_sent") || "",
    hasTurnover: searchParams.get("has_turnover") || "",
    milestone: searchParams.get("milestone") || "",
    entityType: searchParams.get("entity_type") || "",
    region: searchParams.get("region") || "",
    sizeBucket: searchParams.get("size_bucket") || "",
    status: searchParams.get("status") || "",
    companyType: searchParams.get("company_type") || "",
    industry: searchParams.get("industry") || "",
  });
  const [facets, setFacets] = useState({
    entityTypes: [],
    regions: [],
    sizes: [],
    statuses: [],
    companyTypes: [],
    industries: [],
  });
  const [sortField, setSortField] = useState(searchParams.get("sort") || "");
  const [sortOrder, setSortOrder] = useState(searchParams.get("order") || "");

  const filtersRef = useRef(filters);
  const sortFieldRef = useRef(sortField);
  const sortOrderRef = useRef(sortOrder);
  const perPageRef = useRef(perPage);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);
  useEffect(() => {
    sortFieldRef.current = sortField;
  }, [sortField]);
  useEffect(() => {
    sortOrderRef.current = sortOrder;
  }, [sortOrder]);
  useEffect(() => {
    perPageRef.current = perPage;
  }, [perPage]);
  useEffect(() => {
    // /companies is only used by the Overview list; don't pay for it while
    // the user is in the Sales workspace.
    if (activeSection !== "overview") return;
    loadCompanies();
  }, [viewMode, searchParams, activeSection]);
  useEffect(() => {
    if (activeSection !== "overview" || isSouthAfrica) return;
    loadStats();
  }, [searchParams, activeSection, isSouthAfrica]);

  // Wizzyl/Duda builds write to an external system → Grow+ (Core is view-only).
  const dudaEnabled =
    (user?.customer?.features?.duda_enabled ?? false) && canPlan(CAP.CRM_SYNC);
  // URL stores the two ends separately; the picker wants one range. If only
  // one end is present, mirror it so the trigger still shows something.
  const incorporatedStart = parseDateKey(filters.incorporatedAfter);
  const incorporatedEnd = parseDateKey(filters.incorporatedBefore);
  const incorporatedRange =
    incorporatedStart || incorporatedEnd
      ? {
          start: incorporatedStart || incorporatedEnd,
          end: incorporatedEnd || incorporatedStart,
        }
      : null;
  const activeFilterCount = [
    filters.period,
    filters.leadStatus,
    filters.sicCode,
    filters.enriched,
    filters.modifiedBy,
    filters.search,
    filters.incorporatedAfter,
    filters.incorporatedBefore,
    filters.hasCreditData,
    filters.emailSent,
    filters.hasTurnover,
    filters.milestone,
    filters.entityType,
    filters.region,
    filters.sizeBucket,
    filters.status,
    filters.companyType,
    filters.industry,
  ].filter(Boolean).length;

  const { toast } = useToastStack();
  const showToast = useCallback(
    (message, type = "info") => {
      toast({ title: message, type });
    },
    [toast],
  );

  const loadCompanies = useCallback(
    async ({
      forceLoad = false,
      discoveryTrade: dTrade,
      discoveryLocation: dLocation,
    } = {}) => {
      if (viewMode === "sole_trader" && !forceLoad && !discoveryHasRun) {
        setCompanies([]);
        setLoading(false);
        return;
      }
      const activeCategory = dTrade ?? lastDiscovery.trade;
      const activeLocation = dLocation ?? lastDiscovery.location;
      setLoading(true);
      try {
        const params = {
          page: Number(searchParams.get("page") || 1),
          limit: Number(searchParams.get("limit") || perPageRef.current || 20),
          search: searchParams.get("q") || "",
          period: searchParams.get("period") || "",
          lead_status: searchParams.get("lead_status") || "",
          sicCode: searchParams.get("sic") || "",
          sic_exact: searchParams.get("sic_exact") || "",
          enriched: searchParams.get("enriched") || "",
          modified_by: searchParams.get("modified_by") || "",
          incorporated_after: searchParams.get("incorporated_after") || "",
          incorporated_before: searchParams.get("incorporated_before") || "",
          sort: searchParams.get("sort") || "",
          order: searchParams.get("order") || "",
          has_credit_data: searchParams.get("has_credit_data") || "",
          email_sent: searchParams.get("email_sent") || "",
          has_turnover: searchParams.get("has_turnover") || "",
          milestone: searchParams.get("milestone") || "",
          entity_type: searchParams.get("entity_type") || "",
          region: searchParams.get("region") || "",
          size_bucket: searchParams.get("size_bucket") || "",
          status: searchParams.get("status") || "",
          company_type: searchParams.get("company_type") || "",
          industry: searchParams.get("industry") || "",
          market: searchParams.get("market") || activeMarket,
        };
        let res;
        if (viewMode === "sole_trader") {
          res = await apiService.getSoleTraders({
            ...params,
            category: activeCategory,
            location: activeLocation,
          });
          let items = [],
            currentPage = 1,
            totalPages = 1,
            totalItems = 0;
          if (Array.isArray(res.data)) {
            items = res.data;
            currentPage = res.currentPage || 1;
            totalPages = res.totalPages || 1;
            totalItems = res.totalItems || items.length;
          } else if (res?.data?.items && Array.isArray(res.data.items)) {
            items = res.data.items;
            currentPage = res.data.currentPage || 1;
            totalPages = res.data.totalPages || 1;
            totalItems = res.data.totalItems || items.length;
          } else if (res?.items && Array.isArray(res.items)) {
            items = res.items;
            currentPage = res.currentPage || 1;
            totalPages = res.totalPages || 1;
            totalItems = res.totalItems || items.length;
          }
          setCompanies(items);
          setPagination({ currentPage, totalPages, total: totalItems });
        } else {
          res = await apiService.getCompanies(params);
          setCompanies(res.data || []);
          setPagination({
            currentPage: res.pagination?.page || 1,
            totalPages: res.pagination?.totalPages || 1,
            total: res.pagination?.total || 0,
          });
        }
      } catch (err) {
        console.error("Failed to load companies:", err);
        if (err?.response?.status === 403 && viewMode === "sole_trader") {
          showToast("Sole Traders add-on is not active on your plan", "error");
          setViewMode("limited");
        } else {
          showToast("Failed to load companies", "error");
        }
      } finally {
        setLoading(false);
      }
    },
    [
      viewMode,
      searchParams,
      showToast,
      discoveryHasRun,
      lastDiscovery,
      activeMarket,
    ],
  );

  const handleSaImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSaImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiService.importSaCompanies(formData);
      const result = res?.data || {};
      showToast(
        res?.message ||
          `Imported ${result.created || 0} new and updated ${result.updated || 0} South African companies.`,
        "success",
      );
      setFacetsRefresh((n) => n + 1);
      await loadCompanies();
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to import South African data",
        "error",
      );
    } finally {
      setSaImporting(false);
      if (event.target) event.target.value = "";
    }
  };

  useEffect(() => {
    setFilters({
      search: searchParams.get("q") || "",
      period: searchParams.get("period") || "",
      leadStatus: searchParams.get("lead_status") || "",
      sicCode: searchParams.get("sic") || "",
      enriched: searchParams.get("enriched") || "",
      modifiedBy: searchParams.get("modified_by") || "",
      incorporatedAfter: searchParams.get("incorporated_after") || "",
      incorporatedBefore: searchParams.get("incorporated_before") || "",
      hasCreditData: searchParams.get("has_credit_data") || "",
      emailSent: searchParams.get("email_sent") || "",
      hasTurnover: searchParams.get("has_turnover") || "",
      milestone: searchParams.get("milestone") || "",
      entityType: searchParams.get("entity_type") || "",
      region: searchParams.get("region") || "",
      sizeBucket: searchParams.get("size_bucket") || "",
      status: searchParams.get("status") || "",
      companyType: searchParams.get("company_type") || "",
      industry: searchParams.get("industry") || "",
    });
    setSicExact(searchParams.get("sic_exact") === "1");
    setSortField(searchParams.get("sort") || "");
    setSortOrder(searchParams.get("order") || "");
    // Stats are loaded by the [searchParams] effect above; calling them here
    // too fetched the (heavy) stats twice on every navigation.
  }, [searchParams, loadCompanies]);

  useEffect(() => {
    const hasDateRange =
      filters.incorporatedAfter || filters.incorporatedBefore;
    const hasNoSort = !searchParams.get("sort");
    const isLimitedView = viewMode !== "sole_trader";
    if (hasDateRange && hasNoSort && isLimitedView) {
      const params = new URLSearchParams(searchParams);
      params.set("sort", "incorporatedAt");
      params.set("order", "asc");
      setSearchParams(params);
    }
  }, [
    filters.incorporatedAfter,
    filters.incorporatedBefore,
    viewMode,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (hasCapability(user, CAPABILITIES.USERS_VIEW)) {
      apiService
        .getCustomerUsers()
        .then((res) => setTeamUsers(res.data || []))
        .catch(() => {});
    }
  }, [user]);
  useEffect(() => {
    if (viewMode === "sole_trader" || activeSection !== "overview") return;
    apiService
      .getCompanyFacets({ market: activeMarket })
      .then((res) =>
        setFacets(
          res.data || {
            entityTypes: [],
            regions: [],
            sizes: [],
            statuses: [],
            companyTypes: [],
            industries: [],
          },
        ),
      )
      .catch(() => {});
  }, [viewMode, activeSection, activeMarket, facetsRefresh]);

  useEffect(() => {
    localStorage.setItem("axo_theme", darkMode ? "dark" : "light");
  }, [darkMode]);
  useEffect(() => {
    localStorage.setItem("axo_density", density);
  }, [density]);
  useEffect(() => {
    localStorage.setItem("axo_per_page", perPage.toString());
  }, [perPage]);
  useEffect(() => {
    if (!settingsOpen) return;
    const handler = () => setSettingsOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [settingsOpen]);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const res = await apiService.getCompanyStats();
      const d = res.data.companies;
      setStats({
        total: d.total_companies,
        new24h: d.new_24h,
        contactedToday: d.contacted_today ?? 0,
        totalCreditSafe: d.total_credit_safe ?? 0,
        totalTrend: d.total_trend_pct ?? null,
        new24hTrend: d.new_24h_trend_pct ?? null,
        contactedTodayTrend: d.contacted_today_trend_pct ?? null,
        totalSparkline: d.total_sparkline ?? [],
        new24hSparkline: d.new_24h_sparkline ?? [],
        contactedTodaySparkline: d.contacted_today_sparkline ?? [],
        totalLastMonth: d.total_last_month ?? null,
        scopes: d.scopes || null,
        globalPool: d.globalPool || null,
      });
    } catch (err) {
      console.error("Failed to load stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await apiService.refreshLeads();
      showToast(res.message || "Leads refreshed successfully", "success");
      loadCompanies();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to refresh leads";
      showToast(msg, err?.response?.status === 429 ? "info" : "error");
    } finally {
      setRefreshing(false);
    }
  };

  const updateFilters = (newFilters) => {
    const params = new URLSearchParams();
    const paramMap = {
      search: "q",
      period: "period",
      leadStatus: "lead_status",
      sicCode: "sic",
      sicExact: "sic_exact",
      page: "page",
      enriched: "enriched",
      modifiedBy: "modified_by",
      incorporatedAfter: "incorporated_after",
      incorporatedBefore: "incorporated_before",
      sort: "sort",
      order: "order",
      limit: "limit",
      hasCreditData: "has_credit_data",
      emailSent: "email_sent",
      hasTurnover: "has_turnover",
      milestone: "milestone",
      entityType: "entity_type",
      region: "region",
      sizeBucket: "size_bucket",
      status: "status",
      companyType: "company_type",
      industry: "industry",
    };
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(paramMap[k] || k, v);
    });
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    updateFilters({
      ...filters,
      page: "1",
      sort: sortField,
      order: sortOrder,
      limit: perPage,
    });
  };
  const handleResetFilters = () => {
    setFilters({
      search: "",
      period: "",
      leadStatus: "",
      sicCode: "",
      enriched: "",
      modifiedBy: "",
      incorporatedAfter: "",
      incorporatedBefore: "",
      hasCreditData: "",
      emailSent: "",
      hasTurnover: "",
      milestone: "",
      entityType: "",
      region: "",
      sizeBucket: "",
      status: "",
      companyType: "",
      industry: "",
    });
    setSicExact(false);
    setSicValidationError("");
    setSicHint("");
    setSearchParams({});
  };
  const handleSearchInput = (value) => {
    setFilters((f) => ({ ...f, search: value }));
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(
      () =>
        updateFilters({
          ...filtersRef.current,
          search: value,
          page: "1",
          sort: sortFieldRef.current,
          order: sortOrderRef.current,
          limit: perPageRef.current,
        }),
      500,
    );
  };

  // Picking a Companies House suggestion from the search bar.
  const handleSelectRegistryResult = (r) => {
    if (r.ownedCompanyId) {
      // Already one of this customer's leads → narrow the table to it.
      clearTimeout(searchDebounceRef.current);
      setFilters((f) => ({ ...f, search: r.companyNumber }));
      updateFilters({
        ...filtersRef.current,
        search: r.companyNumber,
        page: "1",
        sort: sortFieldRef.current,
        order: sortOrderRef.current,
        limit: perPageRef.current,
      });
      return;
    }
    setChSelected(r);
  };

  const handleSicInput = useCallback(
    async (value) => {
      const trimmed = value.trim();
      if (trimmed && !/^\d{4,5}$/.test(trimmed)) {
        setSicValidationError("SIC code must be 4 or 5 digits (0‑9 only)");
        setSicHint("");
      } else {
        setSicValidationError("");
        if (trimmed && trimmed.length >= 3) {
          try {
            const res = await apiService.getSicDescription(trimmed);
            setSicHint(res?.data?.description || "");
          } catch {
            setSicHint("");
          }
        } else {
          setSicHint("");
        }
      }
      setFilters((f) => ({ ...f, sicCode: value }));
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(
        () =>
          updateFilters({
            ...filtersRef.current,
            sicCode: value,
            sicExact: sicExact ? "1" : "",
            page: "1",
            sort: sortFieldRef.current,
            order: sortOrderRef.current,
            limit: perPageRef.current,
          }),
        500,
      );
    },
    [
      sicExact,
      updateFilters,
      filtersRef,
      sortFieldRef,
      sortOrderRef,
      perPageRef,
    ],
  );

  const handleSelectFilter = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);
    updateFilters({
      ...updated,
      sicExact: sicExact ? "1" : "",
      page: "1",
      sort: sortField,
      order: sortOrder,
      limit: perPage,
    });
  };

  // A range picker always yields both ends at once, so it can't reuse
  // handleSelectFilter twice (the second call would read stale `filters`).
  const handleIncorporatedRangeChange = (range) => {
    const updated = {
      ...filters,
      incorporatedAfter: toDateKey(range.start),
      incorporatedBefore: toDateKey(range.end),
    };
    setFilters(updated);
    updateFilters({
      ...updated,
      sicExact: sicExact ? "1" : "",
      page: "1",
      sort: sortField,
      order: sortOrder,
      limit: perPage,
    });
  };

  const handleRunDiscovery = async () => {
    const trade = discoveryTrade.trim(),
      location = discoveryLocation.trim();
    if (!trade || !location) {
      showToast("Please enter both a trade and a location", "error");
      return;
    }
    setDiscoveryLoading(true);
    try {
      const res = await apiService.triggerSoleTraderDiscovery(trade, location);
      const count = res?.data?.saved ?? res?.data?.count ?? null;
      showToast(
        count !== null
          ? `Discovery complete, ${count} new record${count !== 1 ? "s" : ""} added`
          : "Discovery complete",
        "success",
      );
      setDiscoveryHasRun(true);
      setLastDiscovery({ trade, location });
      setSearchParams({});
      setPagination((p) => ({ ...p, currentPage: 1 }));
      loadCompanies({
        forceLoad: true,
        discoveryTrade: trade,
        discoveryLocation: location,
      });
    } catch (err) {
      showToast(err?.response?.data?.message || "Discovery failed", "error");
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const handleEmailSent = (companyId, type) => {
    const now = new Date().toISOString();
    const fieldMap = {
      preview: "wizzylOutreachEmailSentAt",
      followup: "wizzylFollowUpEmailSentAt",
      intro: "axoOutreachEmailSentAt",
      // 'brief' has no dedicated timestamp column, lastEmailType alone
      // drives its badge, same as the other types drive theirs.
    };
    const field = fieldMap[type];
    if (!field && type !== "brief") return;
    // Optimistic update so the badge appears immediately
    setCompanies((prev) =>
      prev.map((c) =>
        c.id === companyId
          ? { ...c, ...(field ? { [field]: now } : {}), lastEmailType: type }
          : c,
      ),
    );
    // Re-sync from server so the badge survives page refresh
    loadCompanies();
  };

  const handleUpdateLead = async (companyId, updates) => {
    try {
      await apiService.updateLeadWorkflow(companyId, updates);
      loadCompanies();
    } catch {
      showToast("Failed to update lead", "error");
    }
  };
  const handleTriggerEnrich = async (
    companyId,
    setEnrichState,
    setEnrichData,
  ) => {
    try {
      const res = await apiService.triggerEnrichment(companyId);
      const freshData = res.data || null;
      setEnrichData(freshData);
      setEnrichState(freshData ? "done" : "idle");
      if (freshData)
        setCompanies((prev) =>
          prev.map((c) =>
            c.id === companyId ? { ...c, enrichment: freshData } : c,
          ),
        );
    } catch (err) {
      setEnrichState("failed");
      throw err;
    }
  };
  const handleResync = async (companyId, callback) => {
    try {
      const res = await apiService.resyncEnrichment(companyId);
      const freshData = res.data || null;
      if (freshData) {
        setCompanies((prev) =>
          prev.map((c) =>
            c.id === companyId ? { ...c, enrichment: freshData } : c,
          ),
        );
        callback("done", freshData);
      } else {
        callback("empty", null);
      }
    } catch {
      callback("failed", null);
    }
  };
  // Polls /duda-status until it leaves "processing", reporting qaStatus on
  // every tick along the way. Used both right after triggering a build and
  // to resume watching a company whose drawer is (re)opened mid-build or
  // mid-QA, see resumeDudaPoll below.
  const pollDudaStatus = (companyId, setDudaState, onReady, onQaUpdate) => {
    const poll = setInterval(async () => {
      try {
        const res = await apiService.getDudaStatus(companyId);
        const s = res.data?.dudaStatus;
        // qaStatus passes through even while dudaStatus is held at
        // "processing" server-side pending QA approval, lets the drawer
        // show "awaiting QA" instead of implying the build itself is
        // still running. See qaController.js / companyController.getDudaStatus.
        if (onQaUpdate) onQaUpdate(res.data?.qaStatus ?? null);
        if (s && s !== "processing") {
          clearInterval(poll);
          setDudaState(s);
          if (s === "ready") {
            // Pass the fresh previewUrl back to the drawer so it can update
            // enrichData immediately, without this the drawer's local
            // enrichData still has previewUrl=null and isSiteReady stays false.
            if (onReady) onReady(res.data?.previewUrl || null);
            loadCompanies();
          }
        }
      } catch {
        clearInterval(poll);
        setDudaState("failed");
      }
    }, 8000);
    return () => clearInterval(poll);
  };

  // Resumes watching a company that's already mid-build or sitting in QA
  // when its drawer is (re)opened, without this, closing and reopening
  // the drawer left it stuck showing stale state until the next full
  // company-list refresh. Does NOT trigger a new build.
  const resumeDudaPoll = (companyId, setDudaState, onReady, onQaUpdate) =>
    pollDudaStatus(companyId, setDudaState, onReady, onQaUpdate);

  const handleTriggerDuda = async (
    companyId,
    setDudaState,
    onReady,
    onQaUpdate,
    skipQA = false,
  ) => {
    setDudaState("processing");
    if (onQaUpdate) onQaUpdate(null);
    try {
      await apiService.triggerDudaBuild(companyId, { skipQA });
      pollDudaStatus(companyId, setDudaState, onReady, onQaUpdate);
    } catch {
      setDudaState("failed");
    }
  };

  const handleSort = (field) => {
    let newSort = field,
      newOrder = "asc";
    if (sortField === field) {
      if (sortOrder === "asc") newOrder = "desc";
      else if (sortOrder === "desc") {
        newSort = "";
        newOrder = "";
      }
    }
    updateFilters({
      ...filters,
      page: "1",
      sort: newSort,
      order: newOrder,
      limit: perPage,
    });
  };
  const handlePageChange = (newPage) =>
    updateFilters({
      ...filters,
      page: newPage.toString(),
      sort: sortField,
      order: sortOrder,
      limit: perPage,
    });
  const handlePerPageChange = (val) => {
    setPerPage(val);
    updateFilters({
      ...filters,
      page: "1",
      sort: sortField,
      order: sortOrder,
      limit: val,
    });
  };
  const handleJumpPage = () => {
    const page = parseInt(jumpPage, 10);
    if (!isNaN(page) && page >= 1 && page <= pagination.totalPages) {
      handlePageChange(page);
      setJumpPage("");
    }
  };
  const isNew = (dt) => dt && new Date() - new Date(dt) < 86400000;

  const getPageNumbers = () => {
    const { currentPage: c, totalPages: t } = pagination;
    if (t <= 7) return Array.from({ length: t }, (_, i) => i + 1);
    if (c <= 4) return [1, 2, 3, 4, 5, "...", t];
    if (c >= t - 3) return [1, "...", t - 4, t - 3, t - 2, t - 1, t];
    return [1, "...", c - 1, c, c + 1, "...", t];
  };

  const emptyFilters = {
    search: "",
    period: "",
    leadStatus: "",
    sicCode: "",
    enriched: "",
    modifiedBy: "",
    incorporatedAfter: "",
    incorporatedBefore: "",
    hasCreditData: "",
    emailSent: "",
    hasTurnover: "",
    milestone: "",
    entityType: "",
    region: "",
    sizeBucket: "",
    status: "",
    companyType: "",
    industry: "",
  };
  const applyStatFilter = (overrides) => {
    const updated = { ...emptyFilters, ...overrides };
    setFilters(updated);
    setSicHint("");
    updateFilters({
      ...updated,
      page: "1",
      sort: sortField,
      order: sortOrder,
      limit: perPage,
    });
  };

  const statCards = [
    {
      label: "Eligible companies",
      value: stats.scopes?.eligible?.count ?? stats.total,
      cls: "",
      hint: "Company book minus excluded companies. Reset filters",
      onClick: handleResetFilters,
      trend: stats.totalTrend,
      comparison: stats.totalLastMonth,
      scope: "Eligible · All time",
    },
    {
      label: "New · Last 24h",
      value: stats.new24h,
      cls: "c-green",
      hint: "New companies in the Eligible scope today",
      onClick: () => applyStatFilter({ period: "today" }),
      trend: stats.new24hTrend,
      comparison: null,
      scope: "Eligible · Last 24h",
    },
    {
      label: "Credit Safe Companies",
      value: stats.totalCreditSafe,
      cls: "c-amber",
      hint: "Eligible companies with credit data",
      onClick: () => applyStatFilter({ hasCreditData: "true" }),
      trend: null,
      comparison: null,
      scope: "Eligible · All time",
    },
    {
      label: "Contacted Today",
      value: stats.contactedToday,
      scope: "Eligible · Today",
      cls: "c-accent",
      hint: "Today's activity",
      onClick: () => applyStatFilter({ leadStatus: "!new", period: "today" }),
      trend: stats.contactedTodayTrend,
      comparison: null,
    },
  ];

  // Helper for bulk email
  const getRecipientData = (company, isSoleTrader) => {
    const email = isSoleTrader
      ? company.email
      : company.enrichment?.email || company.email;
    if (!email) return null;
    const firstName = getFirstName(company.directors);
    const previewUrl = company.enrichment?.previewUrl
      ? getWizzylPreviewUrl(company.enrichment.previewUrl)
      : "";
    return {
      toEmail: email,
      firstName: firstName || "there",
      companyName: isSoleTrader ? company.businessName : company.companyName,
      previewUrl, // used for preview/followup, ignored for intro
      customerId: user.customerId,
      companyId: company.id,
    };
  };

  // Default template bodies per bulk email type. Merge tags are resolved
  // per-recipient at render time (preview) and again server-side at send time.
  const getDefaultTemplate = (type) => {
    switch (type) {
      case "preview":
        return {
          subject: "We've created a new website for {{companyName}}",
          body:
            `Hi {{firstName}},\n\n` +
            `We came across {{companyName}} and thought your online presence could work much harder for your business.\n\n` +
            `So rather than simply telling you what we could do, we created a new website concept for you, free of charge.\n\n` +
            `Take a look at your website preview:\n\n` +
            `View the website we created for {{companyName}}:\n{{previewUrl}}\n\n` +
            `There is no obligation and nothing to pay. If you like the direction, we'll complete and refine the entire website for you free of charge. You only begin paying if and when you are completely happy and ask us to publish it.\n\n` +
            `A professional website without the agency price\n\n` +
            `Wizzyl is on a mission to make high-quality websites affordable, straightforward and properly supported.\n\n` +
            `Every Wizzyl website includes:\n` +
            `- Professional design tailored to your business\n` +
            `- Mobile, tablet and desktop optimisation\n` +
            `- AI-supported SEO on every new page\n` +
            `- Hosting and SSL security\n` +
            `- A powerful, easy-to-use Wizzyl CMS\n` +
            `- Contact and enquiry forms\n` +
            `- Online booking and appointment tools\n` +
            `- A web app your customers can save to their phone\n` +
            `- Maps, reviews, galleries, social feeds and video\n` +
            `- WhatsApp, live-chat and call-back widgets\n` +
            `- Blog, news, services and landing pages\n` +
            `- Analytics and lead-capture tools\n` +
            `- Ongoing technical support\n` +
            `- Additional pages and functionality as your business grows\n\n` +
            `We take care of the technology, setup and launch so you can concentrate on running your company.\n\n` +
            `What does it cost?\n\n` +
            `Website creation: £0\n` +
            `Nothing to pay until you approve the website and ask us to publish it.\n` +
            `Once published, Wizzyl costs £49.99 per month, including hosting, SSL security, the Wizzyl platform and ongoing support.\n\n` +
            `No large upfront agency bill. No spending days trying to build it yourself. No launching something you are not completely happy with.\n\n` +
            `Like what you see?\n\n` +
            `Click below and we'll email you a short digital brief. Complete it in a few minutes and our team will use your answers to finish your website.\n\n` +
            `Yes - complete my website free of charge:\n{{previewUrl}}\n\n` +
            `Prefer to speak to someone first? Reply to this email, call us on 02381 680 620, or contact us at hello@wizzyl.com.\n\n` +
            `Your preview is ready whenever you are:\n\n` +
            `View the website created for {{companyName}}:\n{{previewUrl}}\n\n` +
            `Kind regards,\n\n` +
            `The Wizzyl Team\n` +
            `Professionally built websites. Done for you.\n` +
            `£0 to build · £49.99 per month when published\n\n` +
            `https://www.wizzyl.com\n\n` +
            `P.S. If you decide it isn't for you, that's absolutely fine - there is no charge and no obligation.`,
        };
      case "followup":
        return {
          subject: "Still thinking it over, {{firstName}}?",
          body:
            `Hi {{firstName}},\n\n` +
            `Just a quick nudge about the website we built for {{companyName}}, it's still live and ready whenever you want to take another look.\n\n` +
            `{{previewUrl}}\n\n` +
            `Happy to answer any questions or make changes if something's not quite right.\n\n` +
            `Best,\nThe Wizzyl Team`,
        };
      case "brief":
        return {
          subject: "Quick details needed to build your site",
          body:
            `Hi {{firstName}},\n\n` +
            `To get started building the Wizzyl website for {{companyName}}, we just need a few details from you, services, branding, and a bit about the business.\n\n` +
            `It only takes a couple of minutes: {{previewUrl}}\n\n` +
            `Once submitted, we'll kick off the build automatically.\n\n` +
            `Best,\nThe Wizzyl Team`,
        };
      default: // intro
        return {
          subject:
            "{{firstName}}, find your next customers before your competitors do",
          body:
            `Hi {{firstName}},\n\n` +
            `What if your sales team could identify new opportunities as they enter the market, and know which companies are worth approaching before making contact?\n\n` +
            `AXO Signal helps you find, understand and qualify potential customers before your competitors reach them.\n\n` +
            `Instead of buying static lists or spending hours researching businesses across multiple platforms, AXO Signal brings your commercial intelligence into one searchable environment:\n\n` +
            `- Discover newly formed businesses as they enter the market\n` +
            `- Target established companies using industry, location and SIC filters\n` +
            `- Enrich records with company and contact intelligence\n` +
            `- Assess commercial suitability using credit and financial indicators\n` +
            `- Build focused, sales-ready prospect lists\n` +
            `- Export opportunities into your existing CRM and workflows\n` +
            `- Move from market discovery to personalised outreach in minutes\n\n` +
            `The result is a better-targeted pipeline, faster speed to contact and less time wasted pursing businesses that are unlikely to convert. This is why AXO Signal customers are using the technology from the UK to South Africa.\n\n` +
            `See AXO Signal in action:\n\n` +
            `Visit the website to explore the live demo:\nhttps://www.axosignal.io\n\n` +
            `Packages start from £250 per month, with flexible monthly access and no long-term contract. You can sign up easily online; you'll choose your ideal target audiences and subscription level and will have access within 24 hours.\n\n` +
            `If you tell us who your ideal customers are, we would also be happy to show you how AXO Signal could identify and qualify that market for your team.\n\n` +
            `View the packages:\nhttps://www.axosignal.io/#Packages\n\n` +
            `Or simply reply with your target industry and location, and we'll arrange a tailored walkthrough.\n\n` +
            `Best regards,\n\n` +
            `The AXO Signal Team\n` +
            `www.axosignal.io\n` +
            `02381 680 620\n\n` +
            `Stop buying lists. Start building intelligence.`,
        };
    }
  };

  // Fills merge tags with a given recipient's data (or sample text if none)
  const renderMergedTemplate = (text, recipient) => {
    if (!text) return "";
    const sample = {
      firstName: recipient?.firstName || "there",
      companyName: recipient?.companyName || "Acme Ltd",
      previewUrl: recipient?.previewUrl || "https://preview.wizzyl.io/sample",
    };
    return text
      .replaceAll("{{firstName}}", sample.firstName)
      .replaceAll("{{companyName}}", sample.companyName)
      .replaceAll("{{previewUrl}}", sample.previewUrl);
  };

  const prepareBulkSend = async () => {
    if (selectedCompanyIds.size === 0) return;

    const companiesToSend = companies.filter((c) =>
      selectedCompanyIds.has(String(c.id)),
    );
    let recipients = companiesToSend
      .map((c) => getRecipientData(c, isSoleTrader))
      .filter((r) => r !== null);

    if (recipients.length === 0) {
      showToast(
        "None of the selected companies have an email address.",
        "error",
      );
      return;
    }
    if (recipients.length > 100) {
      showToast(
        "Cannot send more than 100 emails at once. Please select fewer companies.",
        "error",
      );
      return;
    }

    if (bulkEmailType === "preview") {
      const withSite = recipients.filter(
        (r) =>
          companiesToSend.find((c) => c.id === r.companyId)?.enrichment
            ?.dudaStatus === "ready" &&
          companiesToSend.find((c) => c.id === r.companyId)?.enrichment
            ?.previewUrl,
      );
      if (withSite.length < recipients.length) {
        showToast(
          `${recipients.length - withSite.length} selected company(s) do not have a generated Wizzyl site and will be skipped.`,
          "info",
        );
      }
      recipients = withSite;
    }

    if (bulkEmailType === "followup") {
      const withSite = recipients.filter(
        (r) =>
          companiesToSend.find((c) => c.id === r.companyId)?.enrichment
            ?.dudaStatus === "ready" &&
          companiesToSend.find((c) => c.id === r.companyId)?.enrichment
            ?.previewUrl,
      );
      if (withSite.length < recipients.length) {
        showToast(
          `${recipients.length - withSite.length} selected company(s) do not have a generated Wizzyl site and will be skipped.`,
          "info",
        );
      }
      recipients = withSite;
    }

    // Build a preview object (no full email body – just metadata)
    let subject = "";
    let description = "";
    if (bulkEmailType === "preview") {
      subject = "Pitch / Introduction Email";
      description =
        "Only companies with a generated Wizzyl site will receive the Wizzyl pitch email. Companies without a site will be skipped (a warning will be shown).";
    } else if (bulkEmailType === "followup") {
      subject = "Follow-up: Your website is ready";
      description =
        "A gentle follow-up email reminding the recipient about the pre-built Wizzyl website.";
    } else if (bulkEmailType === "brief") {
      subject = "Business Brief Request";
      description =
        "Sends each recipient a unique link to a short brief form covering their business details, services, and branding. Once submitted, their Wizzyl site build starts automatically.";
    } else if (bulkEmailType === "partner-outreach") {
      subject =
        partnerTemplateKey === "6ela"
          ? "A new cash flow solution for your clients"
          : "A zero-risk web infrastructure for your SME clients";
      description = `Independent partner-recruitment outreach (${
        partnerTemplateKey === "6ela" ? "6ELA" : "Wizzyl"
      } fixed template, not editable here) inviting the recipient to join the partner network. Recipients are treated as external prospects, not AXO leads.`;
    } else {
      // intro email
      subject =
        "Introducing AXO Signal – Commercial Intelligence Built For Growth";
      description =
        "An introduction to AXO Signal, an AI-powered commercial intelligence platform covering newly formed business discovery, company enrichment, credit profiling, financial intelligence, and more. Includes links to the demo environment (demo.axosignal.io) and platform (axosignal.io).";
    }

    let defaultTemplate;
    if (bulkEmailType === "partner-outreach") {
      // Fixed template, fetch the real, read-only rendered copy from the
      // backend (PARTNER_TEMPLATES) instead of showing a placeholder.
      defaultTemplate = { subject, body: "Loading template…" };
      setPendingRecipients(recipients);
      setEmailPreview({ type: bulkEmailType, subject, description });
      setCustomSubject(defaultTemplate.subject);
      setCustomBody(defaultTemplate.body);
      setTemplateTouched(false);
      setTemplateMode("preview");
      setShowSendConfirm(true);

      try {
        const sampleRecipient = recipients[0];
        const { preview } = await apiService.getPartnerOutreachPreview(
          partnerTemplateKey,
          {
            firstName: sampleRecipient?.firstName,
            companyName: sampleRecipient?.companyName,
          },
        );
        setCustomSubject(preview.subject);
        setCustomBody(preview.body);
      } catch (err) {
        console.error("Failed to load partner outreach preview:", err);
        setCustomBody(
          "Unable to load the template preview. You can still send, the fixed template will be used, but please try reopening this dialog first.",
        );
        showToast(
          "Couldn't load the partner outreach template preview.",
          "error",
        );
      }
      return;
    }

    defaultTemplate = getDefaultTemplate(bulkEmailType);
    setPendingRecipients(recipients);
    setEmailPreview({ type: bulkEmailType, subject, description });
    setCustomSubject(defaultTemplate.subject);
    setCustomBody(defaultTemplate.body);
    setTemplateTouched(false);
    setTemplateMode("preview");
    setShowSendConfirm(true);
  };

  const confirmBulkSend = async () => {
    setShowSendConfirm(false);
    setBulkSending(true);
    try {
      if (bulkEmailType === "partner-outreach") {
        const response = await apiService.sendPartnerOutreachBulk(
          partnerTemplateKey,
          pendingRecipients.map((r) => ({
            toEmail: r.toEmail,
            firstName: r.firstName,
            companyName: r.companyName,
          })),
        );
        const { summary } = response;
        setBulkResults(summary);
        showToast(
          `Bulk send finished: ${summary.sent} sent, ${summary.failed} failed`,
          summary.failed === 0 ? "success" : "info",
        );
        setSelectedCompanyIds(new Set());
        return;
      }

      const response = await apiService.bulkSendEmails(
        bulkEmailType,
        pendingRecipients,
        // Overrides only sent if the user actually edited the template;
        // otherwise the backend falls back to its standard template.
        templateTouched ? { customSubject, customBody } : undefined,
      );
      const { summary } = response;
      setBulkResults(summary);
      // Patch companies state so row badges appear immediately
      const now = new Date().toISOString();
      const sentIds = new Set(
        summary.details.filter((r) => r.success).map((r) => r.companyId),
      );
      if (sentIds.size > 0) {
        setCompanies((prev) =>
          prev.map((c) => {
            if (!sentIds.has(c.id)) return c;
            const detail = summary.details.find(
              (r) => r.success && r.companyId === c.id,
            );
            const effectiveType = detail?.emailType ?? bulkEmailType;
            if (effectiveType === "preview")
              return {
                ...c,
                wizzylOutreachEmailSentAt: now,
                lastEmailType: "preview",
              };
            if (effectiveType === "followup")
              return {
                ...c,
                wizzylFollowUpEmailSentAt: now,
                lastEmailType: "followup",
              };
            if (effectiveType === "intro")
              return {
                ...c,
                axoOutreachEmailSentAt: now,
                lastEmailType: "intro",
              };
            if (effectiveType === "brief")
              return { ...c, lastEmailType: "brief" };
            return c;
          }),
        );
      }
      const splitNote =
        bulkEmailType === "preview" && summary.sentAsPitch !== undefined
          ? ` (${summary.sentAsPitch} pitch, ${summary.sentAsIntro} intro)`
          : "";
      showToast(
        `Bulk send finished: ${summary.sent} sent${splitNote}, ${summary.failed} failed, ${summary.skippedUnsubscribed} skipped (unsubscribed)`,
        summary.failed === 0 ? "success" : "info",
      );
      setSelectedCompanyIds(new Set());
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.error || "Bulk send failed", "error");
    } finally {
      setBulkSending(false);
      setPendingRecipients([]);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className={`dash-root${darkMode ? " dark" : ""}`}>
        {activeSection === "overview" && <ProductTour />}
        {activeSection === "revenue-intel" &&
          canPlan(CAP.REVENUE_INTELLIGENCE) &&
          riTab === "agent" && <SalesWorkspaceTour />}

        {/* DRAWER */}
        <Drawer
          open={!!drawerCompany}
          onOpenChange={(next) => {
            if (!next) handleCloseDrawer();
          }}
        >
          {drawerCompany && (
            <DrawerContent
              side="right"
              className="lead-drawer"
              title={drawerCompany.businessName || drawerCompany.companyName}
              description={
                drawerCompany.companyNumber || drawerCompany.category || ""
              }
            >
              <CompanyDrawer
                ref={drawerRef}
                company={drawerCompany}
                scoreCache={scoreCache}
                isSoleTrader={viewMode === "sole_trader"}
                onClose={handleCloseDrawer} // ← changed
                onUpdate={handleUpdateLead}
                onTriggerDuda={handleTriggerDuda}
                onResumeDudaPoll={resumeDudaPoll}
                onTriggerEnrich={handleTriggerEnrich}
                onResync={handleResync}
                dudaEnabled={dudaEnabled}
                onToast={showToast}
                onEmailSent={handleEmailSent}
                onFinancialsRefreshed={(figures) => {
                  setDrawerCompany((prev) =>
                    prev ? { ...prev, ...figures } : prev,
                  );
                  setCompanies((prev) =>
                    prev.map((c) =>
                      c.id === drawerCompany.id ? { ...c, ...figures } : c,
                    ),
                  );
                }}
              />
            </DrawerContent>
          )}
        </Drawer>

        {/* COMPANIES HOUSE (read-only) DRAWER */}
        <CompaniesHouseDrawer
          companyNumber={chSelected?.companyNumber || null}
          summary={chSelected}
          onClose={() => setChSelected(null)}
          onToast={showToast}
          onAdded={() => {
            setChSelected(null);
            loadCompanies();
          }}
        />

        {/* APP COMING SOON BANNER */}
        <div className="app-banner">
          <span className="app-banner-text">
            <strong>Mobile app coming soon!</strong>, A dedicated app experience
            is on its way.
          </span>
          <span className="app-banner-badge">Coming Soon</span>
        </div>

        {/* HEADER */}
        <header className="dash-header">
          <div className="dash-header-inner">
            <div className="dash-brand">
              <div className="dash-brand-logo">
                <img src="/assets/logo.png" alt="logo" />
              </div>
              <div className="dash-brand-text">
                <div className="dash-brand-name">
                  {user?.customer?.name || "Lead Dashboard"}
                </div>
                <div className="dash-brand-sub">Customer Portal</div>
              </div>
            </div>
            <nav className="dash-nav">
              <button
                className={`btn btn-ghost btn-sm${activeSection === "revenue-intel" ? " active" : ""}`}
                onClick={() => setActiveSection("revenue-intel")}
              >
                Sales
              </button>
              {activeSection === "revenue-intel" && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setActiveSection("overview")}
                >
                  Overview
                </button>
              )}
              {hasCapability(user, CAPABILITIES.USERS_VIEW) && (
                <Link to="/team" className="btn btn-ghost btn-sm">
                  Team
                </Link>
              )}
              {user?.isSuperAdmin && (
                <Link
                  to="/super-admin"
                  className="btn btn-ghost btn-sm btn-admin"
                >
                  Super Admin
                </Link>
              )}
              <NotificationBell />
              <CreditBalancePill
                onClick={() => {
                  if (canPlan(CAP.REVENUE_INTELLIGENCE)) {
                    setActiveSection("revenue-intel");
                    setRiTab("credits");
                  } else {
                    setAcctTab("billing");
                    setShowAcct(true);
                  }
                }}
              />
              <div
                className="settings-wrap"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSettingsOpen((o) => !o)}
                >
                  Settings <Icon.ChevronDown />
                </button>
                <div className={`settings-menu${settingsOpen ? " open" : ""}`}>
                  <div className="settings-row">
                    <span>Dark Mode</span>
                    <ThemeSwitch
                      iconOnly
                      theme={darkMode ? "dark" : "light"}
                      onThemeChange={(next, variant, trigger) =>
                        applyThemeWithTransition(
                          next,
                          trigger,
                          setDarkMode,
                          variant,
                        )
                      }
                    />
                  </div>
                  <div className="settings-divider" />
                  <div className="settings-section-label">Table Density</div>
                  <div className="density-btns">
                    {["compact", "comfortable", "spacious"].map((d) => (
                      <button
                        key={d}
                        className={`density-btn${density === d ? " active" : ""}`}
                        onClick={() => setDensity(d)}
                      >
                        {d.charAt(0).toUpperCase() + d.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div className="settings-divider" />
                  <div
                    className="settings-row"
                    onClick={() => {
                      setAcctTab("profile");
                      setShowAcct(true);
                      setSettingsOpen(false);
                    }}
                  >
                    <span>Account settings</span>
                    <span
                      style={{
                        display: "inline-flex",
                        transform: "rotate(-90deg)",
                      }}
                    >
                      <Icon.ChevronDown />
                    </span>
                  </div>
                  <div
                    className="settings-row"
                    onClick={() => {
                      setAcctTab("channels");
                      setShowAcct(true);
                      setSettingsOpen(false);
                    }}
                  >
                    <span>Company &amp; channels</span>
                    <span
                      style={{
                        display: "inline-flex",
                        transform: "rotate(-90deg)",
                      }}
                    >
                      <Icon.ChevronDown />
                    </span>
                  </div>
                  <div
                    className="settings-row"
                    onClick={() => {
                      setAcctTab("connections");
                      setShowAcct(true);
                      setSettingsOpen(false);
                    }}
                  >
                    <span>Connections &amp; API</span>
                    <span
                      style={{
                        display: "inline-flex",
                        transform: "rotate(-90deg)",
                      }}
                    >
                      <Icon.ChevronDown />
                    </span>
                  </div>
                  <div
                    className="settings-row"
                    onClick={() => {
                      setAcctTab("billing");
                      setShowAcct(true);
                      setSettingsOpen(false);
                    }}
                  >
                    <span>Plan &amp; billing</span>
                    <span
                      style={{
                        display: "inline-flex",
                        transform: "rotate(-90deg)",
                      }}
                    >
                      <Icon.ChevronDown />
                    </span>
                  </div>
                </div>
              </div>
              <div className="user-pill">
                <div className="online-dot" />
                {user?.name}
              </div>
              <button onClick={logout} className="btn btn-ghost btn-sm">
                Logout
              </button>
            </nav>
          </div>
        </header>

        <div className="dash-main">
          {/* GREETING */}
          <div className="dashboard-greeting">
            <div className="dashboard-greeting-text">
              {getGreeting(user?.name)}
            </div>
          </div>

          {activeSection === "overview" && (
            <>
              {/* STATS (UK book — hidden for non-UK markets) */}
              {!isSouthAfrica && (
                <div className="stats-grid">
                  {statCards.map((card) => (
                    <EnhancedStatCard
                      key={card.label}
                      {...card}
                      loading={statsLoading}
                    />
                  ))}
                </div>
              )}

              {!isSouthAfrica && stats.scopes && (
                <div className="scope-strip" aria-label="Named company scopes">
                  {[
                    stats.scopes.company_book,
                    stats.scopes.eligible,
                    stats.scopes.today_priorities,
                    stats.scopes.active_pipeline,
                  ].map((scope) =>
                    scope ? (
                      <span className="scope-chip" key={scope.key}>
                        {scope.label} <b>{scope.count.toLocaleString()}</b>
                        <small>{scope.period}</small>
                      </span>
                    ) : null,
                  )}
                  {stats.globalPool && (
                    <span className="scope-chip scope-chip-global">
                      {stats.globalPool.label}{" "}
                      <b>{stats.globalPool.count.toLocaleString()}</b>
                      <small>All companies · not your book</small>
                    </span>
                  )}
                </div>
              )}

              {/* LEAD QUOTA BAR */}
              {!isSuperAdmin && (
                <LeadQuotaBar
                  customer={user?.customer}
                  onRefresh={handleRefresh}
                  refreshing={refreshing}
                  isSuperAdmin={isSuperAdmin}
                />
              )}

              {/* INDUSTRY TARGETING BANNER */}
              {targetSicCodes.length > 0 && !isSouthAfrica && (
                <div className="industry-banner">
                  <Icon.Filter />
                  <span className="industry-banner-text">
                    <strong>Industry filter active:</strong> Your account is set
                    to show companies in {targetSicCodes.length} targeted SIC
                    code
                    {targetSicCodes.length !== 1 ? "s" : ""} only. Results are
                    automatically filtered based on your signup preferences.
                  </span>
                </div>
              )}

              {/* SIDEBAR + CONTENT */}
              <div className="dash-body">
                {/* FILTERS SIDEBAR */}
                <div className="dash-sidebar">
                  <div className="filter-panel">
                    <div className="filter-panel-head">
                      <div className="filter-panel-title">
                        Filters
                        {activeFilterCount > 0 && (
                          <span className="filter-count-badge">
                            {activeFilterCount}
                          </span>
                        )}
                      </div>
                    </div>
                    <form onSubmit={handleSearch}>
                      <div className="filter-grid">
                        <div className="field-group" id="tour-search">
                          <div className="field-label">Search</div>
                          <CompanySearchTypeahead
                            value={filters.search}
                            onChange={handleSearchInput}
                            onSelectResult={handleSelectRegistryResult}
                            canSearchRegistry={
                              canPlan(CAP.SEARCH_COMPANIES_HOUSE) &&
                              viewMode !== "sole_trader" &&
                              !isSouthAfrica
                            }
                          />
                        </div>
                        {isSouthAfrica && (
                          <>
                            <div className="field-group">
                              <div className="field-label">Company Status</div>
                              <Select
                                label="Company Status"
                                hideLabel
                                className="field-input"
                                placeholder="All statuses"
                                value={filters.status}
                                onValueChange={(value) =>
                                  handleSelectFilter("status", value)
                                }
                                options={(facets.statuses || []).map((f) => ({
                                  value: f.value,
                                  label: `${f.label} (${f.count.toLocaleString()})`,
                                }))}
                              />
                            </div>
                            <div className="field-group">
                              <div className="field-label">Industry</div>
                              <Select
                                label="Industry"
                                hideLabel
                                className="field-input"
                                placeholder="All industries"
                                value={filters.industry}
                                onValueChange={(value) =>
                                  handleSelectFilter("industry", value)
                                }
                                options={(facets.industries || []).map((f) => ({
                                  value: f.value,
                                  label: `${f.label} (${f.count.toLocaleString()})`,
                                }))}
                              />
                            </div>
                          </>
                        )}
                        {!isSouthAfrica && (
                          <div className="field-group" id="tour-period">
                            <div className="field-label">Period</div>
                            <Select
                              label="Period"
                              hideLabel
                              className="field-input"
                              placeholder="All Time"
                              value={filters.period}
                              onValueChange={(value) =>
                                handleSelectFilter("period", value)
                              }
                              options={[
                                { value: "today", label: "Today" },
                                { value: "7days", label: "Last 7 Days" },
                              ]}
                            />
                          </div>
                        )}
                        {!isSouthAfrica && (
                          <div className="field-group" id="tour-pipeline">
                            <div className="field-label">Pipeline Stage</div>
                            <Select
                              label="Pipeline Stage"
                              hideLabel
                              className="field-input"
                              placeholder="All Stages"
                              value={filters.leadStatus}
                              onValueChange={(value) =>
                                handleSelectFilter("leadStatus", value)
                              }
                              options={STATUS_OPTIONS.map((s) => ({
                                value: s.value,
                                label: s.label,
                              }))}
                            />
                          </div>
                        )}
                        {!isSouthAfrica && (
                          <div className="field-group" id="tour-sic">
                            <div className="field-label">SIC Code</div>
                            <Input
                              label="SIC Code"
                              hideLabel
                              type="text"
                              value={filters.sicCode}
                              onChange={(e) => handleSicInput(e.target.value)}
                              placeholder="e.g. 78109"
                              className="field-input"
                            />
                            {sicValidationError ? (
                              <div
                                className="sic-hint"
                                style={{ color: "var(--red)" }}
                              >
                                {sicValidationError}
                              </div>
                            ) : (
                              sicHint && (
                                <div className="sic-hint">↳ {sicHint}</div>
                              )
                            )}
                          </div>
                        )}
                        {!isSouthAfrica && (
                          <div className="field-group" id="tour-enrichment">
                            <div className="field-label">Enrichment</div>
                            <Select
                              label="Enrichment"
                              hideLabel
                              className="field-input"
                              placeholder="All"
                              value={filters.enriched}
                              onValueChange={(value) =>
                                handleSelectFilter("enriched", value)
                              }
                              options={[
                                { value: "true", label: "Enriched" },
                                { value: "false", label: "Not Enriched" },
                              ]}
                            />
                          </div>
                        )}
                        {!isSouthAfrica && (
                          <div className="field-group" id="tour-credit-data">
                            <div className="field-label">Credit Data</div>
                            <Select
                              label="Credit Data"
                              hideLabel
                              className="field-input"
                              placeholder="All"
                              value={filters.hasCreditData}
                              onValueChange={(value) =>
                                handleSelectFilter("hasCreditData", value)
                              }
                              options={[
                                { value: "true", label: "Has Credit Info" },
                                { value: "false", label: "No Credit Info" },
                              ]}
                            />
                          </div>
                        )}
                        {!isSouthAfrica && (
                          <div className="field-group">
                            <div className="field-label">Email Outreach</div>
                            <Select
                              label="Email Outreach"
                              hideLabel
                              className="field-input"
                              placeholder="All"
                              value={filters.emailSent}
                              onValueChange={(value) =>
                                handleSelectFilter("emailSent", value)
                              }
                              options={[
                                { value: "true", label: "Email Sent" },
                                { value: "false", label: "Not Contacted" },
                              ]}
                            />
                          </div>
                        )}
                        {!isSoleTrader && !isSouthAfrica && (
                          <div className="field-group">
                            <div className="field-label">Turnover</div>
                            <Select
                              label="Turnover"
                              hideLabel
                              className="field-input"
                              placeholder="All"
                              value={filters.hasTurnover}
                              onValueChange={(value) =>
                                handleSelectFilter("hasTurnover", value)
                              }
                              options={[
                                {
                                  value: "true",
                                  label: "Has turnover (latest)",
                                },
                                {
                                  value: "any",
                                  label: "Has turnover (any year)",
                                },
                                { value: "false", label: "No turnover" },
                              ]}
                            />
                            <div className="sic-hint">
                              ↳ From filed accounts (where reported)
                            </div>
                          </div>
                        )}
                        {!isSoleTrader && !isSouthAfrica && (
                          <div className="field-group">
                            <div className="field-label">Growth Milestone</div>
                            <Select
                              label="Growth Milestone"
                              hideLabel
                              className="field-input"
                              placeholder="Any"
                              value={filters.milestone}
                              onValueChange={(value) =>
                                handleSelectFilter("milestone", value)
                              }
                              options={[
                                { value: "any", label: "Has any milestone" },
                                {
                                  value: "turnover_crossed_1m",
                                  label: "Crossed £1m turnover",
                                },
                                {
                                  value: "turnover_above_1m",
                                  label: "Turnover over £1m",
                                },
                                {
                                  value: "micro_to_small",
                                  label: "Micro → small accounts",
                                },
                                {
                                  value: "turnover_growth",
                                  label: "Turnover growth",
                                },
                                {
                                  value: "net_assets_growth",
                                  label: "Net assets growth",
                                },
                                {
                                  value: "employees_growth",
                                  label: "Headcount growth",
                                },
                                {
                                  value: "full_accounts",
                                  label: "Full accounts upgrade",
                                },
                              ]}
                            />
                            <div className="sic-hint">
                              ↳ Detected from filed-accounts history
                            </div>
                          </div>
                        )}
                        {!isSoleTrader && !isSouthAfrica && (
                          <div className="field-group">
                            <div className="field-label">Entity type</div>
                            <Select
                              label="Entity type"
                              hideLabel
                              className="field-input"
                              placeholder="All entity types"
                              value={filters.entityType}
                              onValueChange={(value) =>
                                handleSelectFilter("entityType", value)
                              }
                              options={(facets.entityTypes || []).map((f) => ({
                                value: f.value,
                                label: `${f.label} (${f.count.toLocaleString()})`,
                              }))}
                            />
                          </div>
                        )}
                        {!isSoleTrader && (
                          <div className="field-group">
                            <div className="field-label">
                              {isSouthAfrica ? "Location" : "Region"}
                            </div>
                            <Select
                              label="Region"
                              hideLabel
                              className="field-input"
                              placeholder={
                                isSouthAfrica ? "All locations" : "All regions"
                              }
                              value={filters.region}
                              onValueChange={(value) =>
                                handleSelectFilter("region", value)
                              }
                              options={(facets.regions || []).map((f) => ({
                                value: f.value,
                                label: `${f.label} (${f.count.toLocaleString()})`,
                              }))}
                            />
                          </div>
                        )}
                        {!isSoleTrader && !isSouthAfrica && (
                          <div className="field-group">
                            <div className="field-label">Size</div>
                            <Select
                              label="Size"
                              hideLabel
                              className="field-input"
                              placeholder="All sizes"
                              value={filters.sizeBucket}
                              onValueChange={(value) =>
                                handleSelectFilter("sizeBucket", value)
                              }
                              options={(facets.sizes || []).map((f) => ({
                                value: f.value,
                                label: `${f.label} (${f.count.toLocaleString()})`,
                              }))}
                            />
                          </div>
                        )}
                        <div className="field-group" id="tour-date-range">
                          <div className="field-label">
                            {isSoleTrader
                              ? "Discovered"
                              : isSouthAfrica
                                ? "Email sent"
                                : "Incorporated"}{" "}
                            Date Range
                          </div>
                          <DateRangePicker
                            className="drp-field"
                            label={
                              isSoleTrader
                                ? "Discovered date range"
                                : isSouthAfrica
                                  ? "Email sent date range"
                                  : "Incorporated date range"
                            }
                            value={incorporatedRange}
                            onChange={handleIncorporatedRangeChange}
                            maxDate={new Date()}
                            weekStartsOn={1}
                            locale="en-GB"
                          />
                        </div>
                        {hasCapability(user, CAPABILITIES.LEADS_VIEW_ALL) &&
                          !isSouthAfrica && (
                            <div className="field-group" id="tour-modified-by">
                              <div className="field-label">Modified By</div>
                              <Select
                                label="Modified By"
                                hideLabel
                                className="field-input"
                                placeholder="All Users"
                                value={filters.modifiedBy}
                                onValueChange={(value) =>
                                  handleSelectFilter("modifiedBy", value)
                                }
                                options={teamUsers.map((u) => ({
                                  value: String(u.id),
                                  label: u.name || u.username,
                                }))}
                              />
                            </div>
                          )}
                      </div>
                      <div className="filter-actions">
                        <button
                          type="button"
                          onClick={handleResetFilters}
                          className="btn btn-secondary btn-sm"
                        >
                          Reset
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
                {/* end dash-sidebar */}

                <div className="dash-content">
                  {/* Enrichment tip banner */}
                  {!isSouthAfrica && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        background: "var(--amber-dim)",
                        border: "1px solid var(--amber)",
                        borderRadius: "var(--radius-sm)",
                        padding: "10px 14px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12.5px",
                          color: "var(--amber)",
                          lineHeight: "1.5",
                        }}
                      >
                        <strong>Tip:</strong> Allow newly registered companies a
                        few weeks to establish their online and social presence
                        before enriching, this leads to more accurate contact
                        data and higher match rates.
                      </span>
                    </div>
                  )}

                  {/* Turnover availability info - Companies House doesn't
                      publish turnover for most UK companies, so there's no
                      turnover/size filter. It's shown per company where filed. */}
                  {!isSoleTrader && !isSouthAfrica && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        background: "var(--accent-dim)",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius-sm)",
                        padding: "10px 14px",
                        marginTop: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12.5px",
                          color: "var(--text-faint)",
                          lineHeight: "1.5",
                        }}
                      >
                        <strong>Turnover:</strong> Companies House doesn't
                        publish turnover for most UK companies (small companies
                        can file balance-sheet-only accounts). Use the Turnover
                        filter to find the ones that do report it; where a
                        company's filed accounts include a profit-and-loss
                        account, its turnover is shown on the company card.
                      </span>
                    </div>
                  )}

                  {/* COUNTRY SWITCHER + SA IMPORT */}
                  {canSwitchCountry && availableMarkets.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: 12,
                        flexWrap: "wrap",
                        marginBottom: 12,
                      }}
                    >
                      <div
                        className="field-group"
                        style={{ maxWidth: 260, flex: "0 1 260px" }}
                      >
                        <div className="field-label">Country</div>
                        <Select
                          label="Country"
                          hideLabel
                          className="field-input"
                          value={activeMarket}
                          onValueChange={setActiveMarket}
                          options={availableMarkets.map((m) => ({
                            value: m.countryCode,
                            label:
                              {
                                GB: "United Kingdom",
                                AU: "Australia",
                                ZA: "South Africa",
                              }[m.countryCode] || m.countryCode,
                          }))}
                        />
                      </div>
                      {isSouthAfrica &&
                        hasCapability(user, CAPABILITIES.COMPANY_EDIT) && (
                          <div className="field-group">
                            <div className="field-label">
                              South African data
                            </div>
                            <input
                              ref={saFileRef}
                              type="file"
                              accept=".csv,.xlsx,.xls"
                              onChange={handleSaImport}
                              style={{ display: "none" }}
                            />
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              disabled={saImporting}
                              onClick={() => saFileRef.current?.click()}
                            >
                              {saImporting
                                ? "Importing…"
                                : "Import CSV / Excel"}
                            </button>
                          </div>
                        )}
                    </div>
                  )}

                  {/* VIEW MODE TABS (UK only — SA has no sole-trader split) */}
                  {!isSouthAfrica && (
                    <Tabs
                      value={viewMode}
                      onValueChange={(next) => {
                        setViewMode(next);
                        setPagination((p) => ({ ...p, currentPage: 1 }));
                      }}
                    >
                      <TabsList aria-label="Company type">
                        <TabsTrigger value="limited">
                          Limited Companies
                        </TabsTrigger>
                        {soleTraderEnabled ? (
                          <TabsTrigger value="sole_trader">
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              Sole Traders
                              <span
                                style={{
                                  fontSize: "10px",
                                  fontFamily: "var(--mono)",
                                  fontWeight: 600,
                                  background: "var(--accent)",
                                  color: "#fff",
                                  padding: "2px 8px",
                                  borderRadius: "100px",
                                  letterSpacing: "0.04em",
                                  lineHeight: 1.3,
                                }}
                              >
                                ADD-ON
                              </span>
                            </span>
                          </TabsTrigger>
                        ) : (
                          <TabsTrigger
                            value="sole_trader"
                            disabled
                            title="Sole Traders add-on not active on your plan"
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Icon.Lock />
                              Sole Traders
                              <span
                                style={{
                                  fontSize: "10px",
                                  fontFamily: "var(--mono)",
                                  fontWeight: 600,
                                  background: "var(--border-strong)",
                                  color: "var(--text-faint)",
                                  padding: "2px 8px",
                                  borderRadius: "100px",
                                  letterSpacing: "0.04em",
                                  lineHeight: 1.3,
                                }}
                              >
                                ADD-ON
                              </span>
                            </span>
                          </TabsTrigger>
                        )}
                      </TabsList>
                    </Tabs>
                  )}

                  {/* Sole Trader Discovery Panel */}
                  {isSoleTrader && soleTraderEnabled && (
                    <div
                      style={{
                        background: "var(--surface)",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius)",
                        padding: "20px 24px",
                        marginTop: "16px",
                        marginBottom: "16px",
                        boxShadow: "var(--shadow-sm)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          marginBottom: "16px",
                        }}
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="var(--accent)"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="11" cy="11" r="8" />
                          <path d="m21 21-4.35-4.35" />
                        </svg>
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "var(--text)",
                          }}
                        >
                          Discover Sole Traders
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "16px",
                          flexWrap: "wrap",
                          alignItems: "flex-end",
                        }}
                      >
                        {[
                          [
                            "Trade / Category",
                            "e.g. plumber, electrician",
                            discoveryTrade,
                            setDiscoveryTrade,
                          ],
                          [
                            "Location",
                            "e.g. Manchester, SW1A",
                            discoveryLocation,
                            setDiscoveryLocation,
                          ],
                        ].map(([lbl, ph, val, setter]) => (
                          <div
                            key={lbl}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                              flex: "1",
                              minWidth: "180px",
                            }}
                          >
                            <label
                              style={{
                                fontSize: "11px",
                                fontWeight: "500",
                                color: "var(--text-muted)",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              {lbl}
                            </label>
                            <Input
                              label={lbl}
                              hideLabel
                              className="field-input"
                              type="text"
                              placeholder={ph}
                              value={val}
                              onChange={(e) => setter(e.target.value)}
                              onKeyDown={(e) =>
                                e.key === "Enter" && handleRunDiscovery()
                              }
                              style={{ fontSize: "13px", padding: "8px 10px" }}
                            />
                          </div>
                        ))}
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleRunDiscovery}
                          disabled={
                            discoveryLoading ||
                            !discoveryTrade.trim() ||
                            !discoveryLocation.trim()
                          }
                          style={{
                            height: "36px",
                            paddingLeft: "16px",
                            paddingRight: "16px",
                            flexShrink: 0,
                          }}
                        >
                          {discoveryLoading ? (
                            <>
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                style={{
                                  animation: "spin 0.8s linear infinite",
                                }}
                              >
                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                              </svg>
                              Searching…
                            </>
                          ) : (
                            <>
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.35-4.35" />
                              </svg>
                              Run Discovery
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* BULK ACTION PANEL */}
                  {selectedCompanyIds.size > 0 && (
                    <div
                      style={{
                        background: "var(--accent-dim)",
                        border: "1px solid var(--accent)",
                        borderRadius: "var(--radius)",
                        padding: "12px 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "12px",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "16px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span>
                          <strong>{selectedCompanyIds.size}</strong>{" "}
                          {selectedCompanyIds.size !== 1
                            ? "companies"
                            : "company"}{" "}
                          selected
                        </span>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span className="toolbar-label">Email type:</span>
                          <Select
                            label="Email type"
                            hideLabel
                            className="field-input toolbar-select"
                            value={bulkEmailType}
                            onValueChange={setBulkEmailType}
                            options={[
                              { value: "preview", label: "Wizzyl Outreach" },
                              {
                                value: "followup",
                                label: "Wizzyl Follow-up",
                              },
                              {
                                value: "intro",
                                label: "AXO Signal Outreach",
                              },
                              { value: "brief", label: "Business Brief" },
                              ...(canSendPartnerOutreach
                                ? [
                                    {
                                      value: "partner-outreach",
                                      label: "Partner Outreach",
                                    },
                                  ]
                                : []),
                            ]}
                          />
                          {bulkEmailType === "partner-outreach" && (
                            <Select
                              label="Partner template"
                              hideLabel
                              className="field-input toolbar-select"
                              value={partnerTemplateKey}
                              onValueChange={setPartnerTemplateKey}
                              options={[
                                { value: "wizzyl", label: "Wizzyl template" },
                                { value: "6ela", label: "6ELA template" },
                              ]}
                            />
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setSelectedCompanyIds(new Set())}
                        >
                          Clear
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={prepareBulkSend}
                          disabled={bulkSending}
                        >
                          {bulkSending
                            ? "Sending..."
                            : `Send ${
                                bulkEmailType === "preview"
                                  ? "Pitch"
                                  : bulkEmailType === "followup"
                                    ? "Follow-up"
                                    : bulkEmailType === "brief"
                                      ? "Brief"
                                      : bulkEmailType === "partner-outreach"
                                        ? "Partner Outreach"
                                        : "Intro"
                              } (${selectedCompanyIds.size})`}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* PRE-SEND CONFIRMATION DIALOG */}

                  {showSendConfirm && (
                    <Dialog
                      open
                      onOpenChange={(next) => {
                        if (!next) setShowSendConfirm(false);
                      }}
                    >
                      <DialogContent
                        className="bulk-send-dialog"
                        title="Confirm Bulk Send"
                      >
                        <div style={{ marginBottom: "20px" }}>
                          <strong>{pendingRecipients.length}</strong> recipient
                          {pendingRecipients.length !== 1 ? "s" : ""} will
                          receive a{" "}
                          <strong>
                            {bulkEmailType === "preview"
                              ? "Pitch Email"
                              : bulkEmailType === "followup"
                                ? "Follow-up Email"
                                : bulkEmailType === "brief"
                                  ? "Business Brief Request"
                                  : "AXO Signal Introduction"}
                          </strong>
                          .
                        </div>

                        {/* Recipient list */}
                        <div
                          className="drawer-card"
                          style={{ marginBottom: "16px" }}
                        >
                          <div className="drawer-section-title">
                            Recipients ({pendingRecipients.length})
                          </div>
                          <div
                            style={{
                              maxHeight: "200px",
                              overflowY: "auto",
                            }}
                          >
                            {pendingRecipients.map((r, idx) => (
                              <div
                                key={idx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "12px",
                                  padding: "8px 0",
                                  borderBottom:
                                    idx < pendingRecipients.length - 1
                                      ? "1px solid var(--border)"
                                      : "none",
                                }}
                              >
                                <span
                                  style={{
                                    fontFamily: "var(--mono)",
                                    fontSize: "12px",
                                    color: "var(--accent)",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {r.toEmail}
                                </span>
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "var(--text-muted)",
                                    flexShrink: 0,
                                    maxWidth: "45%",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    textAlign: "right",
                                  }}
                                >
                                  {r.companyName}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Full email template, preview or edit */}
                        {emailPreview && (
                          <div className="drawer-card">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "10px",
                              }}
                            >
                              <div
                                className="drawer-section-title"
                                style={{ margin: 0 }}
                              >
                                Email Template
                              </div>
                              <div style={{ display: "flex", gap: "6px" }}>
                                <button
                                  type="button"
                                  className={
                                    templateMode === "preview"
                                      ? "btn btn-primary btn-sm"
                                      : "btn btn-secondary btn-sm"
                                  }
                                  onClick={() => setTemplateMode("preview")}
                                >
                                  Preview
                                </button>
                                {bulkEmailType !== "partner-outreach" && (
                                  <button
                                    type="button"
                                    className={
                                      templateMode === "edit"
                                        ? "btn btn-primary btn-sm"
                                        : "btn btn-secondary btn-sm"
                                    }
                                    onClick={() => setTemplateMode("edit")}
                                  >
                                    Edit
                                  </button>
                                )}
                              </div>
                            </div>

                            {templateMode === "edit" ? (
                              <>
                                <label
                                  style={{
                                    display: "block",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color: "var(--text-muted)",
                                    marginBottom: "4px",
                                  }}
                                >
                                  Subject
                                </label>
                                <Input
                                  label="Subject"
                                  hideLabel
                                  type="text"
                                  className="field-input"
                                  value={customSubject}
                                  onChange={(e) => {
                                    setCustomSubject(e.target.value);
                                    setTemplateTouched(true);
                                  }}
                                  style={{
                                    width: "100%",
                                    marginBottom: "12px",
                                  }}
                                />
                                <label
                                  style={{
                                    display: "block",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color: "var(--text-muted)",
                                    marginBottom: "4px",
                                  }}
                                >
                                  Body
                                </label>
                                <Textarea
                                  label="Body"
                                  hideLabel
                                  className="row-textarea"
                                  value={customBody}
                                  onChange={(e) => {
                                    setCustomBody(e.target.value);
                                    setTemplateTouched(true);
                                  }}
                                  rows={12}
                                  style={{
                                    width: "100%",
                                    fontFamily: "var(--mono)",
                                    fontSize: "12px",
                                    resize: "vertical",
                                  }}
                                />
                                <div
                                  style={{
                                    marginTop: "8px",
                                    fontSize: "11px",
                                    color: "var(--text-faint)",
                                  }}
                                >
                                  Merge tags: <code>{"{{firstName}}"}</code>{" "}
                                  <code>{"{{companyName}}"}</code>{" "}
                                  <code>{"{{previewUrl}}"}</code>, these are
                                  replaced per recipient when sent. Branding and
                                  the unsubscribe link are added automatically.
                                </div>
                                {templateTouched && (
                                  <div
                                    style={{
                                      marginTop: "8px",
                                      fontSize: "11px",
                                      color: "var(--amber)",
                                    }}
                                  >
                                    You've customized this template, it will be
                                    used instead of the standard one.
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <div style={{ marginBottom: "10px" }}>
                                  <strong>Subject:</strong>{" "}
                                  {renderMergedTemplate(
                                    customSubject,
                                    pendingRecipients[0],
                                  )}
                                </div>
                                <div
                                  style={{
                                    background: "var(--surface-sunken)",
                                    border: "1px solid var(--border)",
                                    borderRadius: "var(--radius-sm)",
                                    padding: "12px",
                                    fontSize: "12.5px",
                                    whiteSpace: "pre-wrap",
                                    maxHeight: "260px",
                                    overflowY: "auto",
                                  }}
                                >
                                  {renderMergedTemplate(
                                    customBody,
                                    pendingRecipients[0],
                                  )}
                                </div>
                                <div
                                  style={{
                                    marginTop: "10px",
                                    fontSize: "11px",
                                    color: "var(--text-faint)",
                                  }}
                                >
                                  Showing merged content for{" "}
                                  {pendingRecipients[0]?.toEmail ||
                                    "the first recipient"}
                                  . Branding and an unsubscribe link are added
                                  automatically at send time. Click{" "}
                                  <strong>Edit</strong> to change the subject or
                                  body before sending.
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        <div className="drawer-footer">
                          <button
                            className="btn btn-secondary btn-md"
                            onClick={() => setShowSendConfirm(false)}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary btn-md"
                            onClick={confirmBulkSend}
                            disabled={bulkSending}
                          >
                            {bulkSending
                              ? "Sending..."
                              : `Confirm & Send (${pendingRecipients.length})`}
                          </button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}

                  {/* TABLE */}
                  <div className="table-wrap">
                    {!loading && companies.length > 0 && (
                      <div className="table-toolbar">
                        <span className="toolbar-label">
                          {pagination.total.toLocaleString()} results
                        </span>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span className="toolbar-label">Rows per page:</span>
                          <Select
                            label="Rows per page"
                            hideLabel
                            className="per-page-select"
                            value={String(perPage)}
                            onValueChange={(value) =>
                              handlePerPageChange(Number(value))
                            }
                            options={[
                              { value: "20", label: "20" },
                              { value: "50", label: "50" },
                              { value: "100", label: "100" },
                            ]}
                          />
                        </div>
                      </div>
                    )}

                    {loading ? (
                      <div className="table-scroll">
                        <table>
                          <thead>
                            <tr>
                              <th style={{ width: 32 }}></th>
                              <th>Company</th>
                              {!isSoleTrader && <th>Directors</th>}
                              <th>Incorporated</th>
                              <th>Contact</th>
                              <th style={{ width: 48 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            <SkeletonRows count={8} />
                          </tbody>
                        </table>
                      </div>
                    ) : isSoleTrader && !discoveryHasRun ? (
                      <div className="empty-state">
                        <div className="empty-icon">
                          <Search size={32} strokeWidth={1.5} />
                        </div>
                        <div className="empty-title">No results yet</div>
                        <div className="empty-sub">
                          Enter a trade and location above, then click{" "}
                          <strong>Run Discovery</strong> to find sole traders.
                        </div>
                      </div>
                    ) : companies.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-icon">
                          <Radar size={32} strokeWidth={1.5} />
                        </div>
                        <div className="empty-title">No companies found</div>
                        <div className="empty-sub">
                          {targetSicCodes.length > 0 && !activeFilterCount
                            ? "No companies match your targeted industries yet. New registrations matching your SIC codes will appear here."
                            : activeFilterCount > 0
                              ? "You have active filters restricting results, try clearing them."
                              : isSouthAfrica
                                ? "No South African companies found yet. Try clearing the filters or check back soon."
                                : "Try adjusting your filters or wait for new registrations."}
                        </div>
                        {activeFilterCount > 0 && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleResetFilters}
                            style={{ marginTop: 8 }}
                          >
                            Clear all filters
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <SortableDataTable
                          caption={
                            isSouthAfrica ? "South African companies" : "Leads"
                          }
                          rowKey="id"
                          minWidth={isSouthAfrica ? 1800 : undefined}
                          selectable={!isSouthAfrica}
                          selectedKeys={[...selectedCompanyIds].map(String)}
                          onSelectionChange={(keys) =>
                            setSelectedCompanyIds(new Set(keys))
                          }
                          defaultSort={
                            sortField
                              ? {
                                  key: sortField,
                                  direction: sortOrder || "asc",
                                }
                              : undefined
                          }
                          onSortChange={(next) => {
                            const sort = next?.key ?? "";
                            const order = next?.direction ?? "";
                            setSortField(sort);
                            setSortOrder(order);
                            updateFilters({
                              ...filters,
                              page: "1",
                              sort,
                              order,
                              limit: perPage,
                            });
                          }}
                          rows={companies.map((company) => ({
                            ...company,
                            directorsCount: Array.isArray(company.directors)
                              ? company.directors.length
                              : typeof company.directors === "string"
                                ? company.directors
                                    .split("|")
                                    .filter((d) => d.trim()).length
                                : 0,
                            contactAvailable: !!(
                              company.enrichment?.phone ||
                              company.enrichment?.email ||
                              company.phone ||
                              company.email
                            ),
                          }))}
                          columns={[
                            {
                              key: "companyName",
                              label: isSouthAfrica
                                ? "Company name"
                                : isSoleTrader
                                  ? "Business Name"
                                  : "Company",
                              width: isSouthAfrica ? 185 : undefined,
                              render: (_value, company) =>
                                isSouthAfrica ? (
                                  <div
                                    className="cell-company-name"
                                    style={saCellStyle}
                                    title={company.companyName || ""}
                                  >
                                    {company.companyName || "-"}
                                  </div>
                                ) : (
                                  <LeadsCompanyCell
                                    company={company}
                                    isSoleTrader={isSoleTrader}
                                    market={activeMarket}
                                    isNew={isNew(
                                      isSoleTrader
                                        ? company.discoveredAt
                                        : company.incorporatedAt,
                                    )}
                                  />
                                ),
                            },
                            ...(isSouthAfrica
                              ? [
                                  {
                                    key: "contactPerson",
                                    label: "Contact person",
                                    width: 125,
                                    render: (_value, company) => (
                                      <div
                                        className="cell-sic"
                                        style={saCellStyle}
                                        title={company.contactPerson || ""}
                                      >
                                        {company.contactPerson || "-"}
                                      </div>
                                    ),
                                  },
                                  {
                                    key: "email",
                                    label: "Email address",
                                    width: 205,
                                    render: (_value, company) =>
                                      company.email ? (
                                        <a
                                          href={`mailto:${company.email}`}
                                          className="cell-company-num"
                                          style={saCellStyle}
                                          title={company.email}
                                        >
                                          {company.email}
                                        </a>
                                      ) : (
                                        <span className="cell-sic">-</span>
                                      ),
                                  },
                                  {
                                    key: "location",
                                    label: "Location",
                                    width: 115,
                                    render: (_value, company) => (
                                      <div
                                        className="cell-sic"
                                        style={saCellStyle}
                                        title={company.location || ""}
                                      >
                                        {company.location || "-"}
                                      </div>
                                    ),
                                  },
                                  {
                                    key: "status",
                                    label: "Status",
                                    width: 175,
                                    render: (_value, company) => (
                                      <span
                                        className={`ta-pill ${statusTone(
                                          company.status,
                                        )}`}
                                        style={{ whiteSpace: "nowrap" }}
                                        title={company.status || ""}
                                      >
                                        {company.status || "-"}
                                      </span>
                                    ),
                                  },
                                  {
                                    key: "industry",
                                    label: "Industry",
                                    width: 190,
                                    render: (_value, company) => (
                                      <div
                                        className="cell-sic"
                                        style={saCellStyle}
                                        title={company.industry || ""}
                                      >
                                        {company.industry || "-"}
                                      </div>
                                    ),
                                  },
                                  {
                                    key: "website",
                                    label: "Website",
                                    width: 210,
                                    render: (_value, company) =>
                                      company.website ? (
                                        <a
                                          href={
                                            /^https?:\/\//i.test(
                                              company.website,
                                            )
                                              ? company.website
                                              : `https://${company.website}`
                                          }
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="cell-company-num"
                                          style={saCellStyle}
                                          title={company.website}
                                        >
                                          {company.website}
                                        </a>
                                      ) : (
                                        <span className="cell-sic">-</span>
                                      ),
                                  },
                                  {
                                    key: "dateEmailSent",
                                    label: "Date email sent",
                                    width: 120,
                                    render: (_value, company) => (
                                      <div
                                        className="date-val"
                                        style={{ whiteSpace: "nowrap" }}
                                      >
                                        {company.dateEmailSent
                                          ? new Date(
                                              company.dateEmailSent,
                                            ).toLocaleDateString("en-GB")
                                          : "-"}
                                      </div>
                                    ),
                                  },
                                  {
                                    key: "followUpDate",
                                    label: "Follow-up date",
                                    width: 120,
                                    render: (_value, company) => (
                                      <div
                                        className="date-val"
                                        style={{ whiteSpace: "nowrap" }}
                                      >
                                        {company.followUpDate
                                          ? new Date(
                                              company.followUpDate,
                                            ).toLocaleDateString("en-GB")
                                          : "-"}
                                      </div>
                                    ),
                                  },
                                  {
                                    key: "secondFollowUpDate",
                                    label: "2nd Follow-up date",
                                    width: 135,
                                    render: (_value, company) => (
                                      <div
                                        className="date-val"
                                        style={{ whiteSpace: "nowrap" }}
                                      >
                                        {company.secondFollowUpDate
                                          ? new Date(
                                              company.secondFollowUpDate,
                                            ).toLocaleDateString("en-GB")
                                          : "-"}
                                      </div>
                                    ),
                                  },
                                  {
                                    key: "notes",
                                    label: "Notes",
                                    width: 210,
                                    render: (_value, company) => (
                                      <div
                                        className="cell-sic"
                                        style={saCellStyle}
                                        title={company.notes || ""}
                                      >
                                        {company.notes || "-"}
                                      </div>
                                    ),
                                  },
                                ]
                              : [
                                  ...(!isSoleTrader
                                    ? [
                                        {
                                          key: "directorsCount",
                                          label: "Directors",
                                          render: (_value, company) => (
                                            <LeadsDirectorsCell
                                              company={company}
                                            />
                                          ),
                                        },
                                      ]
                                    : []),
                                  {
                                    key: isSoleTrader
                                      ? "discoveredAt"
                                      : "incorporatedAt",
                                    label: isSoleTrader
                                      ? "Discovered"
                                      : "Incorporated",
                                    render: (_value, company) => (
                                      <div className="date-val">
                                        {isSoleTrader
                                          ? company.discoveredAt
                                            ? new Date(
                                                company.discoveredAt,
                                              ).toLocaleDateString("en-GB")
                                            : "-"
                                          : company.incorporatedAt
                                            ? new Date(
                                                company.incorporatedAt,
                                              ).toLocaleDateString("en-GB")
                                            : "-"}
                                      </div>
                                    ),
                                  },
                                  {
                                    key: "contactAvailable",
                                    label: "Contact",
                                    render: (_value, company) => (
                                      <LeadsContactCell
                                        company={company}
                                        isSoleTrader={isSoleTrader}
                                        displayName={
                                          isSoleTrader
                                            ? company.businessName
                                            : company.companyName
                                        }
                                      />
                                    ),
                                  },
                                  {
                                    key: "_actions",
                                    label: "",
                                    sortable: false,
                                    render: (_value, company) => (
                                      <LeadsRowActions
                                        company={company}
                                        isSoleTrader={isSoleTrader}
                                        onTriggerEnrich={handleTriggerEnrich}
                                        onToast={showToast}
                                        onOpenDrawer={setDrawerCompany}
                                      />
                                    ),
                                  },
                                ]),
                          ]}
                        />

                        <div className="pagination">
                          <div className="page-info">
                            Page <b>{pagination.currentPage}</b> of{" "}
                            <b>{pagination.totalPages}</b>&ensp;·&ensp;
                            {pagination.total.toLocaleString()} total
                          </div>
                          <div className="page-controls">
                            <button
                              onClick={() => handlePageChange(1)}
                              disabled={pagination.currentPage === 1}
                              className="btn-page"
                              title="First"
                            >
                              «
                            </button>
                            <button
                              onClick={() =>
                                handlePageChange(pagination.currentPage - 1)
                              }
                              disabled={pagination.currentPage === 1}
                              className="btn-page"
                              title="Previous"
                            >
                              ‹
                            </button>
                            {getPageNumbers().map((page, i) =>
                              page === "..." ? (
                                <span key={`e${i}`} className="page-ellipsis">
                                  …
                                </span>
                              ) : (
                                <button
                                  key={page}
                                  onClick={() => handlePageChange(page)}
                                  className={`btn-page${pagination.currentPage === page ? " active" : ""}`}
                                >
                                  {page}
                                </button>
                              ),
                            )}
                            <button
                              onClick={() =>
                                handlePageChange(pagination.currentPage + 1)
                              }
                              disabled={
                                pagination.currentPage === pagination.totalPages
                              }
                              className="btn-page"
                              title="Next"
                            >
                              ›
                            </button>
                            <button
                              onClick={() =>
                                handlePageChange(pagination.totalPages)
                              }
                              disabled={
                                pagination.currentPage === pagination.totalPages
                              }
                              className="btn-page"
                              title="Last"
                            >
                              »
                            </button>
                          </div>
                          <div className="page-jump">
                            Go to
                            <Input
                              label="Go to page"
                              hideLabel
                              type="number"
                              min={1}
                              max={pagination.totalPages}
                              value={jumpPage}
                              onChange={(e) => setJumpPage(e.target.value)}
                              onKeyDown={(e) =>
                                e.key === "Enter" && handleJumpPage()
                              }
                              className="page-jump-input"
                              placeholder="-"
                            />
                            <button
                              onClick={handleJumpPage}
                              className="btn btn-ghost btn-sm"
                            >
                              Go
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* end dash-content */}
              </div>
              {/* end dash-body */}
            </>
          )}

          {activeSection === "revenue-intel" &&
            !canPlan(CAP.REVENUE_INTELLIGENCE) && (
              <UpgradePanel
                feature="Sales"
                plans="AI and Enterprise"
                onBack={() => setActiveSection("overview")}
              />
            )}

          {activeSection === "revenue-intel" &&
            canPlan(CAP.REVENUE_INTELLIGENCE) && (
              <Tabs className="ri-panel" value={riTab} onValueChange={setRiTab}>
                <div className="ri-subnav" id="tour-sw-tabs">
                  <TabsList aria-label="Sales workspace">
                    {REVENUE_INTEL_TABS.map((t) => (
                      <TabsTrigger key={t.id} value={t.id}>
                        {t.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <div className="ri-subnav-actions" id="ri-subnav-actions" />
                </div>
                <TabsContent value="agent" className="ri-panel-content">
                  <SalesWorkspace embedded darkMode={darkMode} />
                </TabsContent>
                <TabsContent value="pipeline" className="ri-panel-content">
                  <PipelineBoard embedded darkMode={darkMode} />
                </TabsContent>
                <TabsContent value="performance" className="ri-panel-content">
                  <AiPerformanceDashboard embedded darkMode={darkMode} />
                </TabsContent>
                <TabsContent value="forecast" className="ri-panel-content">
                  <RevenueForecastDashboard embedded darkMode={darkMode} />
                </TabsContent>
                <TabsContent value="credits" className="ri-panel-content">
                  <CreditsUsage
                    canManageBilling={hasCapability(
                      user,
                      CAPABILITIES.BILLING_MANAGE,
                    )}
                  />
                </TabsContent>
              </Tabs>
            )}
        </div>
      </div>
      {showFeedback && (
        <FeedbackModal
          onClose={() => setShowFeedback(false)}
          demoUser={demoUser}
        />
      )}

      {showAcct && (
        <AccountSettingsModal
          user={user}
          initialTab={acctTab}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          density={density}
          setDensity={setDensity}
          onClose={() => setShowAcct(false)}
          onRequestCancel={() => {
            setShowAcct(false);
            setShowCancelModal(true);
          }}
        />
      )}

      {showCancelModal && (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next && !cancelSubmitted) setShowCancelModal(false);
          }}
        >
          <DialogContent
            title={
              cancelSubmitted ? "Request received" : "Cancel your subscription?"
            }
          >
            {!cancelSubmitted ? (
              <>
                <div className="cancel-modal-icon">
                  <AlertTriangle size={20} strokeWidth={2} />
                </div>
                <div className="cancel-modal-sub">
                  Your cancellation request will be sent to your administrator.
                  We’ll contact you shortly to confirm the next steps.
                </div>
                <div className="cancel-modal-actions">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={() => setShowCancelModal(false)}
                  >
                    Keep Subscription
                  </button>
                  <button
                    className="btn btn-md"
                    style={{
                      background: "var(--red)",
                      color: "#fff",
                      border: "none",
                    }}
                    onClick={async () => {
                      try {
                        await apiService.cancelSubscription({
                          customerName: user?.customer?.name || "Unknown",
                          userEmail: user?.email,
                          userName: user?.name,
                          customerId: user?.customerId,
                        });
                      } catch (err) {
                        console.error("Failed to notify cancellation:", err);
                      }
                      setCancelSubmitted(true);
                    }}
                  >
                    Yes, Request Cancellation
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="cancel-success-icon">✓</div>
                <div
                  className="cancel-success-sub"
                  style={{ marginBottom: 24 }}
                >
                  Your cancellation request has been reported to your admin. A
                  member of the team will contact you shortly to confirm next
                  steps.
                </div>
                <div
                  className="cancel-modal-actions"
                  style={{ justifyContent: "center" }}
                >
                  <button
                    className="btn btn-primary btn-md"
                    onClick={() => {
                      setShowCancelModal(false);
                      setCancelSubmitted(false);
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* BULK RESULTS MODAL */}
      {bulkResults && (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) setBulkResults(null);
          }}
        >
          <DialogContent
            className="bulk-results-dialog"
            title="Bulk Send Results"
            description={`${bulkResults.totalRequested} recipient${
              bulkResults.totalRequested !== 1 ? "s" : ""
            } processed`}
          >
            {/* Summary chips */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "10px",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  background: "var(--green-dim)",
                  border: "1px solid rgba(5,150,105,0.25)",
                  borderRadius: "var(--radius)",
                  padding: "14px 16px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--mono)",
                    fontSize: "26px",
                    fontWeight: "600",
                    color: "var(--green)",
                    lineHeight: "1",
                    marginBottom: "6px",
                  }}
                >
                  {bulkResults.sent}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color: "var(--green)",
                    opacity: "0.8",
                  }}
                >
                  Sent
                </div>
              </div>
              <div
                style={{
                  background:
                    bulkResults.failed > 0
                      ? "var(--red-dim)"
                      : "var(--surface-raised)",
                  border:
                    bulkResults.failed > 0
                      ? "1px solid rgba(191,41,41,0.22)"
                      : "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  padding: "14px 16px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--mono)",
                    fontSize: "26px",
                    fontWeight: "600",
                    color:
                      bulkResults.failed > 0
                        ? "var(--red)"
                        : "var(--text-faint)",
                    lineHeight: "1",
                    marginBottom: "6px",
                  }}
                >
                  {bulkResults.failed}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color:
                      bulkResults.failed > 0
                        ? "var(--red)"
                        : "var(--text-faint)",
                    opacity: "0.8",
                  }}
                >
                  Failed
                </div>
              </div>
              <div
                style={{
                  background:
                    bulkResults.skippedUnsubscribed > 0
                      ? "var(--amber-dim)"
                      : "var(--surface-raised)",
                  border:
                    bulkResults.skippedUnsubscribed > 0
                      ? "1px solid rgba(146,88,0,0.22)"
                      : "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  padding: "14px 16px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--mono)",
                    fontSize: "26px",
                    fontWeight: "600",
                    color:
                      bulkResults.skippedUnsubscribed > 0
                        ? "var(--amber)"
                        : "var(--text-faint)",
                    lineHeight: "1",
                    marginBottom: "6px",
                  }}
                >
                  {bulkResults.skippedUnsubscribed}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color:
                      bulkResults.skippedUnsubscribed > 0
                        ? "var(--amber)"
                        : "var(--text-faint)",
                    opacity: "0.8",
                  }}
                >
                  Skipped
                </div>
              </div>
            </div>

            {/* Failed details */}
            {bulkResults.failed > 0 && (
              <div className="drawer-card">
                <div className="drawer-section-title">Failed deliveries</div>
                <div style={{ maxHeight: "180px", overflowY: "auto" }}>
                  {bulkResults.details
                    .filter((d) => !d.success)
                    .map((d, i, arr) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: "12px",
                          padding: "8px 0",
                          borderBottom:
                            i < arr.length - 1
                              ? "1px solid var(--border)"
                              : "none",
                        }}
                      >
                        <span
                          style={{
                            fontFamily: "var(--mono)",
                            fontSize: "12px",
                            color: "var(--text-muted)",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {d.email}
                        </span>
                        <span
                          style={{
                            fontSize: "11px",
                            color: "var(--red)",
                            flexShrink: 0,
                            textAlign: "right",
                          }}
                        >
                          {d.error}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
            <div className="drawer-footer">
              <button
                className="btn btn-primary btn-md"
                onClick={() => setBulkResults(null)}
              >
                Done
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default CustomerDashboard;
